import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useInstagram } from './useInstagram';
import { useLinkedInZernio } from './useLinkedInZernio';
import TeamStructureView from './TeamStructureView';
import { db, auth, googleProvider, signInWithEmail, signUpWithEmail, createUserProfile } from './firebase';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, getDoc, setDoc, doc, deleteDoc, onSnapshot, updateDoc, collectionGroup, writeBatch } from 'firebase/firestore';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import './App.css';

// ─── ICONS ───────────────────────────────────────────────────
const InstagramLogo = ({size=12}) => (
  <svg width={size} height={size} viewBox="0 0 448 448" style={{verticalAlign:'-2px'}}>
    <defs>
      <radialGradient id="igGrad" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stopColor="#fdf497"/>
        <stop offset="5%" stopColor="#fdf497"/>
        <stop offset="45%" stopColor="#fd5949"/>
        <stop offset="60%" stopColor="#d6249f"/>
        <stop offset="90%" stopColor="#285AEB"/>
      </radialGradient>
    </defs>
    <rect width="448" height="448" rx="100" fill="url(#igGrad)"/>
    <path fill="#fff" d="M224 130a94 94 0 1 0 0 188 94 94 0 0 0 0-188zm0 155a61 61 0 1 1 0-122 61 61 0 0 1 0 122zM325 90a26 26 0 1 0 0 52 26 26 0 0 0 0-52z"/>
    <path fill="#fff" d="M340 36H108C68 36 36 68 36 108v232c0 40 32 72 72 72h232c40 0 72-32 72-72V108c0-40-32-72-72-72zm38 304c0 21-17 38-38 38H108c-21 0-38-17-38-38V108c0-21 17-38 38-38h232c21 0 38 17 38 38v232z"/>
  </svg>
);

// Card-scoped only (not a global T token change) — ~5.7:1 contrast on T.card vs
// T.textFaint's ~1.4:1, replaces T.textFaint and small-size T.textDim uses inside business cards.
const CARD_DIM_TEXT = '#9a9aa0';

const LinkedInLogo = ({size=12}) => (
  <svg width={size} height={size} viewBox="0 0 382 382" style={{verticalAlign:'-2px'}}>
    <path fill="#0077B7" d="M347.445,0H34.555C15.471,0,0,15.471,0,34.555v312.889C0,366.529,15.471,382,34.555,382h312.889C366.529,382,382,366.529,382,347.444V34.555C382,15.471,366.529,0,347.445,0z"/>
    <path fill="#fff" d="M118.207,329.844c0,5.554-4.502,10.056-10.056,10.056H65.345c-5.554,0-10.056-4.502-10.056-10.056V150.403c0-5.554,4.502-10.056,10.056-10.056h42.806c5.554,0,10.056,4.502,10.056,10.056V329.844z M86.748,123.432c-22.459,0-40.666-18.207-40.666-40.666S64.289,42.1,86.748,42.1s40.666,18.207,40.666,40.666S109.208,123.432,86.748,123.432z M341.91,330.654c0,5.106-4.14,9.246-9.246,9.246H286.73c-5.106,0-9.246-4.14-9.246-9.246v-84.168c0-12.556,3.683-55.021-32.813-55.021c-28.309,0-34.051,29.066-35.204,42.11v97.079c0,5.106-4.139,9.246-9.246,9.246h-44.426c-5.106,0-9.246-4.14-9.246-9.246V149.593c0-5.106,4.14-9.246,9.246-9.246h44.426c5.106,0,9.246,4.14,9.246,9.246v15.655c10.497-15.753,26.097-27.912,59.312-27.912c73.552,0,73.131,68.716,73.131,106.472L341.91,330.654L341.91,330.654z"/>
  </svg>
);

// Widened, glowing progress bar shared by HomeView's business cards and BusinessView's
// Instagram goal bar — tight (small-radius) glow plus a subtle shimmer sweep, not a foggy
// blur, extending the boxShadow-glow language already used elsewhere in this app.
function ProgressBar({ pct, color, height=8 }) {
  // The track gets the rounding; the fill itself is a sharp rectangle so it cuts off cleanly
  // right at the current value instead of tapering into a rounded cap mid-bar.
  return (
    <div style={{height,background:T.border,borderRadius:3,overflow:'hidden'}}>
      <div style={{height:'100%',width:`${pct}%`,borderRadius:0,position:'relative',overflow:'hidden',
        background:`linear-gradient(90deg,${color}cc,${color})`,
        boxShadow:`0 0 6px ${color}90, 0 0 2px ${color}`,transition:'width 0.5s'}}>
        <div className="bar-shimmer" style={{position:'absolute',top:0,bottom:0,width:'40%',
          background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.35),transparent)'}}/>
      </div>
    </div>
  );
}

// Lightweight count-up effect for hero stat numbers — no animation library needed.
function AnimatedNumber({ value, prefix='', decimals=0, duration=700 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = Number(value)||0;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now-start)/duration, 1);
      const eased = 1-Math.pow(1-t, 3);
      setDisplay(target*eased);
      if (t<1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{prefix}{display.toLocaleString(undefined,{maximumFractionDigits:decimals,minimumFractionDigits:decimals})}</>;
}

// Small hex<->HSL helpers so a funnel can be tinted as a smooth hue sweep from a single brand
// color, instead of needing a hand-picked color per stage.
function hexToHsl(hex) {
  const r=parseInt(hex.slice(1,3),16)/255, g=parseInt(hex.slice(3,5),16)/255, b=parseInt(hex.slice(5,7),16)/255;
  const max=Math.max(r,g,b), min=Math.min(r,g,b);
  let h,s; const l=(max+min)/2;
  if(max===min){h=s=0;}
  else{
    const d=max-min;
    s=l>0.5?d/(2-max-min):d/(max+min);
    switch(max){
      case r: h=(g-b)/d+(g<b?6:0); break;
      case g: h=(b-r)/d+2; break;
      default: h=(r-g)/d+4;
    }
    h/=6;
  }
  return [h*360,s*100,l*100];
}
function hslToHex(h,s,l) {
  s/=100; l/=100;
  const k=n=>(n+h/30)%12;
  const a=s*Math.min(l,1-l);
  const f=n=>l-a*Math.max(-1,Math.min(k(n)-3,Math.min(9-k(n),1)));
  const toHex=x=>Math.round(255*x).toString(16).padStart(2,'0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}
// Stage i's tint along a gentle hue sweep anchored at the business's own brand color, so the
// funnel reads as one continuous flowing band rather than a flat single-color block, without
// introducing colors unrelated to that business.
function funnelStageColor(baseColor, i, n, sweep=55) {
  const [h,s,l] = hexToHsl(baseColor);
  const hue = (h + (i/Math.max(n-1,1))*sweep) % 360;
  return hslToHex(hue, Math.min(s+6,88), Math.min(Math.max(l,40)+3,60));
}

// Curved, continuously-flowing funnel — each stage is a bezier-tapered segment (same taper math
// as a Sankey band) blending from its own hue-swept tint into the next stage's, with a value
// readout above, a pill percent-of-first-stage badge centered in the shape, and the label below.
function FunnelHorn({ stages, counts, color, height=190 }) {
  const n = stages.length;
  const max = Math.max(counts[0]||0, 1);
  const norms = counts.map(c=>c/max);
  const my = height/2;
  const gap = 3;
  const stageColors = stages.map((_,i)=>funnelStageColor(color,i,n));
  return (
    <div style={{display:'flex',gap,width:'100%',height}}>
      {stages.map((stage,i)=>{
        const normStart = norms[i];
        const normEnd = i===n-1 ? norms[i] : norms[i+1];
        const h0 = normStart*height*0.44;
        const h1 = normEnd*height*0.44;
        const cx = 55;
        const d = `M 0 ${my-h0} C ${cx} ${my-h0}, ${100-cx} ${my-h1}, 100 ${my-h1} L 100 ${my+h1} C ${100-cx} ${my+h1}, ${cx} ${my+h0}, 0 ${my+h0} Z`;
        const pct = Math.round(normStart*100);
        const gradId = `funnelHornGrad-${color.replace('#','')}-${i}`;
        const fromColor = stageColors[i], toColor = stageColors[Math.min(i+1,n-1)];
        return (
          <div key={stage} style={{flex:1,position:'relative',height}}>
            <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{display:'block',overflow:'visible'}}>
              <defs>
                <linearGradient id={gradId} x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor={fromColor}/>
                  <stop offset="100%" stopColor={toColor}/>
                </linearGradient>
              </defs>
              <path d={d} fill={`url(#${gradId})`} opacity={0.92} stroke={fromColor} strokeOpacity={0.4} strokeWidth={1}/>
            </svg>
            <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'space-between',padding:'4px 0',pointerEvents:'none'}}>
              <div style={{fontSize:13,fontWeight:700,fontFamily:T.mono,color:T.text}}>{counts[i]}</div>
              <div style={{background:'#ffffff',color:'#000000',fontSize:11,fontWeight:800,fontFamily:T.mono,borderRadius:20,padding:'2px 10px',whiteSpace:'nowrap'}}>{pct}%</div>
              <div style={{fontSize:9,color:CARD_DIM_TEXT,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'100%'}}>{stage}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Animated "flowing lines" decoration for the login screen's side panel — same fan-curve
// geometry common to auth-page templates (pure math, generated locally rather than pulling in
// a design library), animated via framer-motion (pathLength/opacity/pathOffset loop). `position`
// flips the lean left/right; two instances (1 and -1) layered together match the reference.
function FloatingPaths({ position }) {
  const paths = Array.from({length:36}, (_,i) => ({
    id: i,
    d: `M-${380-i*5*position} -${189+i*6}C-${380-i*5*position} -${189+i*6} -${312-i*5*position} ${216-i*6} ${152-i*5*position} ${343-i*6}C${616-i*5*position} ${470-i*6} ${684-i*5*position} ${875-i*6} ${684-i*5*position} ${875-i*6}`,
    width: 0.3 + i*0.015,
    opacity: 0.08 + i*0.02,
  }));

  return (
    <svg viewBox="0 0 696 316" preserveAspectRatio="xMidYMid slice" style={{width:'100%',height:'100%',color:'rgba(255,255,255,0.4)'}}>
      {paths.map((p)=>(
        <motion.path
          key={p.id}
          d={p.d}
          stroke="currentColor"
          strokeWidth={p.width}
          strokeOpacity={p.opacity}
          fill="none"
          initial={{pathLength:0.3, opacity:0.6}}
          animate={{pathLength:1, opacity:[0.3,0.6,0.3], pathOffset:[0,1,0]}}
          transition={{duration:20+Math.random()*10, repeat:Infinity, ease:'linear'}}
        />
      ))}
    </svg>
  );
}

// Plain static bracket: a vertical spine plus two ticks reaching into each stacked subcard's
// left edge. No SVG needed for straight lines — two absolutely-positioned divs. No animation,
// no gradient, no glow; a subtle T.border-colored line, not a focal point.
function SocialsConnector() {
  return (
    <div style={{position:'absolute',left:0,top:0,bottom:0,width:14,pointerEvents:'none'}}>
      <div style={{position:'absolute',left:6,top:0,bottom:0,width:1,background:T.border}}/>
      <div style={{position:'absolute',left:6,top:'25%',width:8,height:1,background:T.border}}/>
      <div style={{position:'absolute',left:6,top:'75%',width:8,height:1,background:T.border}}/>
    </div>
  );
}

function SocialStatePill({ dot, label, pulse }) {
  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,background:'rgba(255,255,255,0.03)',border:`1px solid ${T.border}`,borderRadius:6,padding:'8px 0',fontSize:11,color:CARD_DIM_TEXT}}>
      <span style={{width:6,height:6,borderRadius:'50%',background:dot,flexShrink:0,animation:pulse?'urgencyPulse 1.4s ease-in-out infinite':'none'}}/>
      {label}
    </div>
  );
}

function SocialSubcard({ icon, label, connected, loading, color, children }) {
  return (
    <div style={{background:T.bg,borderRadius:8,padding:14,border:`1px solid ${color}20`}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10,fontSize:10,color:CARD_DIM_TEXT,fontFamily:T.mono,letterSpacing:1}}>
        {icon} {label.toUpperCase()}
      </div>
      {!connected ? <SocialStatePill dot={T.borderMid} label="Not connected"/>
        : loading ? <SocialStatePill dot="#F5A623" label="Loading…" pulse/>
        : children || <SocialStatePill dot="#E74C3C" label="Unavailable"/>}
    </div>
  );
}

// ─── ACCESS CONTROL ──────────────────────────────────────────
// Second layer of defense on top of Firestore security rules: blocks the UI itself
// from ever rendering dashboard content or fetching data for non-allowlisted accounts.
const ALLOWED_EMAILS = ['fahdalmostafa99@gmail.com'];

// ─── CONSTANTS ───────────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const VIEWS = { HOME:'home', BUSINESS:'business', CLIENTS:'clients', CRM:'crm', CRM_REPORTS:'crmReports', ANALYSIS:'analysis', PIPELINE:'pipeline', YEAR:'year', SIMULATION:'simulation', CONSISTENCY:'consistency', MANAGE:'manage', DATA:'data', SETTINGS:'settings', TEAM_STRUCTURE:'teamStructure' };
// Views eligible for the mobile bottom tab bar — excludes views that require an
// activeBusiness context (BUSINESS/CLIENTS/CRM) since the tab bar is global.
const BOTTOM_TAB_OPTIONS = [
  { view: VIEWS.HOME, label: 'Home', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 8L8 2l6 6"/><path d="M4 6.5v6.5h3v-3.5h2v3.5h3v-6.5"/></svg> },
  { view: VIEWS.ANALYSIS, label: 'Analytics', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="currentColor"><rect x="2" y="9" width="3" height="5"/><rect x="6.5" y="6" width="3" height="8"/><rect x="11" y="3" width="3" height="11"/></svg> },
  { view: VIEWS.CONSISTENCY, label: 'Consistency', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 9.5l2.5-3 2 2L11 4l3 3.5"/><circle cx="4.5" cy="6.5" r="1"/><circle cx="6.5" cy="8.5" r="1"/><circle cx="8.5" cy="5.5" r="1"/><circle cx="11" cy="7.5" r="1"/></svg> },
  { view: VIEWS.PIPELINE, label: 'Pipeline', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h12l-4.5 5.5V13l-3 1.5V8.5L2 3z"/></svg> },
  { view: VIEWS.YEAR, label: 'Year', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="12" height="11" rx="1"/><path d="M2 7h12M5 1v3M11 1v3"/></svg> },
  { view: VIEWS.SIMULATION, label: 'Simulation', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="8" height="8" rx="1"/><path d="M6 1v3M10 1v3M6 12v3M10 12v3M1 6h3M12 6h3M1 10h3M12 10h3"/></svg> },
  { view: VIEWS.MANAGE, label: 'Manage', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="8" cy="8" r="2.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.1 3.1l1.4 1.4M11.5 11.5l1.4 1.4M3.1 12.9l1.4-1.4M11.5 4.5l1.4-1.4"/></svg> },
  { view: VIEWS.DATA, label: 'Data', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="8" cy="4.5" rx="5" ry="2"/><path d="M3 4.5V8c0 1.1 2.24 2 5 2s5-.9 5-2V4.5"/><path d="M3 8v3.5c0 1.1 2.24 2 5 2s5-.9 5-2V8"/></svg> },
  { view: VIEWS.SETTINGS, label: 'Settings', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><line x1="2" y1="4" x2="14" y2="4"/><line x1="2" y1="8" x2="14" y2="8"/><line x1="2" y1="12" x2="14" y2="12"/><circle cx="5" cy="4" r="1.5" fill="currentColor"/><circle cx="10" cy="8" r="1.5" fill="currentColor"/><circle cx="5" cy="12" r="1.5" fill="currentColor"/></svg> },
  { view: VIEWS.TEAM_STRUCTURE, label: 'Team', icon: (s) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="3" r="2"/><circle cx="3" cy="13" r="2"/><circle cx="13" cy="13" r="2"/><path d="M8 5v3M8 8L3 11M8 8l5 3"/></svg> },
];
const DEFAULT_BOTTOM_TABS = [VIEWS.HOME, VIEWS.CONSISTENCY, VIEWS.ANALYSIS];

// ─── MOBILE BOTTOM TAB BAR ────────────────────────────────────
function MobileTabBar({bottomTabs, activeView, setActiveView, setActiveBusiness}) {
  return (
    <div className="mobile-tab-bar" style={{position:'fixed',bottom:0,left:0,right:0,zIndex:50,background:'#0a0e1a',borderTop:`1px solid ${T.border}`,paddingBottom:'env(safe-area-inset-bottom, 0px)'}}>
      {bottomTabs.map(view=>{
        const opt = BOTTOM_TAB_OPTIONS.find(o=>o.view===view) || BOTTOM_TAB_OPTIONS[0];
        const active = activeView===opt.view;
        return (
          <button key={opt.view} onClick={()=>{setActiveBusiness(null);setActiveView(opt.view);}} style={{flex:1,background:'none',border:'none',display:'flex',flexDirection:'column',alignItems:'center',gap:4,padding:'10px 4px',color:active?'#F5A623':T.textDim,minHeight:54}}>
            {opt.icon(18)}
            <span style={{fontSize:10,fontWeight:active?700:400}}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
const YEARS = Array.from({length:10},(_,i)=>2023+i);
const EMOJIS = ['⚡','👤','🎓','🛍️','🚀','💼','🔥','💡','🌍','📱','🎯','💰','🏆','📊','🧠','🎨'];
const COLORS = ['#F5A623','#E74C3C','#2ECC71','#3498DB','#9B59B6','#1ABC9C','#E67E22','#E91E63','#00BCD4','#FF5722'];
const MOMENTUMS = ['Building','Moving','Slow','Testing','Danger Zone','Paused','Scaling'];
const PRIORITIES = ['First','Second','Third','Fourth','Fifth'];

// Local-time YYYY-MM-DD key for dailyCommitments — NOT toISOString(), which is UTC
// and flips the date at local-midnight-minus-UTC-offset (e.g. 4am in UTC+4).
const getLocalDateKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
// Parse a YYYY-MM-DD key back into a local-midnight Date (new Date("YYYY-MM-DD") parses as UTC).
const parseLocalDateKey = (key) => {
  const [y,m,d] = key.split('-').map(Number);
  return new Date(y, m-1, d);
};
// Time-of-day greeting bucket — uses local hour (device clock = Asia/Dubai), never getUTCHours().
// 05:00–11:59 Morning · 12:00–16:59 Afternoon · 17:00–04:59 Evening.
const getGreeting = (d = new Date()) => {
  const h = d.getHours();
  if (h>=5 && h<12) return 'Good Morning';
  if (h>=12 && h<17) return 'Good Afternoon';
  return 'Good Evening';
};

// ─── THEME ───────────────────────────────────────────────────
const T = {
  bg: '#000000',
  card: '#030303',
  border: '#1c1c1c',
  borderMid: '#2a2a2a',
  // Previously a blue-grey scale (#c0d0e0 → #1a2a3a) — every "default" text color in the app
  // inherited a blue tint from here, which is why it kept showing up in titles, small labels,
  // and icons (the sidebar collapse arrow, the mobile menu glyph) that were never meant to be
  // colored. Desaturated to a plain white→gray scale at the same lightness steps instead.
  text: '#ffffff',
  textMid: '#b4b4b8',
  textDim: '#7a7a80',
  textFaint: '#3a3a3e',
  mono: 'monospace',
};

// One consistent "fade on black" treatment reused across the sidebar, top bar, and every card
const CARD_FADE = 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.006) 100%), #000000';

// The single unified accent for metric values/progress bars across every dashboard — the same
// blue already used for the Home revenue trend chart. Red (urgency/deadline) and each business's
// own card-identity color are the only other colors kept; every other metric routes through this.
const ACCENT = '#3498DB';

// Neutral fill for progress bars inside the business cards — tried white, but against the
// colorful card borders/icons it stood out rather than blending in. A plain gray reads as part
// of the same muted surface instead of another competing accent.
const BAR_FILL = '#9a9a9a';

// ─── KPI TYPES ───────────────────────────────────────────────
const KPI_TYPES = {
  'MRR AED':'snapshot','Active Clients':'snapshot','Engagement %':'snapshot',
  'Profit Margin %':'snapshot','Conversion Rate %':'snapshot','ROAS':'snapshot',
  'CAC AED':'snapshot',
  'Followers':'snapshot','New Leads':'cumulative','Revenue AED':'cumulative',
  'Sales Count':'cumulative','Orders':'cumulative','Content Posts':'cumulative',
  'Inbound Opps':'cumulative','Website Visitors':'cumulative','Active Students':'cumulative',
  'LinkedIn Followers':'snapshot',
  'Closed Deals':'cumulative','Conversion Rate':'snapshot','Avg LTV':'snapshot',
};

// ─── KPI DISPLAY ORDER ───────────────────────────────────────
const KPI_ORDER = ['MRR AED','Active Clients','New Leads','Closed Deals','Conversion Rate','CAC AED'];
const sortKpis = (kpis) => [...kpis].sort((a, b) => {
  const ai = KPI_ORDER.indexOf(a), bi = KPI_ORDER.indexOf(b);
  if (ai === -1 && bi === -1) return 0;
  if (ai === -1) return 1;
  if (bi === -1) return -1;
  return ai - bi;
});
const getKpiType = (kpi) => KPI_TYPES[kpi] || 'cumulative';

// Extracts a numeric target from strings like "1M AED MRR", "100K Followers",
// "First 1,000 Sales". Returns null (not 0/NaN) if no number can be found,
// so callers can distinguish "unparseable" from "parsed as zero".
const parseGoalNumber = (str) => {
  if (!str) return null;
  const match = str.replace(/,/g,'').match(/(\d+(?:\.\d+)?)\s*(K|M)?/i);
  if (!match) return null;
  const num = parseFloat(match[1]);
  if (isNaN(num)) return null;
  const suffix = match[2]?.toUpperCase();
  if (suffix==='K') return num*1000;
  if (suffix==='M') return num*1000000;
  return num;
};

// ─── SEED DATA ───────────────────────────────────────────────
const SEED_BUSINESSES = [
  { id:'personal-brand', name:'My Personal Brand', color:'#F5A623', emoji:'👤', goal:'1M Followers', milestone:'100K Followers', deadline:'2026-06-01', momentum:'Building', priority:'Second', kpis:['Followers','Engagement %','Content Posts','Inbound Opps','Revenue AED','LinkedIn Followers'], current:{Followers:455,'Engagement %':80,'Content Posts':42,'Inbound Opps':0,'Revenue AED':0,'LinkedIn Followers':0}, target:{Followers:100000} },
  { id:'byforce', name:'ByForce.io', color:'#E74C3C', emoji:'⚡', goal:'1M AED MRR', milestone:'100K AED MRR', deadline:'2026-07-01', momentum:'Moving', priority:'First', kpis:['MRR AED','Active Clients','New Leads','Closed Deals','Conversion Rate','CAC AED'], current:{'MRR AED':14500,'Active Clients':3,'New Leads':0,'Closed Deals':0,'Conversion Rate':0,'CAC AED':0}, target:{'MRR AED':100000} },
  { id:'edemyx', name:'edemyx.online', color:'#2ECC71', emoji:'🎓', goal:'First 1,000 Sales', milestone:'First 100 Sales', deadline:'2026-06-01', momentum:'Slow', priority:'Third', kpis:['Sales Count','Revenue AED','Active Students','New Leads','Conversion Rate %','CAC AED'], current:{'Sales Count':0,'Revenue AED':0,'Active Students':0,'New Leads':0,'Conversion Rate %':0,'CAC AED':0}, target:{'Sales Count':100}, commitmentItems:['Build/edit 1 module of the course','Talk to 1 potential customer about it'] },
  { id:'clevene', name:'Clevene.com', color:'#3498DB', emoji:'🛍️', goal:'First 1,000 Sales', milestone:'First 100 Sales', deadline:'2026-06-01', momentum:'Testing', priority:'Fourth', kpis:['Orders','Revenue AED','Profit Margin %','Website Visitors','Conversion Rate %','ROAS'], current:{Orders:0,'Revenue AED':0,'Profit Margin %':0,'Website Visitors':0,'Conversion Rate %':0,ROAS:0}, target:{Orders:100} },
];

// ─── SHARED STYLES ───────────────────────────────────────────
const card = { background:CARD_FADE, borderRadius:12, padding:24, border:`1px solid ${T.border}`, marginBottom:20 };
const label = { fontSize:10, color:T.textDim, letterSpacing:3, fontFamily:T.mono, marginBottom:8, display:'block' };
const inp = { width:'100%', background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'10px 14px', color:T.text, fontSize:14, boxSizing:'border-box', fontFamily:'inherit', outline:'none' };
const inpTarget = { ...inp, borderStyle:'dashed', background:'#0a0a0a' };
const btn = (color='#F5A623') => ({ background:color, border:'none', borderRadius:8, padding:'10px 20px', color:'#000', fontWeight:700, fontSize:14, cursor:'pointer' });
// Frosted-glass shell — mirrors TeamStructureView.js's glassCard() so the Home greeting
// band matches the Team Structure look without exporting it across files.
const homeGlass = (accentColor, radius = 14) => ({
  position:'relative', background:'rgba(9,15,26,0.58)', backdropFilter:'blur(18px) saturate(150%)',
  WebkitBackdropFilter:'blur(18px) saturate(150%)', borderRadius:radius, border:`1px solid ${accentColor}35`,
  boxShadow:`inset 0 1px 0 rgba(255,255,255,0.05), 0 0 24px ${accentColor}12`, overflow:'hidden',
});
// eslint-disable-next-line no-unused-vars
const sectionTitle = (text) => <div style={{fontSize:11,color:T.textDim,letterSpacing:3,fontFamily:T.mono,marginBottom:16,paddingBottom:8,borderBottom:`1px solid ${T.border}`}}>{text}</div>;

// ─── CLIENT HELPERS ──────────────────────────────────────────
// Parse contract start date; falls back to old startDate then createdYear/Month
const parseClientStart = c => {
  const d = c.contractStartDate || c.startDate;
  if (d) { const p=d.split('-').map(Number); if(p[0]&&p[1]) return {year:p[0],month:p[1]}; }
  return {year:c.createdYear||0, month:c.createdMonth||0};
};
const parseClientEnd = c => {
  if (c.contractEndDate) { const p=c.contractEndDate.split('-').map(Number); if(p[0]&&p[1]) return {year:p[0],month:p[1]}; }
  return {year:null, month:null};
};
// Parse invoice month from "YYYY-MM" field (new) or "Mon YYYY" text field (legacy)
const parseInvoiceMonth = inv => {
  if (inv.invoiceMonth) { const [y,m]=inv.invoiceMonth.split('-').map(Number); if(y&&m) return {year:y,month:m}; }
  if (inv.month) {
    const parts=inv.month.trim().split(' ');
    if(parts.length===2){const mi=MONTHS.findIndex(mo=>mo===parts[0])+1;const y=parseInt(parts[1]);if(mi>0&&!isNaN(y)) return {year:y,month:mi};}
  }
  return null;
};
const fmtInvoiceMonth = inv => {
  if (inv.invoiceMonth) { const [y,m]=inv.invoiceMonth.split('-').map(Number); return `${MONTHS[m-1]} ${y}`; }
  return inv.month||'—';
};
const fmtContractPeriod = c => {
  const s=c.contractStartDate||c.startDate;
  if(!s) return '—';
  const fmt=d=>{const[y,m]=d.split('-').map(Number);return `${MONTHS[m-1]} ${y}`;};
  return `${fmt(s)} — ${c.contractEndDate?fmt(c.contractEndDate):'ongoing'}`;
};

const CRM_STAGES = ['New Lead','Contacted','Proposal Sent','Negotiation','Closed Won','Lost'];
const CRM_STAGE_COLORS = {'New Lead':'#3498DB','Contacted':'#9B59B6','Proposal Sent':'#F5A623','Negotiation':'#E67E22','Closed Won':'#2ECC71','Lost':'#E74C3C'};
const LEAD_SOURCES = ['Instagram','LinkedIn','Referral','Cold Outreach','Website','Event','Other'];
const TOUCH_TYPES = ['Call','WhatsApp','Email','Meeting','DM','Other'];

// ─── APP ─────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboardingSaving, setOnboardingSaving] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [activeView, setActiveView] = useState(VIEWS.HOME);
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => {
    try { return localStorage.getItem('lcc_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const setSidebarCollapsed = (val) => {
    setSidebarCollapsedState(val);
    try { localStorage.setItem('lcc_sidebar_collapsed', val ? '1' : '0'); } catch {}
  };
  const [expandedBusinesses, setExpandedBusinessesState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lcc_biz_expanded') || '{}'); } catch { return {}; }
  });
  const toggleBusinessExpanded = (id) => {
    setExpandedBusinessesState(prev => {
      const next = { ...prev, [id]: prev[id] === false ? true : false };
      try { localStorage.setItem('lcc_biz_expanded', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [businesses, setBusinesses] = useState([]);
  const [monthlyData, setMonthlyData] = useState({});
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [bottomTabs, setBottomTabs] = useState(DEFAULT_BOTTOM_TABS);
  const [changeLog, setChangeLog] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [monthlyFocus, setMonthlyFocus] = useState({});
  const [dailyCommitments, setDailyCommitments] = useState({});
  const [byforceClients, setByforceClients] = useState([]);
  const [byforceInvoices, setByforceInvoices] = useState([]);
  const [byforceLeads, setByforceLeads] = useState([]);
  // FIX 1: removed pipelineLink state — no longer needed, funnels are self-contained

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u && !ALLOWED_EMAILS.includes(u.email)) {
        // Not allowlisted: never set user, never load data. Sign-out runs in the
        // background — the UI is already locked down regardless of when it resolves.
        setUser(null); setAccessDenied(true); setAuthLoading(false);
        signOut(auth).catch(() => {});
        return;
      }
      setAccessDenied(false);
      setUser(u); setAuthLoading(false);
      if (u) {
        loadData();
        try {
          const profileSnap = await getDoc(doc(db, 'users', u.uid));
          setNeedsOnboarding(!profileSnap.exists());
        } catch (e) {
          setNeedsOnboarding(false);
        }
      }
    });
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      collection(db, 'businesses', 'byforce', 'clients'),
      (snap) => setByforceClients(snap.docs.map(d => ({...d.data(), id: d.id}))),
      (err) => console.error('byforce clients listener:', err)
    );
    return () => unsub();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      collectionGroup(db, 'invoices'),
      (snap) => {
        setByforceInvoices(
          snap.docs
            .filter(d => d.ref.path.startsWith('businesses/byforce/clients/'))
            .map(d => ({...d.data(), id: d.id}))
        );
      },
      (err) => console.error('byforce invoices listener:', err)
    );
    return () => unsub();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      collection(db, 'businesses', 'byforce', 'leads'),
      (snap) => setByforceLeads(snap.docs.map(d => ({...d.data(), id: d.id}))),
      (err) => console.error('byforce leads listener:', err)
    );
    return () => unsub();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sync byforce KPIs to Firestore whenever clients/invoices/leads change.
  // Reconciles EVERY month that has invoice data (not just the current real-world month), so a
  // past month's actuals stay correct even if invoices for it are added/edited/paid later.
  // Auto-sync always wins here: actuals are invoice-derived and this always overwrites them,
  // including any value a person typed into Enter Monthly Data for one of these 5 fields.
  useEffect(() => {
    if (!user || loading) return;
    const cy=new Date().getFullYear(), cm=new Date().getMonth()+1;

    const activeClientsAsOf = (y,m) => byforceClients.filter(c=>{
      if(c.status!=='Active') return false;
      const {year:sy,month:sm}=parseClientStart(c);
      if(!sy||!sm||sy>y||(sy===y&&sm>m)) return false;
      const {year:ey,month:em}=parseClientEnd(c);
      if(ey&&(ey<y||(ey===y&&em<m))) return false;
      return true;
    }).length;

    // Months to reconcile: every distinct invoiceMonth present in the invoices data, plus the
    // current real-world month (so live tracking of "this month" keeps working even before any
    // invoice for it exists).
    const monthKeys = new Set([`${cy}_${cm}`]);
    byforceInvoices.forEach(inv=>{ const p=parseInvoiceMonth(inv); if(p) monthKeys.add(`${p.year}_${p.month}`); });

    const ACTUAL_FIELDS=['MRR AED','Active Clients','New Leads','Closed Deals','Conversion Rate'];
    const updates = {};
    monthKeys.forEach(mk=>{
      const [y,m]=mk.split('_').map(Number);
      const mrr=byforceInvoices.filter(inv=>{
        if(inv.status!=='Paid') return false;
        const p=parseInvoiceMonth(inv); return p&&p.year===y&&p.month===m;
      }).reduce((s,inv)=>s+(inv.amount||0),0);
      const activeCount=activeClientsAsOf(y,m);
      const newLeads=byforceLeads.filter(l=>{const d=new Date(l.createdAt);return d.getFullYear()===y&&d.getMonth()+1===m;}).length;
      const closed=byforceLeads.filter(l=>{if(l.status!=='converted'||!l.convertedAt)return false;const d=new Date(l.convertedAt);return d.getFullYear()===y&&d.getMonth()+1===m;}).length;
      const convRate=newLeads>0?parseFloat(((closed/newLeads)*100).toFixed(1)):0;

      const key=`byforce_${y}_${m}`;
      const existing=monthlyData[key]||{};
      const next={...existing,bizId:'byforce',month:m,year:y,'MRR AED':mrr,'Active Clients':activeCount,'New Leads':newLeads,'Closed Deals':closed,'Conversion Rate':convRate,_targets:existing._targets||{}};

      // Skip the write entirely if nothing actually changed, to avoid a Firestore write on every
      // snapshot tick for months whose numbers are already correct.
      if (ACTUAL_FIELDS.some(k=>(existing[k]||0)!==next[k])) updates[key]=next;
    });

    if (Object.keys(updates).length===0) return;

    (async () => {
      try {
        const batch=writeBatch(db);
        Object.entries(updates).forEach(([key,data])=>batch.set(doc(db,'monthlyData',key),data));
        await batch.commit();
        setMonthlyData(prev=>({...prev,...updates}));
      } catch(e) { console.error('byforce monthly auto-sync failed:', e); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byforceClients, byforceInvoices, byforceLeads, loading]);

  async function loadData() {
    try {
      const bizSnap = await getDocs(collection(db,'businesses'));
      const existingIds = bizSnap.docs.map(d=>d.id);
      const missing = SEED_BUSINESSES.filter(b=>!existingIds.includes(b.id));
      for (const biz of missing) await setDoc(doc(db,'businesses',biz.id),biz);
      const loadedBizs = bizSnap.docs.map(d=>({...d.data(),id:d.id}));
      // Migrate byforce kpis if Firestore still has stale entries
      const STALE_BF_KPIS = ['Pipeline Value AED','LTV AED'];
      const CORRECT_BF_KPIS = ['MRR AED','Active Clients','New Leads','Closed Deals','Conversion Rate','CAC AED'];
      const bfIdx = loadedBizs.findIndex(b=>b.id==='byforce');
      if (bfIdx !== -1 && loadedBizs[bfIdx].kpis?.some(k=>STALE_BF_KPIS.includes(k))) {
        loadedBizs[bfIdx] = {...loadedBizs[bfIdx], kpis: CORRECT_BF_KPIS};
        await setDoc(doc(db,'businesses','byforce'), loadedBizs[bfIdx]);
      }
      // Backfill 'LinkedIn Followers' onto the existing personal-brand doc (new manual KPI)
      const pbIdx = loadedBizs.findIndex(b=>b.id==='personal-brand');
      if (pbIdx !== -1 && !loadedBizs[pbIdx].kpis?.includes('LinkedIn Followers')) {
        loadedBizs[pbIdx] = {...loadedBizs[pbIdx], kpis:[...loadedBizs[pbIdx].kpis,'LinkedIn Followers'], current:{...loadedBizs[pbIdx].current,'LinkedIn Followers':loadedBizs[pbIdx].current?.['LinkedIn Followers']||0}};
        await setDoc(doc(db,'businesses','personal-brand'), loadedBizs[pbIdx]);
      }
      setBusinesses([...loadedBizs,...missing]);
      const dataSnap = await getDocs(collection(db,'monthlyData'));
      const data = {}; dataSnap.docs.forEach(d=>{data[d.id]=d.data();}); setMonthlyData(data);
      const dcSnap = await getDocs(collection(db,'dailyCommitments'));
      const dc = {}; dcSnap.docs.forEach(d=>{dc[d.id]=d.data();}); setDailyCommitments(dc);
      const logSnap = await getDocs(collection(db,'changeLog'));
      setChangeLog(logSnap.docs.map(d=>({...d.data(),id:d.id})).sort((a,b)=>b.timestamp-a.timestamp));
      const settingsSnap = await getDocs(collection(db,'settings'));
      if (!settingsSnap.empty) {
        const s = settingsSnap.docs[0].data();
        if (s.logoUrl) setLogoUrl(s.logoUrl);
        if (s.monthlyFocus) setMonthlyFocus(s.monthlyFocus);
        if (s.bottomTabs?.length===3) setBottomTabs(s.bottomTabs);
        // FIX 3: removed s.pipelineLink load — funnels handled independently
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  const showNotif = (msg,type='success') => { setNotification({msg,type}); setTimeout(()=>setNotification(null),3000); };

  const logChange = async (bizId,bizName,field,oldVal,newVal) => {
    const entry = {bizId,bizName,field,oldVal:String(oldVal),newVal:String(newVal),timestamp:Date.now(),date:new Date().toLocaleString()};
    try { const ref=doc(collection(db,'changeLog')); await setDoc(ref,entry); setChangeLog(prev=>[{...entry,id:ref.id},...prev]); } catch(e){}
  };

  const saveSettings = async (updates) => {
    try { const s=await getDocs(collection(db,'settings')); const ex=s.empty?{}:s.docs[0].data(); await setDoc(doc(db,'settings','main'),{...ex,...updates}); } catch(e){}
  };

  const saveBusiness = async (biz,oldBiz) => {
    try {
      await setDoc(doc(db,'businesses',biz.id),biz);
      setBusinesses(prev=>prev.find(b=>b.id===biz.id)?prev.map(b=>b.id===biz.id?biz:b):[...prev,biz]);
      if (oldBiz) {
        if (oldBiz.goal!==biz.goal) await logChange(biz.id,biz.name,'Goal',oldBiz.goal,biz.goal);
        if (oldBiz.momentum!==biz.momentum) await logChange(biz.id,biz.name,'Momentum',oldBiz.momentum,biz.momentum);
        Object.keys(biz.current||{}).forEach(k=>{if((oldBiz.current||{})[k]!==biz.current[k]) logChange(biz.id,biz.name,k,(oldBiz.current||{})[k]||0,biz.current[k]);});
      }
      showNotif('Business saved!');
    } catch(e) { showNotif('Error saving','error'); }
  };

  const deleteBusiness = async (bizId) => {
    try { await deleteDoc(doc(db,'businesses',bizId)); setBusinesses(prev=>prev.filter(b=>b.id!==bizId)); showNotif('Business deleted!'); }
    catch(e) { showNotif('Error deleting','error'); }
  };

  // FIX 4: added silent=false param — pipeline bumpKpi passes silent=true to suppress toast spam
  const saveMonthlyData = async (bizId,month,year,values,targets,oldValues,silent=false) => {
    const key=`${bizId}_${year}_${month}`;
    try {
      await setDoc(doc(db,'monthlyData',key),{bizId,month,year,...values,_targets:targets||{}});
      setMonthlyData(prev=>({...prev,[key]:{bizId,month,year,...values,_targets:targets||{}}}));
      const biz=businesses.find(b=>b.id===bizId);
      if (oldValues) Object.keys(values).forEach(k=>{if(oldValues[k]!==values[k]) logChange(bizId,biz?.name||bizId,`${k} (${MONTHS[month-1]} ${year})`,oldValues[k]||0,values[k]);});
      if (!silent) showNotif('Data saved!');
    } catch(e) { showNotif('Error saving','error'); }
  };

  const saveLogoUrl = async (url) => { await saveSettings({logoUrl:url}); setLogoUrl(url); showNotif('Logo saved!'); };
  const saveBottomTabs = async (tabs) => { await saveSettings({bottomTabs:tabs}); setBottomTabs(tabs); showNotif('Quick bar updated!'); };

  // ─── DAILY COMMITMENT TRACKING ──────────────────────────────
  const toggleCommitmentItem = async (businessId, date, itemText) => {
    const key = `${businessId}_${date}`;
    const biz = businesses.find(b=>b.id===businessId);
    const items = biz?.commitmentItems || [];
    const existing = dailyCommitments[key];
    const checked = { ...(existing?.checked||{}), [itemText]: !existing?.checked?.[itemText] };
    const completedCount = items.filter(i=>checked[i]).length;
    const totalCount = items.length;
    const updated = { businessId, date, checked, completedCount, totalCount };
    try {
      await setDoc(doc(db,'dailyCommitments',key), updated);
      setDailyCommitments(prev=>({...prev,[key]:updated}));
    } catch(e) { showNotif('Error saving commitment','error'); }
  };

  // Computed client-side from the dailyCommitments already loaded in state — no extra reads.
  const calculateStreak = (businessId) => {
    const completedDates = new Set(
      Object.values(dailyCommitments)
        .filter(d=>d.businessId===businessId && d.totalCount>0 && d.completedCount===d.totalCount)
        .map(d=>d.date)
    );

    // Current streak: walk backward from today (local time). If today isn't complete
    // yet, that shouldn't break an otherwise-live streak, so start from yesterday.
    let cursor = new Date();
    if (!completedDates.has(getLocalDateKey(cursor))) cursor.setDate(cursor.getDate()-1);
    let currentStreak = 0;
    while (completedDates.has(getLocalDateKey(cursor))) { currentStreak++; cursor.setDate(cursor.getDate()-1); }

    // Longest streak: scan all completed dates ascending, track consecutive runs.
    const sortedAsc = [...completedDates].sort();
    let longestStreak = 0, run = 0, prevDate = null;
    for (const ds of sortedAsc) {
      if (prevDate) {
        const expected = parseLocalDateKey(prevDate); expected.setDate(expected.getDate()+1);
        run = getLocalDateKey(expected)===ds ? run+1 : 1;
      } else run = 1;
      longestStreak = Math.max(longestStreak, run);
      prevDate = ds;
    }

    return { currentStreak, longestStreak };
  };

  // Average daily completion ratio over an inclusive local-date range. Days with no
  // tracked commitments (totalCount===0 / missing) are excluded from the average,
  // not counted as 0% — null means "no tracked days in this range" (not "0%").
  const getCommitmentRate = (businessId, startDate, endDate) => {
    let totalRatio = 0, count = 0;
    let cursor = new Date(startDate);
    while (cursor <= endDate) {
      const entry = dailyCommitments[`${businessId}_${getLocalDateKey(cursor)}`];
      if (entry && entry.totalCount > 0) { totalRatio += entry.completedCount/entry.totalCount; count++; }
      cursor.setDate(cursor.getDate()+1);
    }
    return count > 0 ? totalRatio/count : null;
  };

  // WoW/MoM/YoY as rolling windows (last 7/30/365 days vs the equal-length window
  // immediately before it) rather than calendar-aligned periods — avoids partial-week/
  // month noise and is simpler to reason about for a daily habit tracker.
  const getPeriodTrends = (businessId) => {
    const today = new Date();
    const daysAgo = (n) => { const d=new Date(today); d.setDate(d.getDate()-n); return d; };
    const toPct = (v) => v==null ? null : Math.round(v*100);
    const window = (n) => {
      const current = toPct(getCommitmentRate(businessId, daysAgo(n-1), today));
      const previous = toPct(getCommitmentRate(businessId, daysAgo(n*2-1), daysAgo(n)));
      const delta = (current!=null && previous!=null) ? current-previous : null;
      return { current, previous, delta };
    };
    return { wow: window(7), mom: window(30), yoy: window(365) };
  };

  const getByforceKpis = () => {
    const cm=new Date().getMonth()+1, cy=new Date().getFullYear();
    const activeNow=byforceClients.filter(c=>{
      if(c.status!=='Active') return false;
      const {year:sy,month:sm}=parseClientStart(c);
      if(!sy||!sm||sy>cy||(sy===cy&&sm>cm)) return false;
      const {year:ey,month:em}=parseClientEnd(c);
      if(ey&&(ey<cy||(ey===cy&&em<cm))) return false;
      return true;
    });
    const mrr=byforceInvoices.filter(inv=>{
      if(inv.status!=='Paid') return false;
      const p=parseInvoiceMonth(inv); return p&&p.year===cy&&p.month===cm;
    }).reduce((s,inv)=>s+(inv.amount||0),0);
    const newLeads=byforceLeads.filter(l=>{const d=new Date(l.createdAt);return d.getFullYear()===cy&&d.getMonth()+1===cm;}).length;
    const closed=byforceLeads.filter(l=>{if(l.status!=='converted'||!l.convertedAt)return false;const d=new Date(l.convertedAt);return d.getFullYear()===cy&&d.getMonth()+1===cm;}).length;
    const convRate=newLeads>0?parseFloat(((closed/newLeads)*100).toFixed(1)):0;
    return {'MRR AED':mrr,'Active Clients':activeNow.length,'New Leads':newLeads,'Closed Deals':closed,'Conversion Rate':convRate};
  };
  const saveMonthlyFocus = async (bizId,text) => { const key=`${bizId}_${new Date().getFullYear()}_${new Date().getMonth()+1}`; const updated={...monthlyFocus,[key]:text}; setMonthlyFocus(updated); await saveSettings({monthlyFocus:updated}); };
  const convertLeadToClient = async (lead, clientData) => {
    const cm=new Date().getMonth()+1, cy=new Date().getFullYear();
    const clientId=`client_${Date.now()}`;
    await setDoc(doc(db,'businesses','byforce','clients',clientId),{...clientData,id:clientId,createdAt:Date.now(),createdMonth:cm,createdYear:cy});
    await updateDoc(doc(db,'businesses','byforce','leads',lead.id),{status:'converted',convertedAt:Date.now(),stage:'Closed Won',activities:[...(lead.activities||[]),{type:'converted',timestamp:Date.now()}]});
    showNotif(`${lead.name} converted to client!`);
  };
  // FIX 2: removed savePipelineLink — no longer needed

  const getKpiValue = (biz,kpi,year) => {
    const y=year||new Date().getFullYear();
    if (getKpiType(kpi)==='snapshot') {
      for(let m=new Date().getMonth()+1;m>=1;m--){const v=monthlyData[`${biz.id}_${y}_${m}`]?.[kpi];if(v!==undefined&&v!==0)return v;}
      return biz.current?.[kpi]||0;
    } else {
      let sum=biz.current?.[kpi]||0;
      for(let m=1;m<=12;m++){const v=monthlyData[`${biz.id}_${y}_${m}`]?.[kpi];if(v!==undefined)sum+=v;}
      return sum;
    }
  };

  const getMonthlyTarget = (bizId,kpi) => { const cm=new Date().getMonth()+1,cy=new Date().getFullYear(); return monthlyData[`${bizId}_${cy}_${cm}`]?._targets?.[kpi]||0; };
  const getMonthlyActual = (bizId,kpi) => { const cm=new Date().getMonth()+1,cy=new Date().getFullYear(); return monthlyData[`${bizId}_${cy}_${cm}`]?.[kpi]||0; };
  const getMonthlyFocus = (bizId) => { const key=`${bizId}_${new Date().getFullYear()}_${new Date().getMonth()+1}`; return monthlyFocus[key]||''; };
  // Sums a cumulative-type KPI across all 12 months of the current year — same
  // accumulation logic as the "Total" column in BusinessView's monthly table.
  const getYearTotal = (bizId,kpi) => {
    const cy=new Date().getFullYear();
    let total=0;
    for (let m=1;m<=12;m++) { const v=monthlyData[`${bizId}_${cy}_${m}`]?.[kpi]; if (v!=null) total+=v; }
    return total;
  };

  const handleLogin = async () => { try { await signInWithPopup(auth,googleProvider); } catch(e){} };

  const handleEmailAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (authMode === 'signup' && !ALLOWED_EMAILS.includes(authEmail)) {
      setAuthError('This email is not authorized to create an account.');
      return;
    }
    setAuthSubmitting(true);
    try {
      if (authMode === 'signin') await signInWithEmail(authEmail, authPassword);
      else await signUpWithEmail(authEmail, authPassword);
    } catch (err) {
      setAuthError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setAuthSubmitting(false);
    }
  };
  const handleSignOut = async () => { try { await signOut(auth); } catch(e){} };
  const getBiz = (id) => businesses.find(b=>b.id===id);
  const daysLeft = (d) => Math.max(0,Math.ceil((new Date(d)-new Date())/86400000));
  const rawDaysLeft = (d) => Math.ceil((new Date(d)-new Date())/86400000);
  const daysSinceSet = (d) => d ? Math.max(0,Math.floor((new Date()-new Date(d))/86400000)) : null;
  const getUrgencyTier = (rawDl) => {
    if (rawDl===null||rawDl===undefined||isNaN(rawDl)||rawDl>30) return {color:'#2ECC71',message:null,pulse:false};
    if (rawDl>=21) return {color:'#2ECC71',message:"Start building now — don't procrastinate or delay",pulse:false};
    if (rawDl>=13) return {color:'#F5A623',message:"Deadline is coming closer — push harder",pulse:false};
    if (rawDl>=1) return {color:'#E74C3C',message:"You're running out of time",pulse:true};
    return {color:'#8B0000',message:"Deadline passed — reset or escalate",pulse:true};
  };

  if (authLoading) return <div style={{background:T.bg,minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:T.text,fontSize:16,fontFamily:T.mono,letterSpacing:2}}>INITIALIZING...</div>;

  if (accessDenied) return (
    <div style={{minHeight:'100vh',background:T.bg,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:48,textAlign:'center',maxWidth:400,width:'90%'}}>
        <div style={{fontSize:48,marginBottom:20}}>🔒</div>
        <h1 style={{color:T.text,fontSize:22,fontWeight:700,marginBottom:8}}>Access Restricted</h1>
        <p style={{color:T.textDim,fontSize:14}}>This account is not authorized to view this dashboard.</p>
      </div>
    </div>
  );

  if (!user) return (
    <div style={{minHeight:'100vh',background:T.bg,display:'flex',fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif'}}>
      <style>{`
        .auth-left-panel { display:none; }
        .auth-mobile-brand { display:flex; }
        .auth-google-btn { transition: background 0.15s; }
        .auth-google-btn:hover { background: rgba(255,255,255,0.9) !important; }
        @media (min-width: 1024px) {
          .auth-left-panel { display:flex !important; }
          .auth-mobile-brand { display:none !important; }
        }
      `}</style>

      {/* Decorative panel — desktop only. Quote is placeholder flavor text, easy to swap. */}
      <div className="auth-left-panel" style={{flex:1,position:'relative',flexDirection:'column',padding:'48px 56px',overflow:'hidden',background:`linear-gradient(160deg,#0a0f1a 0%,${T.bg} 60%,#030509 100%)`,borderRight:`1px solid ${T.border}`}}>
        <div style={{position:'absolute',inset:0,pointerEvents:'none'}}>
          <FloatingPaths position={1}/>
          <div style={{position:'absolute',inset:0}}><FloatingPaths position={-1}/></div>
        </div>
        <div style={{position:'relative',zIndex:1}}>
          <img src="/singlemass-wordmark-new-light.svg" alt="Singlemass" style={{height:56}}/>
        </div>
        <div style={{position:'relative',zIndex:1,marginTop:'auto',maxWidth:420}}>
          <div style={{fontSize:32,color:CARD_DIM_TEXT,lineHeight:1,marginBottom:4,fontFamily:'Georgia,serif'}}>"</div>
          <p style={{fontSize:20,color:T.text,lineHeight:1.5,marginBottom:14}}>
            One command center for every business you're building — pipeline, revenue, and the people, all in one place.
          </p>
          <div style={{fontSize:14,color:CARD_DIM_TEXT,fontFamily:T.mono,fontWeight:600}}>— Fahd, Founder</div>
        </div>
      </div>

      {/* Sign-in form */}
      <div style={{flex:1,position:'relative',display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',padding:'40px 16px',overflow:'hidden'}}>
        {/* Decorative corner fade — single soft radial glow, pure CSS, no library */}
        <div style={{position:'absolute',top:0,right:0,width:'70%',height:'70%',background:'radial-gradient(circle at top right, rgba(255,255,255,0.08) 0%, transparent 65%)',pointerEvents:'none'}}/>

        <div style={{position:'relative',width:'100%',maxWidth:384}}>
          <div className="auth-mobile-brand" style={{justifyContent:'center',marginBottom:28}}>
            {logoUrl?<img src={logoUrl} alt="Logo" style={{width:64,height:64,objectFit:'contain',borderRadius:12}}/>:<img src="/singlemass-wordmark-light.svg" alt="Singlemass" style={{height:40}}/>}
          </div>
          <div style={{textAlign:'center',marginBottom:28}}>
            <h1 style={{fontSize:24,fontWeight:700,color:T.text,letterSpacing:'0.01em',marginBottom:8}}>{authMode==='signin'?'Welcome back':'Create your account'}</h1>
            <p style={{color:'rgba(255,255,255,0.5)',fontSize:16}}>{authMode==='signin'?'Sign in to your Singlemass command center.':'Sign up to start your Singlemass command center.'}</p>
          </div>
          <button onClick={handleLogin} className="auth-google-btn" style={{width:'100%',height:44,padding:'0 16px',background:'#fff',color:'#0a0a0a',border:'none',borderRadius:8,fontSize:14,fontWeight:500,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
            <img src="https://www.google.com/favicon.ico" width="18" height="18" alt="G"/>Continue with Google
          </button>
          <div style={{display:'flex',alignItems:'center',gap:10,margin:'20px 0'}}>
            <div style={{flex:1,height:1,background:T.border}}/>
            <span style={{color:T.textFaint,fontSize:11,fontFamily:T.mono}}>OR</span>
            <div style={{flex:1,height:1,background:T.border}}/>
          </div>
          <form onSubmit={handleEmailAuthSubmit} style={{display:'flex',flexDirection:'column',gap:10}}>
            <Input type="email" placeholder="Email" value={authEmail} onChange={e=>setAuthEmail(e.target.value)} required style={{height:44}}/>
            <Input type="password" placeholder="Password" value={authPassword} onChange={e=>setAuthPassword(e.target.value)} required style={{height:44}}/>
            {authError && <p style={{color:'#E74C3C',fontSize:12,margin:0}}>{authError}</p>}
            <Button type="submit" disabled={authSubmitting} style={{width:'100%',height:44}}>
              {authSubmitting ? 'Please wait…' : (authMode==='signin' ? 'Sign in' : 'Create account')}
            </Button>
          </form>
          <p style={{textAlign:'center',fontSize:13,color:T.textDim,marginTop:16}}>
            {authMode==='signin' ? "Don't have an account? " : 'Already have an account? '}
            <button type="button" onClick={()=>{setAuthMode(authMode==='signin'?'signup':'signin'); setAuthError('');}} style={{background:'none',border:'none',color:T.text,fontWeight:600,cursor:'pointer',padding:0,fontSize:13,textDecoration:'underline'}}>
              {authMode==='signin' ? 'Create one' : 'Sign in'}
            </button>
          </p>
          <p style={{color:T.textFaint,fontSize:12,marginTop:24,fontFamily:T.mono,letterSpacing:'0.05em',textAlign:'center'}}>AUTHORIZED ACCESS ONLY</p>
        </div>
      </div>
    </div>
  );

  if (user && !accessDenied && needsOnboarding) return (
    <div style={{minHeight:'100vh',background:T.bg,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',padding:16}}>
      <div style={{maxWidth:420,width:'100%',textAlign:'center'}}>
        <h1 style={{fontSize:22,fontWeight:700,color:T.text,marginBottom:8}}>What are you signing up as?</h1>
        <p style={{color:T.textDim,fontSize:14,marginBottom:28}}>This helps us tailor Singlemass to how you work.</p>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {['Business','Freelancer','Individual'].map(opt => (
            <Button
              key={opt}
              variant="outline"
              size="lg"
              disabled={onboardingSaving}
              onClick={async () => {
                setOnboardingSaving(true);
                const providerId = user.providerData?.[0]?.providerId === 'google.com' ? 'google' : 'password';
                try {
                  await createUserProfile(user.uid, user.email, opt.toLowerCase(), providerId);
                  setNeedsOnboarding(false);
                } finally {
                  setOnboardingSaving(false);
                }
              }}
            >
              {opt}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{display:'flex',height:'100vh',background:T.bg,color:T.text,fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',fontSize:14}}>
      {notification&&<div style={{position:'fixed',top:20,right:20,zIndex:1000,background:notification.type==='success'?'#0a2a1a':'#2a0a0a',color:notification.type==='success'?'#2ECC71':'#E74C3C',padding:'12px 20px',borderRadius:8,fontSize:13,fontWeight:500,border:`1px solid ${notification.type==='success'?'#2ECC7140':'#E74C3C40'}`}}>{notification.type==='success'?'✓':' ✕'} {notification.msg}</div>}
      {showWizard&&<BusinessWizard onSave={(biz)=>{saveBusiness(biz,null);setShowWizard(false);}} onClose={()=>setShowWizard(false)}/>}

      {/* SIDEBAR */}
      {sidebarOpen&&<div className="sidebar-backdrop" onClick={()=>setSidebarOpen(false)}/>}
      <div className="sidebar" style={{width:!sidebarOpen?0:(sidebarCollapsed?68:256),overflow:'hidden',transition:'width 0.3s',background:CARD_FADE,borderRight:`1px solid ${T.border}`,flexShrink:0,display:'flex',flexDirection:'column',position:'sticky',top:0,height:'100vh'}}>
        <button onClick={()=>setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed?'Expand sidebar':'Collapse sidebar'} style={{position:'absolute',top:20,right:8,width:22,height:22,borderRadius:'50%',background:T.card,border:`1px solid ${T.border}`,color:T.textDim,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0,zIndex:5}}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{transform:sidebarCollapsed?'rotate(180deg)':'none',transition:'transform 0.2s'}}><path d="M6.5 1L2.5 5l4 4"/></svg>
        </button>
        <div style={{padding:sidebarCollapsed?'20px 8px':'20px 16px',borderBottom:`1px solid ${T.border}`,display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
          {logoUrl?<img src={logoUrl} alt="Logo" style={{width:52,height:52,objectFit:'contain',borderRadius:10,marginBottom:4}}/>:sidebarCollapsed?<img src="/singlemass-icon-light.png" alt="Singlemass" style={{width:32,height:32,objectFit:'contain',marginBottom:4}}/>:<img src="/singlemass-wordmark-light.svg" alt="Singlemass" style={{height:40,marginBottom:4}}/>}
        </div>
        <nav style={{padding:'12px 8px',flex:1,overflowY:'auto'}}>
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 8L8 2l6 6"/><path d="M4 6.5v6.5h3v-3.5h2v3.5h3v-6.5"/></svg>} label="Home" active={activeView===VIEWS.HOME} onClick={()=>{setActiveView(VIEWS.HOME);setActiveBusiness(null);}}/>
          {!sidebarCollapsed&&<div style={{fontSize:11,color:T.textDim,letterSpacing:2,padding:'16px 10px 6px',fontFamily:T.mono}}>MY BUSINESSES</div>}
          {businesses.map(b=>{
            const bizExpanded = expandedBusinesses[b.id] !== false;
            return (
            <React.Fragment key={b.id}>
              <SideItem collapsed={sidebarCollapsed} icon={getBizIcon(b.name,b.color)} label={b.name} color={b.color} active={activeBusiness===b.id&&activeView===VIEWS.BUSINESS} onClick={()=>{setActiveBusiness(b.id);setActiveView(VIEWS.BUSINESS);}} onToggleExpand={b.id==='byforce'?()=>toggleBusinessExpanded(b.id):undefined} expanded={bizExpanded}/>
              {activeBusiness===b.id&&b.id==='byforce'&&bizExpanded&&(
                <div style={{paddingLeft:sidebarCollapsed?0:16,marginTop:2}}>
                  <SideItem collapsed={sidebarCollapsed} icon={<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/></svg>} label="Clients" active={activeView===VIEWS.CLIENTS} onClick={()=>{setActiveBusiness(b.id);setActiveView(VIEWS.CLIENTS);}}/>
                  <SideItem collapsed={sidebarCollapsed} icon={<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 4h12M2 8h8M2 12h5"/><circle cx="13" cy="11" r="2.5"/><path d="M13 9V7"/></svg>} label="Leads" active={activeView===VIEWS.CRM} onClick={()=>{setActiveBusiness(b.id);setActiveView(VIEWS.CRM);}}/>
                  <SideItem collapsed={sidebarCollapsed} icon={<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h12l-4.5 5.5V13l-3 1.5V8.5L2 3z"/></svg>} label="Reports" active={activeView===VIEWS.CRM_REPORTS} onClick={()=>{setActiveBusiness(b.id);setActiveView(VIEWS.CRM_REPORTS);}}/>
                </div>
              )}
            </React.Fragment>
          );})}
          <div onClick={()=>setShowWizard(true)} title={sidebarCollapsed?'Add Business':undefined} style={{display:'flex',alignItems:'center',justifyContent:sidebarCollapsed?'center':'flex-start',gap:sidebarCollapsed?0:8,padding:'7px 10px',borderRadius:8,cursor:'pointer',color:T.textDim,fontSize:12,marginTop:4,border:`1px dashed ${T.border}`}}>
            <span style={{fontSize:12}}>＋</span>{!sidebarCollapsed&&<span>Add Business</span>}
          </div>
          {!sidebarCollapsed&&<div style={{fontSize:11,color:T.textDim,letterSpacing:2,padding:'16px 10px 6px',fontFamily:T.mono}}>VIEWS</div>}
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="2" y="9" width="3" height="5"/><rect x="6.5" y="6" width="3" height="8"/><rect x="11" y="3" width="3" height="11"/></svg>} label="Analysis" active={activeView===VIEWS.ANALYSIS} onClick={()=>setActiveView(VIEWS.ANALYSIS)}/>
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h12l-4.5 5.5V13l-3 1.5V8.5L2 3z"/></svg>} label="Pipeline & Funnels" active={activeView===VIEWS.PIPELINE} onClick={()=>setActiveView(VIEWS.PIPELINE)}/>
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="12" height="11" rx="1"/><path d="M2 7h12M5 1v3M11 1v3"/></svg>} label="Year Overview" active={activeView===VIEWS.YEAR} onClick={()=>setActiveView(VIEWS.YEAR)}/>
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="8" height="8" rx="1"/><path d="M6 1v3M10 1v3M6 12v3M10 12v3M1 6h3M12 6h3M1 10h3M12 10h3"/></svg>} label="Simulation" active={activeView===VIEWS.SIMULATION} onClick={()=>setActiveView(VIEWS.SIMULATION)}/>
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9.5l2.5-3 2 2L11 4l3 3.5"/><circle cx="4.5" cy="6.5" r="1"/><circle cx="6.5" cy="8.5" r="1"/><circle cx="8.5" cy="5.5" r="1"/><circle cx="11" cy="7.5" r="1"/></svg>} label="Consistency" active={activeView===VIEWS.CONSISTENCY} onClick={()=>setActiveView(VIEWS.CONSISTENCY)}/>
          {!sidebarCollapsed&&<div style={{fontSize:11,color:T.textDim,letterSpacing:2,padding:'16px 10px 6px',fontFamily:T.mono}}>TEAM</div>}
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="3" r="2"/><circle cx="3" cy="13" r="2"/><circle cx="13" cy="13" r="2"/><path d="M8 5v3M8 8L3 11M8 8l5 3"/></svg>} label="Team Structure" active={activeView===VIEWS.TEAM_STRUCTURE} onClick={()=>setActiveView(VIEWS.TEAM_STRUCTURE)}/>
          {!sidebarCollapsed&&<div style={{fontSize:11,color:T.textDim,letterSpacing:2,padding:'16px 10px 6px',fontFamily:T.mono}}>MANAGE</div>}
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="8" cy="8" r="2.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.1 3.1l1.4 1.4M11.5 11.5l1.4 1.4M3.1 12.9l1.4-1.4M11.5 4.5l1.4-1.4"/></svg>} label="Manage Businesses" active={activeView===VIEWS.MANAGE} onClick={()=>setActiveView(VIEWS.MANAGE)}/>
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="8" cy="4.5" rx="5" ry="2"/><path d="M3 4.5V8c0 1.1 2.24 2 5 2s5-.9 5-2V4.5"/><path d="M3 8v3.5c0 1.1 2.24 2 5 2s5-.9 5-2V8"/></svg>} label="Enter Monthly Data" active={activeView===VIEWS.DATA} onClick={()=>setActiveView(VIEWS.DATA)}/>
          <SideItem collapsed={sidebarCollapsed} icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><line x1="2" y1="4" x2="14" y2="4"/><line x1="2" y1="8" x2="14" y2="8"/><line x1="2" y1="12" x2="14" y2="12"/><circle cx="5" cy="4" r="1.5" fill="currentColor"/><circle cx="10" cy="8" r="1.5" fill="currentColor"/><circle cx="5" cy="12" r="1.5" fill="currentColor"/></svg>} label="Settings" active={activeView===VIEWS.SETTINGS} onClick={()=>setActiveView(VIEWS.SETTINGS)}/>
        </nav>
        <div style={{padding:sidebarCollapsed?'12px 8px':'12px 16px',borderTop:`1px solid ${T.border}`}}>
          <button onClick={handleSignOut} title={sidebarCollapsed?'Sign Out':undefined} style={{width:'100%',background:T.bg,color:'#e8e9ed',border:`1px solid ${T.border}`,borderRadius:8,padding:'8px',fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M10.5 11l3.5-3-3.5-3M14 8H6"/></svg>
            {!sidebarCollapsed&&'Sign Out'}
          </button>
        </div>
      </div>

      {/* MAIN */}
      <div className="main-content" style={{flex:1,display:'flex',flexDirection:'column',overflow:'auto',minWidth:0,position:'relative'}}>
        <div style={{position:'fixed',top:0,left:0,right:0,height:'100vh',pointerEvents:'none',zIndex:0,background:'radial-gradient(1100px 650px at 85% -10%, rgba(245,166,35,0.07) 0%, transparent 60%), radial-gradient(800px 500px at -5% 100%, rgba(255,255,255,0.035) 0%, transparent 60%)'}}/>
        <div className="top-bar" style={{display:'flex',alignItems:'center',padding:'12px 24px',borderBottom:`1px solid ${T.border}`,background:CARD_FADE,position:'sticky',top:0,zIndex:10}}>
          <button onClick={()=>setSidebarOpen(!sidebarOpen)} style={{background:'none',border:'none',color:T.textDim,cursor:'pointer',fontSize:18,marginRight:16}}>☰</button>
          {activeView!==VIEWS.HOME&&<div style={{fontSize:13,color:T.textDim}}>{new Date().toLocaleDateString('en-GB',{weekday:'long',day:'2-digit',month:'long',year:'numeric'})}</div>}
          {activeView!==VIEWS.HOME&&<div style={{marginLeft:'auto',fontSize:12,color:'#F5A623',fontWeight:600,fontFamily:T.mono}}>WEEK {Math.ceil(new Date().getDate()/7)} · {new Date().toLocaleString('default',{month:'long'}).toUpperCase()}</div>}
        </div>
        <div className="page-content" style={{padding:24,flex:1,position:'relative',zIndex:1}}>
          {loading?<div style={{textAlign:'center',color:T.textDim,marginTop:80,fontFamily:T.mono,letterSpacing:2}}>LOADING DATA...</div>:(<>
            {activeView===VIEWS.HOME&&<HomeView businesses={businesses} daysLeft={daysLeft} rawDaysLeft={rawDaysLeft} daysSinceSet={daysSinceSet} getUrgencyTier={getUrgencyTier} calculateStreak={calculateStreak} setActiveView={setActiveView} setActiveBusiness={setActiveBusiness} VIEWS={VIEWS} monthlyData={monthlyData} getKpiValue={getKpiValue} getMonthlyTarget={getMonthlyTarget} getMonthlyActual={getMonthlyActual} getMonthlyFocus={getMonthlyFocus} saveMonthlyFocus={saveMonthlyFocus} saveMonthlyData={saveMonthlyData} />}
            {activeView===VIEWS.BUSINESS&&activeBusiness&&<BusinessView biz={getBiz(activeBusiness)} monthlyData={monthlyData} getKpiValue={getKpiValue} kpiOverrides={activeBusiness==='byforce'?getByforceKpis():null} liveClients={activeBusiness==='byforce'?byforceClients:null} liveInvoices={activeBusiness==='byforce'?byforceInvoices:null} saveMonthlyData={saveMonthlyData} saveMonthlyFocus={saveMonthlyFocus}/>}
            {activeView===VIEWS.CLIENTS&&activeBusiness==='byforce'&&<ClientsView biz={getBiz('byforce')} monthlyData={monthlyData} saveMonthlyData={saveMonthlyData} showNotif={showNotif} setByforceClients={setByforceClients} byforceInvoices={byforceInvoices}/>}
            {activeView===VIEWS.CRM&&activeBusiness==='byforce'&&<CRMView biz={getBiz('byforce')} leads={byforceLeads} showNotif={showNotif} onConvertToClient={convertLeadToClient}/>}
            {activeView===VIEWS.CRM_REPORTS&&activeBusiness==='byforce'&&<CRMReportsView biz={getBiz('byforce')} leads={byforceLeads}/>}
            {activeView===VIEWS.ANALYSIS&&<AnalysisView businesses={businesses} monthlyData={monthlyData} getKpiValue={getKpiValue}/>}
            {activeView===VIEWS.DATA&&<DataEntryView businesses={businesses} monthlyData={monthlyData} saveMonthlyData={saveMonthlyData}/>}
            {activeView===VIEWS.MANAGE&&<ManageView businesses={businesses} saveBusiness={saveBusiness} deleteBusiness={deleteBusiness} changeLog={changeLog}/>}
            {activeView===VIEWS.SIMULATION&&<SimulationView businesses={businesses} monthlyData={monthlyData} getKpiValue={getKpiValue}/>}
            {activeView===VIEWS.PIPELINE&&<PipelineView businesses={businesses} monthlyData={monthlyData} saveBusiness={saveBusiness} saveMonthlyData={saveMonthlyData} showNotif={showNotif}/>}
            {activeView===VIEWS.YEAR&&<YearView businesses={businesses} monthlyData={monthlyData}/>}
            {activeView===VIEWS.CONSISTENCY&&<ConsistencyView businesses={businesses} dailyCommitments={dailyCommitments} toggleCommitmentItem={toggleCommitmentItem} calculateStreak={calculateStreak} getPeriodTrends={getPeriodTrends} getMonthlyTarget={getMonthlyTarget} getMonthlyActual={getMonthlyActual} getYearTotal={getYearTotal}/>}
            {activeView===VIEWS.SETTINGS&&<SettingsView user={user} logoUrl={logoUrl} saveLogoUrl={saveLogoUrl} changeLog={changeLog} businesses={businesses} bottomTabs={bottomTabs} saveBottomTabs={saveBottomTabs}/>}
            {activeView===VIEWS.TEAM_STRUCTURE&&<TeamStructureView showNotif={showNotif}/>}
          </>)}
        </div>
      </div>
      <MobileTabBar bottomTabs={bottomTabs} activeView={activeView} setActiveView={setActiveView} setActiveBusiness={setActiveBusiness}/>
    </div>
  );
}

// ─── BUSINESS ICONS ──────────────────────────────────────────
function getBizIcon(name, color, size=18) {
  const s = {stroke: color, strokeWidth:2, strokeLinecap:'round', strokeLinejoin:'round'};
  if (/byforce/i.test(name))   return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...s}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
  if (/clevene/i.test(name))   return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...s}><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>;
  if (/edemyx/i.test(name))    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...s}><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>;
  if (/personal|brand/i.test(name)) return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...s}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
  return <span>{name[0]}</span>;
}

// Official ClickUp logomark (gradient). `uid` keeps gradient ids unique when rendered multiple times on one page.
function ClickUpLogo({size=20, uid=''}) {
  const id1 = `cuGrad0_${uid}`, id2 = `cuGrad1_${uid}`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M6.43828 50.2095C5.81176 49.4456 5.94882 48.3212 6.71918 47.7027L14.0169 41.8433C14.6535 41.3322 15.5841 41.4476 16.1032 42.0777C21.06 48.0956 26.32 50.8815 32.142 50.8815C37.9238 50.8815 43.0403 48.135 47.7801 42.1839C48.2888 41.5452 49.2172 41.4131 49.8629 41.9129L57.2604 47.6377C58.0415 48.2422 58.1989 49.3637 57.5855 50.1379C50.5775 58.9839 41.9971 63.6446 32.142 63.6446C22.3156 63.6446 13.6586 59.013 6.43828 50.2095Z" fill={`url(#${id1})`}/>
      <path fillRule="evenodd" clipRule="evenodd" d="M32.4953 17.1804C32.2202 16.9368 31.8067 16.9367 31.5314 17.1801L15.663 31.2116C15.0564 31.748 14.1285 31.6857 13.599 31.0731L7.40986 23.9122C6.88774 23.3082 6.95019 22.3961 7.54979 21.8688L31.0527 1.20062C31.6021 0.717503 32.4248 0.717611 32.9741 1.20088L56.4822 21.8847C57.0822 22.4125 57.1439 23.3256 56.6205 23.9294L50.4164 31.0866C49.8862 31.6982 48.9589 31.7594 48.353 31.2229L32.4953 17.1804Z" fill={`url(#${id2})`}/>
      <defs>
        <linearGradient id={id1} x1="5.3335" y1="32.1624" x2="58.6653" y2="32.1624" gradientUnits="userSpaceOnUse">
          <stop offset="0.225962" stopColor="#6647F0"/>
          <stop offset="0.793269" stopColor="#0091FF"/>
        </linearGradient>
        <linearGradient id={id2} x1="5.3335" y1="31.6447" x2="58.6653" y2="31.6447" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FF02F0"/>
          <stop offset="0.778846" stopColor="#F76808"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

// ─── SIDEBAR ITEM ─────────────────────────────────────────────
function SideItem({icon,label,active,onClick,color,collapsed,onToggleExpand,expanded}) {
  const [hovered,setHovered] = useState(false);
  const bizStyle = color ? {
    background: active ? `${color}20` : hovered ? `${color}18` : `${color}10`,
    border: active ? `1px solid ${color}50` : hovered ? `1px solid ${color}40` : `1px solid ${color}25`,
    boxShadow: active ? `0 0 12px ${color}20, inset 0 0 12px ${color}08` : 'none',
    borderRadius: '10px',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    padding: collapsed?'8px':'8px 10px',
    marginBottom: '4px',
    transition: 'all 0.2s ease',
  } : {
    borderRadius: 8, padding: collapsed?'9px':'9px 10px', marginBottom: 2,
    background: active ? `${T.border}` : hovered ? 'rgba(255,255,255,0.05)' : 'transparent',
    borderLeft: active ? '2px solid rgba(255,255,255,0.85)' : hovered ? '2px solid rgba(255,255,255,0.3)' : '2px solid transparent',
    transition: 'all 0.15s',
  };
  return (
    <div onClick={onClick} onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)} title={collapsed?label:undefined} style={{display:'flex',alignItems:'center',justifyContent:collapsed?'center':'flex-start',gap:collapsed?0:10,cursor:'pointer',color:active?T.text:'#E8E9ED',...bizStyle}}>
      <span style={{display:'flex',alignItems:'center',minWidth:18}}>{icon}</span>
      {!collapsed&&<span style={{fontSize:13,fontWeight:active?600:400}}>{label}</span>}
      {!collapsed&&color&&<div style={{width:6,height:6,borderRadius:'50%',background:color,marginLeft:'auto',boxShadow:active?`0 0 6px ${color}`:'none'}}/>}
      {!collapsed&&onToggleExpand&&(
        <button onClick={(e)=>{e.stopPropagation();onToggleExpand();}} title={expanded?'Collapse':'Expand'} style={{background:'none',border:'none',color:T.textDim,cursor:'pointer',padding:2,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{transform:expanded?'rotate(90deg)':'none',transition:'transform 0.15s'}}><path d="M3 2l4 3-4 3"/></svg>
        </button>
      )}
    </div>
  );
}

// ─── HOME VIEW ───────────────────────────────────────────────
function HomeView({businesses,daysLeft,rawDaysLeft,daysSinceSet,getUrgencyTier,calculateStreak,setActiveView,setActiveBusiness,VIEWS,monthlyData,getKpiValue,getMonthlyTarget,getMonthlyActual,getMonthlyFocus,saveMonthlyFocus,saveMonthlyData}) {
  const [editingFocus,setEditingFocus] = useState(null);
  const [focusText,setFocusText] = useState('');
  const cm = MONTHS[new Date().getMonth()];
  const cy = new Date().getFullYear();
  const { data: igData, loading: igLoading } = useInstagram();
  const { data: liData, loading: liLoading } = useLinkedInZernio();

  const getRevenue = (biz) => {
    const rKpis=['MRR AED','Revenue AED'];
    for(const kpi of rKpis){
      if(biz.kpis.includes(kpi)){
        for(let m=new Date().getMonth()+1;m>=1;m--){const v=monthlyData[`${biz.id}_${cy}_${m}`]?.[kpi];if(v!==undefined&&v>0)return v;}
        return biz.current?.[kpi]||0;
      }
    }
    return 0;
  };

  const revenues = businesses.map(b=>({biz:b,revenue:getRevenue(b)}));
  const totalRevenue = revenues.reduce((s,r)=>s+r.revenue,0);
  const maxRevenue = Math.max(...revenues.map(r=>r.revenue),1);

  // Yearly trend: total income across all businesses, month by month, Jan → current month.
  const monthsElapsed = new Date().getMonth()+1;
  const trendData = MONTHS.slice(0,monthsElapsed).map((mon,idx)=>{
    const m = idx+1;
    let total = 0;
    businesses.forEach(b=>{
      const rKpis=['MRR AED','Revenue AED'];
      for(const kpi of rKpis){
        if(b.kpis.includes(kpi)){
          if(getKpiType(kpi)==='snapshot'){
            for(let mm=m;mm>=1;mm--){const v=monthlyData[`${b.id}_${cy}_${mm}`]?.[kpi];if(v!==undefined&&v>0){total+=v;break;}}
          } else {
            total += monthlyData[`${b.id}_${cy}_${m}`]?.[kpi]||0;
          }
          break;
        }
      }
    });
    return {month:mon,total};
  });
  const firstTrendVal = trendData.find(d=>d.total>0)?.total;
  const trendChangePct = firstTrendVal&&trendData.length>1?((trendData[trendData.length-1].total-firstTrendVal)/firstTrendVal)*100:null;

  const RevTrendTooltip = ({active,payload,label}) => {
    if(!active||!payload||!payload.length) return null;
    return (
      <div style={{background:'#0a0a0a',border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 12px',boxShadow:'0 4px 16px #00000080'}}>
        <div style={{fontSize:10,color:T.textDim,fontFamily:T.mono,letterSpacing:1,marginBottom:3}}>{String(label).toUpperCase()} {cy}</div>
        <div style={{fontSize:14,fontWeight:700,color:T.text,fontFamily:T.mono}}>AED {Number(payload[0].value).toLocaleString()}</div>
      </div>
    );
  };

  const startEdit=(id)=>{setEditingFocus(id);setFocusText(getMonthlyFocus(id));};
  const saveFocus=async(id)=>{await saveMonthlyFocus(id,focusText);setEditingFocus(null);};

  return (
    <div>
      <style>{`
        @keyframes urgencyPulse{0%{opacity:1}50%{opacity:0.6}100%{opacity:1}}
        .clickup-pill{transition:background 0.15s,border-color 0.15s}
        .clickup-pill:hover{background:rgba(123,104,238,0.18);border-color:rgba(123,104,238,0.5)}
        .clickup-pill:hover .clickup-tooltip{opacity:1}
      `}</style>
      {/* ── GREETING BAND ── */}
      <div style={{position:'relative',background:'radial-gradient(ellipse 620px 220px at 85% 0%,rgba(255,255,255,0.09) 0%,rgba(0,0,0,0) 60%),'+CARD_FADE,borderRadius:16,padding:'24px 28px',marginBottom:20,border:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:16,overflow:'hidden'}}>
        <div style={{fontSize:30,fontWeight:600,color:T.text,letterSpacing:-0.5}}>{getGreeting()}, Fahd</div>
        <div style={{...homeGlass('#ffffff'),padding:'12px 18px',minWidth:210}}>
          <div style={{display:'flex',alignItems:'center',gap:8,fontSize:15,fontWeight:700,color:'#ffffff',fontFamily:T.mono}}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="12" height="11" rx="1"/><path d="M2 7h12M5 1v3M11 1v3"/></svg>
            WEEK {Math.ceil(new Date().getDate()/7)}
          </div>
          <div style={{fontSize:12,fontWeight:700,color:T.text,fontFamily:T.mono,marginTop:4}}>{new Date().toLocaleDateString('en-GB',{weekday:'long',day:'2-digit',month:'long',year:'numeric'})}</div>
        </div>
      </div>
      {/* ── REVENUE OVERVIEW ── */}
      <div style={{position:'relative',background:CARD_FADE,borderRadius:14,padding:'28px 32px',marginBottom:28,border:`1px solid ${T.border}`,overflow:'hidden'}}>
        <div style={{position:'absolute',inset:0,borderRadius:14,backgroundImage:`linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`,backgroundSize:'40px 40px',opacity:0.6,pointerEvents:'none'}}/>
        <div className="rev-hero-head" style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24,position:'relative'}}>
          <div>
            <div style={{fontSize:14,color:T.text,letterSpacing:3,fontFamily:T.mono,marginBottom:6}}>REVENUE OVERVIEW</div>
            <div style={{fontSize:12,color:T.textDim,letterSpacing:2,fontFamily:T.mono}}>{cm.toUpperCase()} {cy} · ALL STREAMS CONSOLIDATED</div>
          </div>
          <div className="rev-hero-total" style={{textAlign:'right'}}>
            <div style={{fontSize:10,color:T.textDim,letterSpacing:3,fontFamily:T.mono,marginBottom:6}}>TOTAL ACTIVE REVENUE</div>
            <div className="rev-hero-amount" style={{fontSize:40,fontWeight:900,color:T.text,letterSpacing:-1,fontFamily:T.mono,lineHeight:1}}>AED {totalRevenue.toLocaleString()}</div>
            <div style={{fontSize:11,color:revenues.filter(r=>r.revenue>0).length>0?'#2ECC71':T.textDim,letterSpacing:2,fontFamily:T.mono,marginTop:6}}>
              {revenues.filter(r=>r.revenue>0).length} OF {businesses.length} STREAMS ACTIVE
            </div>
          </div>
        </div>
        <div className="rev-split" style={{display:'flex',gap:20,position:'relative',alignItems:'stretch'}}>
          <div className="rev-bars-col" style={{flex:'1 1 0',minWidth:320,display:'grid',gap:10}}>
            {revenues.sort((a,b)=>b.revenue-a.revenue).map(({biz,revenue})=>{
              const pct=totalRevenue>0?(revenue/totalRevenue)*100:0;
              const barW=totalRevenue>0?(revenue/maxRevenue)*100:0;
              const isLead=revenue===maxRevenue&&revenue>0;
              return (
                <div key={biz.id} className="rev-row" onClick={()=>{setActiveBusiness(biz.id);setActiveView(VIEWS.BUSINESS);}} style={{cursor:'pointer',background:revenue>0?`${ACCENT}0d`:'rgba(255,255,255,0.02)',borderRadius:8,padding:'14px 16px',border:`1px solid ${revenue>0?ACCENT+'25':T.border}`,display:'flex',alignItems:'center',gap:12}}>
                  <div className="rev-row-amount" style={{width:108,flexShrink:0}}>
                    <div style={{fontFamily:T.mono,fontSize:15,fontWeight:700,color:revenue>0?T.text:T.textFaint}}>{revenue>0?`AED ${revenue.toLocaleString()}`:'—'}</div>
                    <div style={{fontSize:9,color:T.textDim,fontFamily:T.mono,marginTop:2}}>AED/MO</div>
                  </div>
                  <div className="rev-row-bar" style={{flex:1,height:3,background:'rgba(255,255,255,0.08)',borderRadius:2,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${barW}%`,background:`linear-gradient(90deg,${ACCENT}70,${ACCENT})`,borderRadius:0,boxShadow:revenue>0?`0 0 8px ${ACCENT}50`:'none',transition:'width 0.8s ease'}}/>
                  </div>
                  <div className="rev-row-pct" style={{width:44,textAlign:'right',fontFamily:T.mono,fontSize:13,color:revenue>0?ACCENT:T.textFaint,fontWeight:700,flexShrink:0}}>{pct.toFixed(1)}%</div>
                  <div className="rev-row-name" style={{display:'flex',alignItems:'center',gap:8,width:168,flexShrink:0,justifyContent:'flex-end'}}>
                    {isLead&&<span style={{fontSize:9,color:ACCENT,letterSpacing:2,fontFamily:T.mono,background:`${ACCENT}15`,padding:'1px 6px',borderRadius:3}}>LEAD</span>}
                    <span style={{fontSize:13,color:revenue>0?T.textMid:T.textDim,fontFamily:T.mono,letterSpacing:0.5,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{biz.name}</span>
                    <div style={{width:7,height:7,borderRadius:'50%',background:revenue>0?ACCENT:T.borderMid,boxShadow:revenue>0?`0 0 8px ${ACCENT}`:'none',flexShrink:0}}/>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rev-trend-card" style={{flex:'1 1 0',minWidth:320,background:'rgba(255,255,255,0.03)',backdropFilter:'blur(12px)',WebkitBackdropFilter:'blur(12px)',border:`1px solid ${T.border}`,borderRadius:12,padding:'18px 18px 14px',display:'flex',flexDirection:'column'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div style={{fontSize:10,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginBottom:4}}>YEARLY TREND</div>
                <div style={{fontSize:11,color:T.textMid,fontFamily:T.mono}}>{cy} · TOTAL INCOME</div>
              </div>
              {trendChangePct!=null&&(
                <div style={{fontSize:12,fontWeight:700,fontFamily:T.mono,color:trendChangePct>=0?'#2ECC71':'#E74C3C',whiteSpace:'nowrap'}}>
                  {trendChangePct>=0?'▲':'▼'} {Math.abs(trendChangePct).toFixed(1)}%
                </div>
              )}
            </div>
            <div style={{flex:1,minHeight:120,margin:'6px -8px -2px'}}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{top:8,right:8,left:8,bottom:0}}>
                  <defs>
                    <linearGradient id="revTrendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3498DB" stopOpacity={0.42}/>
                      <stop offset="100%" stopColor="#3498DB" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" hide/>
                  <YAxis hide domain={[0,'auto']}/>
                  <Tooltip content={<RevTrendTooltip/>} cursor={{stroke:T.borderMid,strokeWidth:1}}/>
                  <Area type="monotone" dataKey="total" stroke="#3498DB" strokeWidth={2.5} fill="url(#revTrendFill)" dot={false} activeDot={{r:4,fill:'#3498DB',stroke:'#000000',strokeWidth:2}} isAnimationActive={false}/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:T.textDim,fontFamily:T.mono,letterSpacing:1}}>
              <span>{trendData[0]?.month?.toUpperCase()}</span>
              <span>{trendData[trendData.length-1]?.month?.toUpperCase()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── BUSINESS CARDS ── */}
      <div style={{fontSize:10,color:T.textDim,letterSpacing:3,fontFamily:T.mono,marginBottom:14}}>BUSINESS UNITS — PERFORMANCE OVERVIEW</div>
      <div className="cards-grid" style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))',gap:16,marginBottom:28}}>
        {businesses.map(b=>{
          const dl=daysLeft(b.deadline);
          const tier=getUrgencyTier(rawDaysLeft(b.deadline));
          const sinceSet=daysSinceSet(b.deadlineSetAt);
          const pk=b.kpis[0];
          const cur=getKpiValue(b,pk);
          const ltTarget=b.target?.[pk]||1;
          const ltPct=Math.min((cur/ltTarget)*100,100);
          const mTarget=getMonthlyTarget(b.id,pk);
          const mActual=getMonthlyActual(b.id,pk);
          const mPct=mTarget>0?Math.min((mActual/mTarget)*100,100):0;
          const focus=getMonthlyFocus(b.id);
          const hasCommitments=(b.commitmentItems||[]).length>0;
          const streak=hasCommitments?calculateStreak(b.id):null;
          return (
            <div key={b.id} style={{background:`radial-gradient(140% 100% at 0% 0%,${b.color}22 0%,${b.color}0a 35%,rgba(0,0,0,0.78) 65%),#000000`,backdropFilter:'blur(18px)',WebkitBackdropFilter:'blur(18px)',border:`1px solid ${b.color}28`,borderRadius:14,padding:20,boxShadow:`0 8px 28px ${b.color}12, inset 0 1px 0 rgba(255,255,255,0.04)`,position:'relative',overflow:'hidden'}}>
              <div className="biz-card-head" style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:16}}>
                <div style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',minWidth:0}} onClick={()=>{setActiveBusiness(b.id);setActiveView(VIEWS.BUSINESS);}}>
                  <span style={{display:'flex',alignItems:'center',flexShrink:0}}>{getBizIcon(b.name,b.color,22)}</span>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:15,color:b.color,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{b.name}</div>
                    <div style={{fontSize:11,color:'rgba(255,255,255,0.45)',marginTop:2,whiteSpace:'nowrap'}}>{b.momentum} · {b.priority} priority</div>
                  </div>
                </div>
                {b.clickupUrl&&(
                  <a href={b.clickupUrl} target="_blank" rel="noopener noreferrer" className="clickup-pill" onClick={e=>e.stopPropagation()} style={{position:'relative',display:'inline-flex',alignItems:'center',gap:6,flexShrink:0,background:'rgba(123,104,238,0.10)',border:'1px solid rgba(123,104,238,0.30)',borderRadius:20,padding:'6px 10px 6px 8px',minHeight:32}}>
                    <ClickUpLogo size={14} uid={b.id}/>
                    <span style={{fontSize:10,fontWeight:600,color:'#a599f5',fontFamily:T.mono,letterSpacing:0.5}}>ClickUp</span>
                    <span className="clickup-tooltip" style={{position:'absolute',bottom:'calc(100% + 8px)',right:0,background:'#0a0a0a',border:`1px solid ${T.border}`,borderRadius:6,padding:'6px 10px',fontSize:11,color:T.text,whiteSpace:'nowrap',opacity:0,pointerEvents:'none',transition:'opacity 0.15s',boxShadow:'0 4px 16px #00000080',zIndex:10}}>View project on ClickUp</span>
                  </a>
                )}
              </div>

              <div style={{marginBottom:14,cursor:'pointer'}} onClick={()=>{setActiveBusiness(b.id);setActiveView(VIEWS.BUSINESS);}}>
                <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',letterSpacing:2,fontFamily:T.mono,marginBottom:6}}>NORTH STAR GOAL</div>
                <div style={{fontSize:15,fontWeight:700,color:'#ffffff',marginBottom:8}}>{b.goal}</div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'rgba(255,255,255,0.45)',marginBottom:6}}>
                  <span>{pk}: <strong style={{color:'#ffffff'}}>{Number(cur).toLocaleString()}</strong></span>
                  <span style={{color:'#ffffff',fontWeight:700}}>{ltPct.toFixed(1)}%</span>
                </div>
                <ProgressBar pct={ltPct} color={BAR_FILL}/>
              </div>

              <div style={{background:T.bg,borderRadius:8,padding:12,marginBottom:14,border:`1px solid ${T.border}`}}>
                <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',letterSpacing:2,fontFamily:T.mono,marginBottom:8}}>{cm.toUpperCase()} TARGET</div>
                {mTarget>0?(<>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:8}}>
                    <span style={{color:'rgba(255,255,255,0.45)'}}>Actual: <strong style={{color:'#ffffff'}}>{Number(mActual).toLocaleString()}</strong></span>
                    <span style={{color:'rgba(255,255,255,0.45)'}}>Target: <strong style={{color:'#ffffff'}}>{Number(mTarget).toLocaleString()}</strong></span>
                  </div>
                  <ProgressBar pct={mPct} color={BAR_FILL} height={8}/>
                  <div style={{fontSize:11,color:'#ffffff',marginTop:6,fontWeight:600}}>{mPct.toFixed(1)}% of target</div>
                </>):(<>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:8}}>
                    <span style={{color:'rgba(255,255,255,0.45)'}}>Actual: <strong style={{color:'rgba(255,255,255,0.45)'}}>—</strong></span>
                    <span style={{color:'rgba(255,255,255,0.45)'}}>Target: <strong style={{color:'rgba(255,255,255,0.45)'}}>—</strong></span>
                  </div>
                  <ProgressBar pct={0} color={T.borderMid} height={8}/>
                  <div style={{fontSize:11,color:'rgba(255,255,255,0.45)',marginTop:6}}>No target set — add in Enter Monthly Data</div>
                </>)}
              </div>

              <div className="biz-card-stats" style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:8,marginBottom:10}}>
                {sinceSet!=null&&(
                  <div title={`Deadline set on ${b.deadlineSetAt}`} style={{background:'rgba(255,255,255,0.04)',border:`1px solid ${T.border}`,borderRadius:10,padding:'10px 12px',textAlign:'center',minWidth:60,backdropFilter:'blur(10px)',WebkitBackdropFilter:'blur(10px)'}}>
                    <div className="stat-number" style={{fontSize:24,fontWeight:900,color:'#ffffff',lineHeight:1,fontFamily:T.mono}}>{sinceSet}</div>
                    <div style={{fontSize:9,color:'rgba(255,255,255,0.45)',letterSpacing:1,marginTop:3,fontFamily:T.mono,whiteSpace:'nowrap'}}>DAYS AGO</div>
                  </div>
                )}
                <div style={{background:'rgba(255,255,255,0.04)',border:`1px solid ${T.border}`,borderRadius:10,padding:'10px 14px',textAlign:'center',minWidth:72}}>
                  <div className="stat-number" style={{fontSize:24,fontWeight:900,color:'#ffffff',lineHeight:1,fontFamily:T.mono}}>{dl}</div>
                  <div style={{fontSize:9,color:'rgba(255,255,255,0.45)',letterSpacing:2,marginTop:3,fontFamily:T.mono}}>DAYS</div>
                </div>
              </div>

              {(tier.message||streak)&&(
                <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:14}}>
                  {tier.message&&(
                    <div style={{display:'flex',alignItems:'center',gap:6,fontSize:11,color:'rgba(255,255,255,0.7)',fontWeight:600,background:'rgba(255,255,255,0.04)',border:`1px solid ${T.border}`,borderRadius:8,padding:'6px 10px'}}>
                      {tier.pulse&&<span style={{width:6,height:6,borderRadius:'50%',background:'#E74C3C',flexShrink:0,animation:'urgencyPulse 1.4s ease-in-out infinite'}}/>}
                      {tier.message}
                    </div>
                  )}
                  {streak&&(
                    <div style={{background:streak.currentStreak>0?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.04)',border:`1px solid ${T.border}`,borderRadius:8,padding:'6px 10px',fontSize:11,fontWeight:600,color:streak.currentStreak>0?'#ffffff':'rgba(255,255,255,0.45)',fontFamily:T.mono}}>
                      {streak.currentStreak>0?`🔥 ${streak.currentStreak} day streak`:'◌ streak broken'}
                    </div>
                  )}
                </div>
              )}

              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',letterSpacing:2,fontFamily:T.mono,marginBottom:8}}>SOCIALS</div>
                <div style={{position:'relative',paddingLeft:24}}>
                  <SocialsConnector/>
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    <SocialSubcard icon={<InstagramLogo size={16}/>} label="Instagram" connected={b.id==='personal-brand'} loading={b.id==='personal-brand'&&igLoading} color={b.color}>
                      {igData && (
                        <div style={{display:'flex',flexDirection:'column',gap:10}}>
                          <div style={{display:'flex',justifyContent:'space-between'}}>
                            <div style={{textAlign:'center'}}><div style={{fontSize:17,fontWeight:700,color:'#ffffff',fontFamily:T.mono}}>{igData.followers?.toLocaleString()}</div><div style={{fontSize:10,color:'rgba(255,255,255,0.45)',marginTop:2}}>Followers</div></div>
                            <div style={{textAlign:'center'}}><div style={{fontSize:17,fontWeight:700,color:'#ffffff',fontFamily:T.mono}}>{igData.postCount}</div><div style={{fontSize:10,color:'rgba(255,255,255,0.45)',marginTop:2}}>Posts</div></div>
                            <div style={{textAlign:'center'}}><div style={{fontSize:17,fontWeight:700,color:'#ffffff',fontFamily:T.mono}}>{igData.engagementRate}%</div><div style={{fontSize:10,color:'rgba(255,255,255,0.45)',marginTop:2}}>Engmt</div></div>
                          </div>
                          <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',textAlign:'center'}}>Updated {new Date(igData.lastFetched).toLocaleTimeString()}</div>
                        </div>
                      )}
                    </SocialSubcard>
                    <SocialSubcard icon={<LinkedInLogo size={16}/>} label="LinkedIn" connected={b.id==='personal-brand'} loading={b.id==='personal-brand'&&liLoading} color={b.color}>
                      {liData && (
                        <div style={{display:'flex',justifyContent:'space-around'}}>
                          <div style={{textAlign:'center'}}>
                            <div style={{fontSize:17,fontWeight:700,color:'#ffffff',fontFamily:T.mono}}>{liData.currentFollowers!=null?liData.currentFollowers.toLocaleString():'—'}</div>
                            <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',marginTop:2}}>Followers</div>
                          </div>
                          {liData.followerDelta7d!=null && (
                            <div style={{textAlign:'center'}}>
                              <div style={{fontSize:15,fontWeight:700,fontFamily:T.mono,color:'#ffffff'}}>
                                {liData.followerDelta7d>0?'▲':liData.followerDelta7d<0?'▼':'■'} {Math.abs(liData.followerDelta7d)}
                              </div>
                              <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',marginTop:2}}>7d trend</div>
                            </div>
                          )}
                        </div>
                      )}
                    </SocialSubcard>
                  </div>
                </div>
              </div>

              <div style={{borderTop:`1px solid ${T.border}`,paddingTop:12}}>
                <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',letterSpacing:2,fontFamily:T.mono,marginBottom:6}}>{cm.toUpperCase()} FOCUS</div>
                {editingFocus===b.id?(
                  <div style={{display:'flex',gap:6}}>
                    <input autoFocus value={focusText} onChange={e=>setFocusText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&saveFocus(b.id)} placeholder="What's your focus this month?" style={{flex:1,background:T.bg,border:`1px solid ${b.color}40`,borderRadius:6,padding:'7px 10px',color:'#ffffff',fontSize:13,outline:'none'}}/>
                    <button onClick={()=>saveFocus(b.id)} style={{background:b.color,border:'none',borderRadius:6,padding:'4px 12px',color:'#000',fontWeight:700,cursor:'pointer'}}>✓</button>
                    <button onClick={()=>setEditingFocus(null)} style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:'4px 8px',color:'rgba(255,255,255,0.45)',cursor:'pointer'}}>✕</button>
                  </div>
                ):(
                  <div onClick={()=>startEdit(b.id)} style={{fontSize:13,color:focus?'#ffffff':'rgba(255,255,255,0.45)',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',minHeight:44,padding:'10px 0'}}>
                    <span>{focus||"Click to set this month's focus..."}</span>
                    <span style={{fontSize:10,color:'rgba(255,255,255,0.45)'}}>EDIT</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── FOCUS BOARD ── */}
      <div style={{background:T.card,borderRadius:12,padding:20,border:`1px solid ${T.border}`}}>
        <div style={{fontSize:10,color:T.textDim,letterSpacing:3,fontFamily:T.mono,marginBottom:16}}>THIS MONTH'S FOCUS — {cm.toUpperCase()} {cy}</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:12}}>
          {businesses.map(b=>{
            const focus=getMonthlyFocus(b.id);
            return (
              <div key={b.id} style={{background:T.bg,borderRadius:8,padding:14,borderLeft:`2px solid ${b.color}50`}}>
                <div style={{fontSize:12,fontWeight:600,color:b.color,marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
                  <div style={{width:5,height:5,borderRadius:'50%',background:b.color,boxShadow:`0 0 4px ${b.color}`}}/>{b.name}
                </div>
                {editingFocus===b.id?(
                  <div style={{display:'flex',gap:6}}>
                    <input autoFocus value={focusText} onChange={e=>setFocusText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&saveFocus(b.id)} style={{flex:1,background:T.card,border:`1px solid ${b.color}40`,borderRadius:4,padding:'5px 8px',color:T.text,fontSize:12,outline:'none'}}/>
                    <button onClick={()=>saveFocus(b.id)} style={{background:b.color,border:'none',borderRadius:4,padding:'2px 8px',color:'#000',fontWeight:700,cursor:'pointer',fontSize:12}}>✓</button>
                  </div>
                ):(
                  <div onClick={()=>startEdit(b.id)} style={{fontSize:13,color:focus?T.textMid:T.textFaint,cursor:'pointer',minHeight:44,display:'flex',alignItems:'center'}}>{focus||'Click to set focus...'}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── BUSINESS VIEW ───────────────────────────────────────────
function BusinessView({biz,monthlyData,getKpiValue,kpiOverrides,liveClients,liveInvoices,saveMonthlyData,saveMonthlyFocus}) {
  const { data: igData } = useInstagram();
  const [activeKpi,setActiveKpi] = useState('');
  const [viewYear,setViewYear] = useState(new Date().getFullYear());
  const [selectedMonth,setSelectedMonth] = useState(null);
  const [activeByMonth,setActiveByMonth] = useState({1:0,2:0,3:0,4:0,5:0,6:0,7:0,8:0,9:0,10:0,11:0,12:0});
  const [mrrByMonth,setMrrByMonth] = useState({1:0,2:0,3:0,4:0,5:0,6:0,7:0,8:0,9:0,10:0,11:0,12:0});
  const [closedByMonth,setClosedByMonth] = useState({1:0,2:0,3:0,4:0,5:0,6:0,7:0,8:0,9:0,10:0,11:0,12:0});
  const [convRateByMonth,setConvRateByMonth] = useState({1:0,2:0,3:0,4:0,5:0,6:0,7:0,8:0,9:0,10:0,11:0,12:0});

  useEffect(()=>{if(biz)setActiveKpi(sortKpis(biz.kpis)[0]);},[biz]);

  useEffect(()=>{
    if(!liveClients) return;
    const abd={}, mbd={}, cbd={};
    for(let m=1;m<=12;m++){abd[m]=0; mbd[m]=0; cbd[m]=0;}
    liveClients.forEach(c=>{
      const {year:sy,month:sm}=parseClientStart(c);
      if(!sy||!sm) return;
      if(sy===viewYear&&sm>=1&&sm<=12) cbd[sm]++;
      if(c.status==='Active'){
        const {year:ey,month:em}=parseClientEnd(c);
        for(let m=1;m<=12;m++){
          const withinStart=sy<viewYear||(sy===viewYear&&sm<=m);
          const withinEnd=!ey||ey>viewYear||(ey===viewYear&&em>=m);
          if(withinStart&&withinEnd) abd[m]++;
        }
      }
    });
    // MRR per month = sum of paid invoices for that month (invoice-based)
    if(liveInvoices){
      for(let m=1;m<=12;m++){
        mbd[m]=liveInvoices.filter(inv=>{
          if(inv.status!=='Paid') return false;
          const p=parseInvoiceMonth(inv); return p&&p.year===viewYear&&p.month===m;
        }).reduce((s,inv)=>s+(inv.amount||0),0);
      }
    }
    setActiveByMonth({...abd});
    setMrrByMonth({...mbd});
    setClosedByMonth({...cbd});
    const crb={};
    for(let m=1;m<=12;m++){
      const nl=monthlyData[`byforce_${viewYear}_${m}`]?.['New Leads']||0;
      crb[m]=nl>0?parseFloat(((cbd[m]/nl)*100).toFixed(1)):0;
    }
    setConvRateByMonth({...crb});
  },[liveClients,liveInvoices,monthlyData,viewYear]); // eslint-disable-line react-hooks/exhaustive-deps

  if(!biz) return null;

  const cy=new Date().getFullYear();
  const cm=new Date().getMonth()+1;
  // For byforce in the current year: future months = null (no bar, no projection)
  const data=MONTHS.map((_,i)=>{
    const m=i+1;
    if(liveClients!==null&&viewYear===cy&&m>cm) return null;
    return monthlyData[`${biz.id}_${viewYear}_${m}`]?.[activeKpi]||0;
  });
  const targets=MONTHS.map((_,i)=>monthlyData[`${biz.id}_${viewYear}_${i+1}`]?._targets?.[activeKpi]||0);
  const max=Math.max(...data.map(v=>v??0),1);
  const filled=data.filter(v=>v!=null&&v>0);
  const avg=filled.length>0?(filled.reduce((a,b)=>a+b,0)/filled.length).toFixed(1):0;
  const best=Math.max(...data.map(v=>v??0));
  const bestMonth=MONTHS[data.map(v=>v??0).indexOf(best)];

  const isPersonalBrand = biz.id==='personal-brand';
  const igPosts = isPersonalBrand ? (igData?.recentPosts||[]) : [];
  const totalViews = igPosts.reduce((sum,p)=>sum+(p.views||0),0);
  const topContent = [...igPosts].sort((a,b)=>{
    if(a.views==null&&b.views==null) return 0;
    if(a.views==null) return 1;
    if(b.views==null) return -1;
    return b.views-a.views;
  });

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:24}}>
        <span style={{fontSize:32}}>{biz.emoji}</span>
        <div style={{flex:1}}>
          <h1 style={{fontSize:24,fontWeight:700,color:biz.color,marginBottom:4}}>{biz.name}</h1>
          <p style={{color:T.textDim,fontSize:13}}>{biz.goal} · {biz.momentum} · {biz.priority} priority</p>
        </div>
        <select value={viewYear} onChange={e=>setViewYear(parseInt(e.target.value))} style={{width:'auto',flexShrink:0,background:'rgba(255,255,255,0.04)',border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 14px',color:T.textMid,fontSize:13}}>
          {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* KPI CARDS */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,marginBottom:28}}>
        {sortKpis(biz.kpis).map(kpi=>{
          let val, isLive;
          if(kpiOverrides&&kpiOverrides[kpi]!==undefined){
            val=kpiOverrides[kpi]; isLive=true;
          } else {
            val=getKpiValue(biz,kpi,viewYear); isLive=false;
          }
          const isActive=activeKpi===kpi;
          return (
            <div key={kpi} onClick={()=>setActiveKpi(kpi)} style={{background:isActive?`${biz.color}10`:T.card,border:`1px solid ${isActive?biz.color+'50':T.border}`,borderRadius:10,padding:16,cursor:'pointer',transition:'all 0.2s',boxShadow:isActive?`0 0 16px ${biz.color}15`:'none'}}>
              <div style={{fontSize:11,color:T.textDim,marginBottom:6,display:'flex',alignItems:'center',gap:4}}>
                {kpi}{isLive&&<span style={{fontSize:8,color:biz.color,letterSpacing:1,fontFamily:T.mono}}>LIVE</span>}
              </div>
              <div className="stat-number" style={{fontSize:22,fontWeight:700,color:isActive?biz.color:T.text,fontFamily:T.mono}}>
                {Number(val).toLocaleString()}{kpi==='Conversion Rate'?'%':''}
              </div>
              <div style={{fontSize:10,color:T.textFaint,marginTop:4}}>
                {isLive?'⬤ Auto-calculated':getKpiType(kpi)==='snapshot'?'◈ Latest':'◆ Cumulative'}
              </div>
            </div>
          );
        })}
        {isPersonalBrand&&igData&&(
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:16}}>
            <div style={{fontSize:11,color:T.textDim,marginBottom:6,display:'flex',alignItems:'center',gap:4}}>
              Views<span style={{fontSize:8,color:biz.color,letterSpacing:1,fontFamily:T.mono}}>LIVE</span>
            </div>
            <div className="stat-number" style={{fontSize:22,fontWeight:700,color:T.text,fontFamily:T.mono}}>{totalViews.toLocaleString()}</div>
            <div style={{fontSize:10,color:T.textFaint,marginTop:4}}>⬤ Last {igPosts.length} posts</div>
          </div>
        )}
      </div>

      {/* CHART */}
      <div style={{background:`radial-gradient(140% 100% at 0% 0%,${biz.color}14 0%,${biz.color}06 35%,rgba(8,13,22,0.7) 65%)`,backdropFilter:'blur(16px)',WebkitBackdropFilter:'blur(16px)',borderRadius:14,padding:24,marginBottom:20,border:`1px solid ${biz.color}22`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16,flexWrap:'wrap',gap:12}}>
          <div>
            <div style={{fontSize:15,fontWeight:600,color:biz.color,marginBottom:4}}>{activeKpi} — {viewYear}</div>
            <div style={{fontSize:11,color:T.textDim}}>{selectedMonth!==null?`Selected: ${MONTHS[selectedMonth]}`:'Click a month to see details'}</div>
          </div>
          <div style={{display:'flex',gap:20,fontSize:12,color:T.textDim,alignItems:'center'}}>
            <span>Avg <strong style={{color:T.textMid}}>{avg}</strong></span>
            <span>Best <strong style={{color:'#2ECC71'}}>{best} ({bestMonth})</strong></span>
            {selectedMonth!==null&&<button onClick={()=>setSelectedMonth(null)} style={{background:'rgba(255,255,255,0.04)',border:`1px solid ${T.border}`,color:T.textDim,borderRadius:6,padding:'3px 10px',cursor:'pointer',fontSize:11}}>Clear</button>}
          </div>
        </div>
        <div style={{display:'flex',gap:4,marginBottom:16,flexWrap:'wrap'}}>
          {MONTHS.map((m,i)=>(
            <button key={m} onClick={()=>setSelectedMonth(selectedMonth===i?null:i)} style={{background:selectedMonth===i?`${biz.color}20`:'rgba(255,255,255,0.03)',border:`1px solid ${selectedMonth===i?biz.color+'60':T.border}`,color:selectedMonth===i?biz.color:T.textDim,borderRadius:5,padding:'4px 10px',cursor:'pointer',fontSize:11,boxShadow:selectedMonth===i?`0 0 8px ${biz.color}20`:'none',transition:'all 0.15s'}}>{m}</button>
          ))}
        </div>
        <div className="chart-row" style={{display:'flex',gap:0,alignItems:'stretch'}}>
          <div className="chart-area" style={{flex:1,minWidth:0,height:210}}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={MONTHS.map((m,i)=>({month:m,value:data[i],target:targets[i]||0}))} margin={{top:10,right:10,left:0,bottom:0}}>
                <defs>
                  <linearGradient id={`bizTrend-${biz.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={biz.color} stopOpacity={0.35}/>
                    <stop offset="100%" stopColor={biz.color} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 6" stroke={T.border} vertical={false}/>
                <XAxis dataKey="month" tick={{fill:T.textFaint,fontSize:10,fontFamily:T.mono}} axisLine={{stroke:T.border}} tickLine={false}/>
                <YAxis tick={{fill:T.textFaint,fontSize:9,fontFamily:T.mono}} axisLine={false} tickLine={false} width={40} tickFormatter={v=>v>999?(v/1000).toFixed(0)+'k':v}/>
                <Tooltip cursor={{stroke:T.borderMid,strokeWidth:1}} content={({active,payload,label})=>{
                  if(!active||!payload||!payload.length) return null;
                  const p=payload[0].payload;
                  if(p.value==null) return null;
                  const hit=p.target>0&&p.value>=p.target;
                  return (
                    <div style={{background:'#0a0a0a',border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 12px',boxShadow:'0 4px 16px #00000080'}}>
                      <div style={{fontSize:10,color:T.textDim,fontFamily:T.mono,letterSpacing:1,marginBottom:3}}>{String(label).toUpperCase()} {viewYear}</div>
                      <div style={{fontSize:14,fontWeight:700,color:biz.color,fontFamily:T.mono}}>{Number(p.value).toLocaleString()}</div>
                      {p.target>0&&<div style={{fontSize:11,color:hit?'#2ECC71':'#E74C3C',marginTop:2}}>{hit?'✓':'✗'} Target {p.target.toLocaleString()}</div>}
                    </div>
                  );
                }}/>
                <Area type="monotone" dataKey="value" stroke={biz.color} strokeWidth={2.5} fill={`url(#bizTrend-${biz.id})`} connectNulls={false} isAnimationActive={false}
                  dot={(props)=>{
                    const {cx,cy,payload,index}=props;
                    if(payload.value==null) return null;
                    const hit=payload.target>0&&payload.value>=payload.target;
                    return <circle key={`d${index}`} cx={cx} cy={cy} r={selectedMonth===index?5:3} fill={hit?'#2ECC71':biz.color} stroke="#000000" strokeWidth={1.5} style={{cursor:'pointer'}} onClick={()=>setSelectedMonth(selectedMonth===index?null:index)}/>;
                  }}
                  activeDot={{r:5,fill:biz.color,stroke:'#000000',strokeWidth:2}}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-detail" style={{minWidth:130,borderLeft:`1px solid ${T.border}`,paddingLeft:16,display:'flex',flexDirection:'column',justifyContent:'center',gap:10,flexShrink:0}}>
            <div style={{fontSize:11,color:biz.color,fontWeight:600,marginBottom:4,width:'100%'}}>{activeKpi}</div>
            {selectedMonth!==null?(<>
              <div><div style={{fontSize:9,color:T.textDim,fontFamily:T.mono,letterSpacing:1,marginBottom:2}}>{MONTHS[selectedMonth].toUpperCase()}</div></div>
              <div><div style={{fontSize:9,color:T.textDim,fontFamily:T.mono,letterSpacing:1}}>ACTUAL</div><div style={{fontSize:17,fontWeight:700,color:biz.color,fontFamily:T.mono}}>{data[selectedMonth]!=null?data[selectedMonth].toLocaleString():'—'}</div></div>
              {targets[selectedMonth]>0&&<div><div style={{fontSize:9,color:T.textDim,fontFamily:T.mono,letterSpacing:1}}>TARGET</div><div style={{fontSize:14,fontWeight:700,color:'#F5A623',fontFamily:T.mono}}>{targets[selectedMonth].toLocaleString()}</div></div>}
              {targets[selectedMonth]>0&&data[selectedMonth]!=null&&<div><div style={{fontSize:9,color:T.textDim,fontFamily:T.mono,letterSpacing:1}}>ACHIEVEMENT</div><div style={{fontSize:14,fontWeight:700,color:data[selectedMonth]>=targets[selectedMonth]?'#2ECC71':'#E74C3C',fontFamily:T.mono}}>{((data[selectedMonth]/targets[selectedMonth])*100).toFixed(1)}%</div></div>}
              {selectedMonth>0&&data[selectedMonth-1]!=null&&data[selectedMonth-1]>0&&data[selectedMonth]!=null&&<div><div style={{fontSize:9,color:T.textDim,fontFamily:T.mono,letterSpacing:1}}>VS PREV</div><div style={{fontSize:14,fontWeight:700,color:data[selectedMonth]>=data[selectedMonth-1]?'#2ECC71':'#E74C3C',fontFamily:T.mono}}>{data[selectedMonth]>=data[selectedMonth-1]?'+':''}{(data[selectedMonth]-data[selectedMonth-1]).toLocaleString()}</div></div>}
            </>):(
              <div style={{fontSize:11,color:T.textFaint,lineHeight:1.6}}>Click any bar to see details</div>
            )}
          </div>
        </div>
      </div>

      {/* MONTHLY TABLE */}
      <div style={{fontSize:11,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginBottom:12}}>FULL {viewYear} MONTHLY TABLE</div>
      <div style={{overflowX:'auto',background:T.card,borderRadius:12,border:`1px solid ${T.border}`}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead>
            <tr style={{background:T.bg}}>
              <td style={{padding:'12px 16px',color:T.textDim,fontWeight:600,borderBottom:`1px solid ${T.border}`,fontSize:11}}>KPI</td>
              <td style={{padding:'12px 8px',color:T.textDim,fontSize:10,borderBottom:`1px solid ${T.border}`}}>TYPE</td>
              {MONTHS.map((m,i)=>(
                <td key={m} onClick={()=>setSelectedMonth(selectedMonth===i?null:i)} style={{padding:'12px 8px',color:selectedMonth===i?biz.color:T.textDim,fontWeight:selectedMonth===i?700:500,textAlign:'center',borderBottom:`1px solid ${selectedMonth===i?biz.color+'30':T.border}`,fontSize:11,cursor:'pointer',background:selectedMonth===i?`${biz.color}06`:'transparent',transition:'all 0.15s'}}>{m}</td>
              ))}
              <td style={{padding:'12px 8px',color:T.textDim,fontWeight:600,textAlign:'center',borderBottom:`1px solid ${T.border}`,fontSize:11}}>Total</td>
            </tr>
          </thead>
          <tbody>
            {sortKpis(biz.kpis).map(kpi=>{
              const isLiveByforce = liveClients!==null;
              const isClosed = isLiveByforce && kpi==='Closed Deals';
              const isConv   = isLiveByforce && kpi==='Conversion Rate';
              const isAct    = isLiveByforce && kpi==='Active Clients';
              const isMrr    = isLiveByforce && kpi==='MRR AED';

              const row = MONTHS.map((_,i)=>{
                const m=i+1;
                // Future months: always null
                if(isLiveByforce&&(viewYear>cy||(viewYear===cy&&m>cm))) return null;
                // Active Clients + MRR: current month = live, past months = monthlyData (manual)
                if((isAct||isMrr)&&viewYear===cy&&m===cm) return isAct?activeByMonth[m]:mrrByMonth[m];
                if(isAct||isMrr) return monthlyData[`byforce_${viewYear}_${m}`]?.[kpi];
                // Closed Deals + Conversion Rate: all non-future months = computed from clients
                if(isClosed) return closedByMonth[m];
                if(isConv)   return convRateByMonth[m];
                // All other KPIs: always from monthlyData
                return monthlyData[`${biz.id}_${viewYear}_${m}`]?.[kpi];
              });

              const isSnapshot=getKpiType(kpi)==='snapshot';
              // Only consider defined (non-null) values for totals
              const defined=row.filter(v=>v!=null);
              const nonZeroDef=defined.filter(v=>v>0);
              const nonZero=row.filter(v=>v!=null&&v>0); // kept for original branch

              let rowTotal;
              if(isAct){
                rowTotal=defined.length>0?defined[defined.length-1]:0;
              } else if(isMrr||isClosed){
                rowTotal=defined.reduce((a,v)=>a+(v||0),0);
              } else if(isConv){
                rowTotal=nonZeroDef.length>0?parseFloat((nonZeroDef.reduce((a,v)=>a+v,0)/nonZeroDef.length).toFixed(1)):0;
              } else {
                rowTotal=(kpi==='MRR AED'||!isSnapshot)
                  ?defined.reduce((a,v)=>a+(v||0),0)
                  :(nonZero.length>=2?nonZero[nonZero.length-1]-nonZero[0]:nonZero[0]||0);
              }

              const isActive=activeKpi===kpi;
              const suffix=isConv?'%':'';
              const showTotal=isAct||isMrr||isClosed||isConv?defined.length>0:nonZero.length>0;
              return (
                <tr key={kpi} style={{borderBottom:`1px solid ${T.border}`,background:isActive?`${biz.color}05`:'transparent'}}>
                  <td style={{padding:'11px 16px',color:isActive?biz.color:T.textMid,fontWeight:isActive?600:400,whiteSpace:'nowrap',fontSize:13}}>{kpi}</td>
                  <td style={{padding:'11px 8px',fontSize:10,color:T.textFaint}}>{getKpiType(kpi)==='snapshot'?'◈':'◆'}</td>
                  {row.map((val,i)=>{
                    const display=val!=null?`${Number(val).toLocaleString()}${suffix}`:'—';
                    return (
                      <td key={i} style={{padding:'11px 8px',textAlign:'center',color:val!=null&&val>0?biz.color:T.textFaint,fontWeight:val!=null&&val>0?600:400,background:selectedMonth===i?`${biz.color}08`:'transparent',fontSize:13,transition:'background 0.15s'}}>
                        {display}
                      </td>
                    );
                  })}
                  <td style={{padding:'11px 8px',textAlign:'center',color:showTotal?T.text:T.textFaint,fontWeight:700,fontSize:13}}>
                    {showTotal?`${rowTotal.toLocaleString()}${suffix}`:'—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isPersonalBrand&&igData&&(
        <div style={{background:T.card,borderRadius:12,padding:24,marginTop:20,border:`1px solid ${biz.color}15`}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div style={{fontSize:15,fontWeight:600,color:biz.color,display:'flex',alignItems:'center',gap:6}}>
              <InstagramLogo size={16}/> Instagram — Follower Goal
            </div>
            <button
              onClick={async () => {
                const gm = new Date().getMonth() + 1;
                const gy = new Date().getFullYear();
                const key = `${biz.id}_${gy}_${gm}`;
                const existing = monthlyData[key] || {};
                await saveMonthlyFocus(biz.id, '');
                await saveMonthlyData(
                  biz.id, gm, gy,
                  {
                    ...existing,
                    'Followers': igData.followers,
                    'Engagement %': igData.engagementRate,
                    'Content Posts': igData.postCount,
                  },
                  existing._targets || {},
                  existing
                );
              }}
              style={{fontSize:10,background:`${biz.color}20`,color:biz.color,border:`1px solid ${biz.color}40`,borderRadius:6,padding:'3px 10px',cursor:'pointer',fontWeight:600}}
            >
              ↻ Sync to Data
            </button>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:13,color:T.textDim,marginBottom:8}}>
            <span>Followers: <strong style={{color:T.textMid}}>{igData.followers?.toLocaleString()}</strong></span>
            <span style={{color:biz.color,fontWeight:700}}>{Math.round((igData.followers/1000)*100)}%</span>
          </div>
          <ProgressBar pct={Math.min((igData.followers/1000)*100,100)} color={biz.color} height={8}/>
          <div style={{fontSize:11,color:T.textDim,marginTop:8}}>
            {igData.followers?.toLocaleString()}/1,000 goal · Updated {new Date(igData.lastFetched).toLocaleTimeString()}
          </div>
        </div>
      )}

      {isPersonalBrand&&igData&&topContent.length>0&&(
        <div style={{background:T.card,borderRadius:12,padding:24,marginTop:20,border:`1px solid ${biz.color}15`}}>
          <div style={{fontSize:15,fontWeight:600,color:biz.color,marginBottom:4}}>Top Content</div>
          <div style={{fontSize:11,color:T.textDim,marginBottom:16}}>Ranked by views · last {topContent.length} posts</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:16}}>
            {topContent.map(post=>{
              const cover = post.thumbnail_url || post.media_url;
              return (
                <a key={post.id} href={post.permalink} target="_blank" rel="noopener noreferrer" style={{display:'block',textDecoration:'none',color:'inherit'}}>
                  <div style={{borderRadius:10,overflow:'hidden',border:`1px solid ${T.border}`,background:T.bg}}>
                    <div style={{width:'100%',aspectRatio:'1/1',background:T.border,display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}>
                      {cover ? (
                        <img src={cover} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                      ) : (
                        <span style={{fontSize:24,color:T.textFaint}}>🎬</span>
                      )}
                    </div>
                    <div style={{padding:'8px 10px',display:'flex',justifyContent:'space-between',fontSize:11,color:T.textDim}}>
                      <span>👁 {post.views!=null?post.views.toLocaleString():'—'}</span>
                      <span>❤ {(post.like_count||0).toLocaleString()}</span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ANALYSIS VIEW ───────────────────────────────────────────
function AnalysisView({businesses,monthlyData,getKpiValue}) {
  const [selBiz,setSelBiz] = useState('all');
  const [selKpi,setSelKpi] = useState('');
  const [startMonth,setStartMonth] = useState(1);
  const [startYear,setStartYear] = useState(new Date().getFullYear());
  const [endMonth,setEndMonth] = useState(new Date().getMonth()+1);
  const [endYear,setEndYear] = useState(new Date().getFullYear());
  const [tip,setTip] = useState({visible:false,x:0,y:0,content:null});

  const biz=businesses.find(b=>b.id===selBiz);
  const filteredBizs=selBiz==='all'?businesses:[biz].filter(Boolean);
  const kpis=biz?biz.kpis:[];
  useEffect(()=>{if(kpis.length)setSelKpi(kpis[0]);},[selBiz]); // eslint-disable-line react-hooks/exhaustive-deps

  const getRange=()=>{
    const r=[];let y=startYear,m=startMonth;
    while(y<endYear||(y===endYear&&m<=endMonth)){r.push({year:y,month:m,label:`${MONTHS[m-1]} ${y}`});m++;if(m>12){m=1;y++;}}
    return r;
  };
  const range=getRange();
  const getData=(b,kpi)=>range.map(r=>monthlyData[`${b.id}_${r.year}_${r.month}`]?.[kpi]||0);
  const maxAll=Math.max(...filteredBizs.flatMap(b=>getData(b,selBiz==='all'?b.kpis[0]:selKpi)),1);

  const radarData=businesses.map(b=>{const pk=b.kpis[0];const t=b.target?.[pk]||1;const v=getKpiValue(b,pk);return{name:b.name,color:b.color,emoji:b.emoji,progress:Math.min((v/t)*100,100)};});
  const pieDataFor=(b)=>b.kpis.map(kpi=>({kpi,total:getData(b,kpi).reduce((a,v)=>a+v,0)})).filter(d=>d.total>0);
  const pieData=biz?pieDataFor(biz):[];

  const showTip=(e,content)=>setTip({visible:true,x:e.clientX,y:e.clientY,content});
  const moveTip=(e)=>setTip(t=>({...t,x:e.clientX,y:e.clientY}));
  const hideTip=()=>setTip(t=>({...t,visible:false}));

  // width:'auto'+flexShrink:0 override the global `select{width:100%}` rule — without it, each
  // bare select in this wrapped flex row claims 100% of the row and stacks one-per-line instead
  // of packing tightly, which is what was leaving the filter bar looking half-empty.
  const selStyle={width:'auto',flexShrink:0,background:'rgba(255,255,255,0.04)',border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 12px',color:T.textMid,fontSize:13};
  // No backdrop-filter here: it would sample (and glow-blend) the page's animated scan line
  // through from behind, making the line appear to sweep across the cards instead of only
  // being visible in the gaps between them.
  const neonCard={...card,border:`1px solid ${T.border}`,boxShadow:'0 8px 24px rgba(0,0,0,0.25)'};
  const metricLabel = selBiz==='all' ? 'Primary KPI · All Businesses' : selKpi;
  const typeBadge = (label) => <span style={{fontSize:9,color:T.textFaint,letterSpacing:1,fontFamily:T.mono,background:'rgba(255,255,255,0.04)',padding:'2px 8px',borderRadius:10,flexShrink:0}}>{label}</span>;
  // Smooths a polyline into a curved path (midpoint-quadratic technique) — used by the Line
  // and Area charts so trends read as flowing curves instead of sharp angular joins.
  const smoothPath = (pts) => {
    if (pts.length===0) return '';
    if (pts.length===1) return `M ${pts[0].x} ${pts[0].y}`;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i=1;i<pts.length;i++){
      const mx=(pts[i-1].x+pts[i].x)/2, my=(pts[i-1].y+pts[i].y)/2;
      d += i===1 ? ` L ${mx} ${my}` : ` Q ${pts[i-1].x} ${pts[i-1].y} ${mx} ${my}`;
    }
    d += ` L ${pts[pts.length-1].x} ${pts[pts.length-1].y}`;
    return d;
  };

  return (
    <div style={{position:'relative',margin:-24,padding:24,background:T.bg,backgroundImage:`linear-gradient(${T.border} 1px, transparent 1px), linear-gradient(90deg, ${T.border} 1px, transparent 1px), radial-gradient(ellipse at 50% 0%, rgba(245,166,35,0.03) 0%, transparent 60%)`,backgroundSize:'40px 40px, 40px 40px, 100% 100%',minHeight:'100%'}}>
      <style>{`@keyframes analysisScank{from{top:0}to{top:100%}}`}</style>
      <div style={{position:'absolute',inset:0,overflow:'hidden',pointerEvents:'none'}}>
        <div style={{position:'absolute',left:0,right:0,height:1,background:'rgba(0,200,255,0.3)',animation:'analysisScank 10s linear infinite'}}/>
      </div>
      {tip.visible&&<div style={{position:'fixed',left:tip.x+14,top:tip.y-10,background:'#0a0a0a',border:`1px solid ${T.border}`,borderRadius:8,padding:'10px 14px',fontSize:13,color:T.text,zIndex:999,pointerEvents:'none',boxShadow:'0 4px 20px #00000080',minWidth:140,lineHeight:1.8}}>{tip.content}</div>}
      <div style={{marginBottom:24}}>
        <h1 style={{fontSize:22,fontWeight:700,marginBottom:4}}>Analysis</h1>
        <p style={{color:T.textDim,fontSize:14}}>Deep dive — all charts visible, scroll to explore.</p>
      </div>
      <div style={{background:T.card,borderRadius:12,padding:16,marginBottom:24,border:`1px solid ${T.border}`,display:'flex',gap:12,flexWrap:'wrap',alignItems:'center'}}>
        <select value={selBiz} onChange={e=>setSelBiz(e.target.value)} style={selStyle}>
          <option value="all">All Businesses</option>
          {businesses.map(b=><option key={b.id} value={b.id}>{b.emoji} {b.name}</option>)}
        </select>
        {selBiz!=='all'&&kpis.length>0&&<select value={selKpi} onChange={e=>setSelKpi(e.target.value)} style={selStyle}>{kpis.map(k=><option key={k} value={k}>{k}</option>)}</select>}
        <div style={{display:'flex',alignItems:'center',gap:8,color:T.textDim,fontSize:13,flexWrap:'wrap'}}>
          <span>From:</span>
          <select value={startMonth} onChange={e=>setStartMonth(parseInt(e.target.value))} style={selStyle}>{MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}</select>
          <select value={startYear} onChange={e=>setStartYear(parseInt(e.target.value))} style={selStyle}>{YEARS.map(y=><option key={y} value={y}>{y}</option>)}</select>
          <span>To:</span>
          <select value={endMonth} onChange={e=>setEndMonth(parseInt(e.target.value))} style={selStyle}>{MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}</select>
          <select value={endYear} onChange={e=>setEndYear(parseInt(e.target.value))} style={selStyle}>{YEARS.map(y=><option key={y} value={y}>{y}</option>)}</select>
        </div>
      </div>
      {/* minmax(0,1fr), not plain 1fr — a bare 1fr track still respects each item's intrinsic
          min-content width, so a wide chart in one column can steal space from the other and
          break the 50/50 split. alignItems left at the stretch default so row-mates match height
          instead of the shorter card leaving a gap before the next row starts. */}
      <div className="analysis-grid" style={{display:'grid',gridTemplateColumns:'repeat(2,minmax(0,1fr))',gap:20}}>
      <div style={neonCard}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}><div style={{fontSize:15,fontWeight:600,color:T.text}}>{metricLabel}</div>{typeBadge('BAR')}</div>
          <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>{filteredBizs.map(b=><div key={b.id} style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:T.textDim}}><div style={{width:10,height:10,borderRadius:2,background:b.color}}/>{b.name}</div>)}</div>
        </div>
        <div style={{display:'flex',gap:12}}>
          {/* Y-axis labels */}
          <div style={{display:'flex',flexDirection:'column',justifyContent:'space-between',alignItems:'flex-end',height:160,paddingBottom:0,flexShrink:0}}>
            {[100,75,50,25,0].map(p=><div key={p} style={{fontSize:11,color:T.textFaint,fontFamily:T.mono,lineHeight:1}}>{p===0?'0':Number((maxAll*(p/100)).toFixed(0)).toLocaleString()}</div>)}
          </div>
          {/* Chart area */}
          <div style={{flex:1,position:'relative',padding:'4px 4px 0',overflow:'hidden'}}>
            {/* Horizontal grid lines */}
            {[0,25,50,75,100].map(p=>(
              <div key={p} style={{position:'absolute',left:0,right:0,top:`${100-p}%`,borderTop:'1px dashed rgba(255,255,255,0.05)',pointerEvents:'none'}}/>
            ))}
            {/* Bars */}
            <div style={{display:'flex',gap:8,alignItems:'flex-end',height:160,position:'relative'}}>
              {range.map((r,ri)=>{
                const vals=filteredBizs.map(b=>getData(b,selBiz==='all'?b.kpis[0]:selKpi)[ri]);
                return(
                  <div key={ri} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4,minWidth:0}}>
                    <div style={{display:'flex',gap:8,alignItems:'flex-end',width:'100%',justifyContent:'center'}}>
                      {filteredBizs.map((b,bi)=>{
                        const val=vals[bi];const prev=ri>0?getData(b,selBiz==='all'?b.kpis[0]:selKpi)[ri-1]:null;const diff=prev!==null?val-prev:null;
                        const tipContent=<div><div style={{color:b.color,fontWeight:700}}>{b.name}</div><div style={{color:T.text}}>{Number(val).toLocaleString()}</div>{diff!==null&&<div style={{color:diff>=0?'#2ECC71':'#E74C3C'}}>{diff>=0?'+':'-'}{Math.abs(diff).toLocaleString()} vs prev</div>}</div>;
                        return <div key={bi} style={{minWidth:20,flex:1,background:`linear-gradient(180deg, ${b.color} 0%, ${b.color}20 100%)`,height:`${Math.max((val/maxAll)*130,2)}px`,borderRadius:'6px 6px 0 0',cursor:'pointer',transition:'height 0.4s',boxShadow:`0 0 12px ${b.color}20`}} onMouseEnter={e=>showTip(e,tipContent)} onMouseMove={moveTip} onMouseLeave={hideTip}/>;
                      })}
                    </div>
                    <div style={{fontSize:9,color:T.textFaint,whiteSpace:'nowrap'}}>{r.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div style={{display:'flex',gap:14,marginTop:12,flexWrap:'wrap'}}>{filteredBizs.map(b=><div key={b.id} style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:T.textDim}}><div style={{width:10,height:10,borderRadius:2,background:b.color}}/>{b.name}</div>)}</div>
      </div>
      <div style={neonCard}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}><div style={{fontSize:15,fontWeight:600,color:T.text}}>{metricLabel}</div>{typeBadge('LINE')}</div>
          <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>{filteredBizs.map(b=><div key={b.id} style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:T.textDim}}><div style={{width:20,height:2,background:b.color}}/>{b.name}</div>)}</div>
        </div>
        <svg viewBox="0 0 600 160" preserveAspectRatio="none" style={{width:'100%',height:180,display:'block'}}>
          {filteredBizs.map(b=>{const vals=getData(b,selBiz==='all'?b.kpis[0]:selKpi);const pts=vals.map((v,i)=>({x:range.length>1?(i/(range.length-1))*560+20:300,y:140-(v/maxAll)*120}));return <path key={b.id} d={smoothPath(pts)} fill="none" stroke={b.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" style={{filter:`drop-shadow(0 0 6px ${b.color})`}}/>;  })}
          {filteredBizs.map(b=>getData(b,selBiz==='all'?b.kpis[0]:selKpi).map((v,i)=>{const cx=range.length>1?(i/(range.length-1))*560+20:300;const cy2=140-(v/maxAll)*120;const diff=i>0?v-getData(b,selBiz==='all'?b.kpis[0]:selKpi)[i-1]:null;const tipC=<div><div style={{color:b.color,fontWeight:700}}>{b.name}</div><div style={{color:T.text}}>{Number(v).toLocaleString()}</div><div style={{color:T.textDim}}>{range[i]?.label}</div>{diff!==null&&<div style={{color:diff>=0?'#2ECC71':'#E74C3C'}}>{diff>=0?'+':'-'}{Math.abs(diff).toLocaleString()}</div>}</div>;return <circle key={`${b.id}-${i}`} cx={cx} cy={cy2} r={4} fill={b.color} style={{cursor:'pointer'}} onMouseEnter={e=>showTip(e,tipC)} onMouseMove={moveTip} onMouseLeave={hideTip}/>;  }))}
          {range.map((r,i)=><text key={i} x={range.length>1?(i/(range.length-1))*560+20:300} y={158} textAnchor="middle" fill={T.textFaint} fontSize="9">{r.label}</text>)}
        </svg>
      </div>
      <div style={neonCard}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}><div style={{fontSize:15,fontWeight:600,color:T.text}}>{metricLabel}</div>{typeBadge('AREA')}</div>
          <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>{filteredBizs.map(b=><div key={b.id} style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:T.textDim}}><div style={{width:14,height:10,background:b.color,opacity:0.5,borderRadius:2}}/>{b.name}</div>)}</div>
        </div>
        <svg viewBox="0 0 600 160" preserveAspectRatio="none" style={{width:'100%',height:180,display:'block'}}>
          <defs>{filteredBizs.map(b=><linearGradient key={b.id} id={`ag-${b.id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={b.color} stopOpacity="0.4"/><stop offset="100%" stopColor={b.color} stopOpacity="0.02"/></linearGradient>)}</defs>
          {filteredBizs.map(b=>{const vals=getData(b,selBiz==='all'?b.kpis[0]:selKpi);const pts=vals.map((v,i)=>({x:range.length>1?(i/(range.length-1))*560+20:300,y:140-(v/maxAll)*120}));const linePath=smoothPath(pts);const first=pts[0]?.x??20;const last=pts[pts.length-1]?.x??580;return <g key={b.id}><path d={`${linePath} L ${last} 140 L ${first} 140 Z`} fill={`url(#ag-${b.id})`} stroke="none"/><path d={linePath} fill="none" stroke={b.color} strokeWidth="2" strokeLinejoin="round" style={{filter:`drop-shadow(0 0 6px ${b.color})`}}/></g>;  })}
          {range.map((r,i)=><text key={i} x={range.length>1?(i/(range.length-1))*560+20:300} y={158} textAnchor="middle" fill={T.textFaint} fontSize="9">{r.label}</text>)}
        </svg>
      </div>
      <div style={neonCard}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:10}}>
          <div style={{fontSize:15,fontWeight:600,color:T.text}}>Business Health Radar — Goal Progress %</div>
          {radarData.length>0&&(()=>{const avgProgress=radarData.reduce((a,d)=>a+d.progress,0)/radarData.length;return(
            <span style={{fontSize:11,fontWeight:700,fontFamily:T.mono,color:avgProgress>=50?'#2ECC71':'#F5A623',background:avgProgress>=50?'rgba(46,204,113,0.12)':'rgba(245,166,35,0.12)',borderRadius:20,padding:'3px 10px'}}>Avg {avgProgress.toFixed(1)}%</span>
          );})()}
        </div>
        <div style={{display:'flex',gap:32,flexWrap:'wrap',alignItems:'center',justifyContent:'center'}}>
          <svg viewBox="0 0 300 300" style={{width:240,height:240,flexShrink:0,overflow:'visible'}}>
            {[20,40,60,80,100].map(p=><circle key={p} cx={150} cy={150} r={(p/100)*110} fill="none" stroke={T.border} strokeWidth="1" strokeDasharray="3 4"/>)}
            {radarData.map((_,i)=>{const angle=(i/radarData.length)*Math.PI*2-Math.PI/2;return <line key={i} x1={150} y1={150} x2={150+Math.cos(angle)*110} y2={150+Math.sin(angle)*110} stroke={T.border} strokeWidth="1"/>;  })}
            <polygon points={radarData.map((d,i)=>{const angle=(i/radarData.length)*Math.PI*2-Math.PI/2;const r=(d.progress/100)*110;return`${150+Math.cos(angle)*r},${150+Math.sin(angle)*r}`;}).join(' ')} fill="#F5A62325" stroke="#F5A623" strokeWidth="2"/>
            {radarData.map((d,i)=>{
              const angle=(i/radarData.length)*Math.PI*2-Math.PI/2;const r=(d.progress/100)*110;
              const cx=150+Math.cos(angle)*r, cy=150+Math.sin(angle)*r;
              const tipC=<div><div style={{color:d.color,fontWeight:700}}>{d.emoji} {d.name}</div><div style={{color:T.text}}>{d.progress.toFixed(1)}% of goal</div></div>;
              return <g key={i}>
                <circle cx={cx} cy={cy} r={6} fill={d.color} style={{cursor:'pointer'}} onMouseEnter={e=>showTip(e,tipC)} onMouseMove={moveTip} onMouseLeave={hideTip}/>
                <text x={150+Math.cos(angle)*122} y={150+Math.sin(angle)*122} textAnchor="middle" dominantBaseline="middle" fill={T.textMid} fontSize="10">{d.emoji}</text>
              </g>;
            })}
          </svg>
          <div style={{display:'grid',gap:10}}>
            {radarData.map(d=>(
              <div key={d.name} style={{background:T.bg,borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'center',gap:12,minWidth:190,border:`1px solid ${T.borderMid}`,borderLeft:`3px solid ${d.color}`}}>
                <span style={{fontSize:20}}>{d.emoji}</span>
                <div style={{flex:1}}><div style={{fontSize:12,color:T.textDim,marginBottom:2}}>{d.name}</div><div style={{fontSize:16,fontWeight:700,color:d.color,fontFamily:T.mono}}>{d.progress.toFixed(1)}%</div></div>
                <div style={{width:48,height:4,background:T.border,borderRadius:2}}><div style={{height:'100%',width:`${d.progress}%`,background:d.color,borderRadius:2}}/></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={neonCard}>
        <div style={{fontSize:15,fontWeight:600,color:T.text,marginBottom:20}}>KPI Breakdown {biz?`— ${biz.name}`:'— All Businesses'}</div>
        {!biz&&(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:16}}>
            {businesses.map(b=>{
              const bd=pieDataFor(b);
              if(bd.length===0) return (
                <div key={b.id} style={{textAlign:'center',padding:'16px 4px'}}>
                  <div style={{fontSize:22,marginBottom:6,opacity:0.4}}>{b.emoji}</div>
                  <div style={{fontSize:12,color:b.color,fontWeight:600,marginBottom:2}}>{b.name}</div>
                  <div style={{fontSize:11,color:T.textFaint}}>No data yet</div>
                </div>
              );
              const total=bd.reduce((a,d)=>a+d.total,0);let cum=0;
              const slices=bd.map((d,i)=>{const angle=(d.total/total)*360;const s={...d,angle,startAngle:cum,color:COLORS[i%COLORS.length]};cum+=angle;return s;});
              const p2xy=(angle,r)=>({x:60+r*Math.cos((angle-90)*Math.PI/180),y:60+r*Math.sin((angle-90)*Math.PI/180)});
              return (
                <div key={b.id} style={{textAlign:'center'}}>
                  <svg viewBox="0 0 120 120" style={{width:110,height:110}}>
                    {slices.map((s,i)=>{const st=p2xy(s.startAngle,50);const en=p2xy(s.startAngle+s.angle,50);const lg=s.angle>180?1:0;const tipC=<div><div style={{color:s.color,fontWeight:700}}>{s.kpi}</div><div style={{color:T.text}}>{s.total.toLocaleString()}</div><div style={{color:T.textDim}}>{((s.total/total)*100).toFixed(1)}% of {b.name}</div></div>;return <path key={i} d={`M60,60 L${st.x},${st.y} A50,50 0 ${lg},1 ${en.x},${en.y} Z`} fill={s.color} stroke={T.card} strokeWidth="1.5" style={{cursor:'pointer'}} onMouseEnter={e=>showTip(e,tipC)} onMouseMove={moveTip} onMouseLeave={hideTip}/>;})}
                    <circle cx={60} cy={60} r={26} fill={T.card}/>
                    <text x={60} y={57} textAnchor="middle" fill={T.text} fontSize="13" fontWeight="bold">{bd.length}</text>
                    <text x={60} y={70} textAnchor="middle" fill={T.textDim} fontSize="8">KPIs</text>
                  </svg>
                  <div style={{fontSize:12,fontWeight:600,color:b.color,marginTop:6}}>{b.emoji} {b.name}</div>
                </div>
              );
            })}
          </div>
        )}
        {biz&&pieData.length===0&&<p style={{color:T.textDim,fontSize:14}}>No data in selected date range yet.</p>}
        {biz&&pieData.length>0&&(()=>{
          const total=pieData.reduce((a,d)=>a+d.total,0);let cum=0;
          const slices=pieData.map((d,i)=>{const angle=(d.total/total)*360;const s={...d,angle,startAngle:cum,color:COLORS[i%COLORS.length]};cum+=angle;return s;});
          const p2xy=(angle,r)=>({x:150+r*Math.cos((angle-90)*Math.PI/180),y:150+r*Math.sin((angle-90)*Math.PI/180)});
          return (
            <div style={{display:'flex',gap:32,flexWrap:'wrap',alignItems:'center'}}>
              <svg viewBox="0 0 300 300" style={{width:200,height:200,flexShrink:0}}>
                {slices.map((s,i)=>{const st=p2xy(s.startAngle,90);const en=p2xy(s.startAngle+s.angle,90);const lg=s.angle>180?1:0;return <path key={i} d={`M150,150 L${st.x},${st.y} A90,90 0 ${lg},1 ${en.x},${en.y} Z`} fill={s.color} stroke={T.card} strokeWidth="2"/>;  })}
                <circle cx={150} cy={150} r={55} fill={T.card}/>
                <text x={150} y={148} textAnchor="middle" fill={T.text} fontSize="15" fontWeight="bold">{pieData.length}</text>
                <text x={150} y={164} textAnchor="middle" fill={T.textDim} fontSize="10">KPIs</text>
              </svg>
              <div style={{display:'grid',gap:10,flex:1}}>
                {slices.map((s,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:12,height:12,borderRadius:3,background:s.color,flexShrink:0}}/>
                    <span style={{fontSize:13,color:T.textMid,flex:1}}>{s.kpi}</span>
                    <span style={{fontSize:13,fontWeight:700,color:s.color}}>{s.total.toLocaleString()}</span>
                    <span style={{fontSize:12,color:T.textDim}}>({((s.total/total)*100).toFixed(1)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
      <div style={neonCard}>
        <div style={{fontSize:15,fontWeight:600,color:T.text,marginBottom:20}}>Month-over-Month Growth %</div>
        {filteredBizs.map(b=>{
          const vals=getData(b,selBiz==='all'?b.kpis[0]:selKpi);
          const growths=vals.map((v,i)=>i===0||vals[i-1]===0?null:parseFloat((((v-vals[i-1])/vals[i-1])*100).toFixed(1)));
          return (
            <div key={b.id} style={{marginBottom:20}}>
              <div style={{fontSize:13,fontWeight:600,color:b.color,marginBottom:10}}>{b.emoji} {b.name}</div>
              <div style={{display:'flex',gap:4,overflowX:'auto'}}>
                {growths.map((g,i)=>(
                  <div key={i} style={{flex:'0 0 auto',minWidth:44,textAlign:'center'}}>
                    <div style={{fontSize:11,fontWeight:700,color:g===null?T.textFaint:g>0?'#2ECC71':g<0?'#E74C3C':T.textDim,marginBottom:3}}>{g===null?'—':(g>0?'+':'-')+Math.abs(g)+'%'}</div>
                    <div style={{height:4,background:g===null?T.border:g>0?'#2ECC71':g<0?'#E74C3C':T.border,borderRadius:2,margin:'0 2px'}}/>
                    <div style={{fontSize:9,color:T.textFaint,marginTop:3}}>{range[i]?.label}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div style={neonCard}>
        <div style={{fontSize:15,fontWeight:600,color:T.text,marginBottom:16}}>Goal Completion Rate</div>
        <div style={{display:'grid',gap:14}}>
          {businesses.map(b=>{
            const pk=b.kpis[0];const target=b.target?.[pk]||1;const val=getKpiValue(b,pk);const pct=Math.min((val/target)*100,100);
            return (
              <div key={b.id} style={{display:'flex',alignItems:'center',gap:12}}>
                <span style={{fontSize:20}}>{b.emoji}</span>
                <div style={{flex:1}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}>
                    <span style={{color:b.color,fontWeight:600}}>{b.name}</span>
                    <span style={{color:T.textDim}}>{val.toLocaleString()} / {target.toLocaleString()} {pk}</span>
                  </div>
                  <div style={{height:6,background:T.bg,borderRadius:3}}><div style={{height:'100%',width:`${pct}%`,background:b.color,borderRadius:3,transition:'width 0.5s',boxShadow:`0 0 6px ${b.color}50`}}/></div>
                </div>
                <div style={{fontSize:15,fontWeight:700,color:pct>=100?'#2ECC71':b.color,minWidth:52,textAlign:'right',fontFamily:T.mono}}>{pct.toFixed(1)}%</div>
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
}

// ─── DATA ENTRY ───────────────────────────────────────────────
function DataEntryView({businesses,monthlyData,saveMonthlyData}) {
  const [selBiz,setSelBiz] = useState(businesses[0]?.id||'');
  const [selMonth,setSelMonth] = useState(new Date().getMonth()+1);
  const [selYear,setSelYear] = useState(new Date().getFullYear());
  const [localValues,setLocalValues] = useState({});
  const [localTargets,setLocalTargets] = useState({});
  const biz=businesses.find(b=>b.id===selBiz);

  useEffect(()=>{
    if(!biz) return;
    const key=`${selBiz}_${selYear}_${selMonth}`;const existing=monthlyData[key]||{};
    const vals={};const tgts={};
    biz.kpis.forEach(kpi=>{vals[kpi]=existing[kpi]!==undefined?String(existing[kpi]):'';tgts[kpi]=existing._targets?.[kpi]!==undefined?String(existing._targets[kpi]):'';});
    setLocalValues(vals);setLocalTargets(tgts);
  },[selBiz,selMonth,selYear,monthlyData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave=async()=>{
    const key=`${selBiz}_${selYear}_${selMonth}`;const oldValues=monthlyData[key]||{};
    const toSave={};const toTargets={};
    Object.entries(localValues).forEach(([k,v])=>{if(v!==''&&v!==undefined)toSave[k]=parseFloat(v);});
    Object.entries(localTargets).forEach(([k,v])=>{if(v!==''&&v!==undefined)toTargets[k]=parseFloat(v);});
    await saveMonthlyData(selBiz,selMonth,selYear,toSave,toTargets,oldValues);
  };

  const hasData=!!monthlyData[`${selBiz}_${selYear}_${selMonth}`];
  const selStyle={background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:'10px 14px',color:T.text,fontSize:14,boxSizing:'border-box'};

  return (
    <div style={{maxWidth:720}}>
      <h1 style={{fontSize:22,fontWeight:700,marginBottom:6}}>Enter Monthly Data</h1>
      <p style={{color:T.textDim,fontSize:14,marginBottom:24}}>Log actuals and set monthly targets per KPI.</p>
      <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:24}}>
        <div><label style={label}>BUSINESS</label><select value={selBiz} onChange={e=>setSelBiz(e.target.value)} style={{...selStyle,width:'100%'}}>{businesses.map(b=><option key={b.id} value={b.id}>{b.emoji} {b.name}</option>)}</select></div>
        <div><label style={label}>MONTH</label><select value={selMonth} onChange={e=>setSelMonth(parseInt(e.target.value))} style={{...selStyle,width:'100%'}}>{MONTHS_FULL.map((m,i)=><option key={m} value={i+1}>{m}</option>)}</select></div>
        <div><label style={label}>YEAR</label><select value={selYear} onChange={e=>setSelYear(parseInt(e.target.value))} style={{...selStyle,width:'100%'}}>{YEARS.map(y=><option key={y} value={y}>{y}</option>)}</select></div>
      </div>
      {biz&&(
        <div style={{background:T.card,borderRadius:12,padding:24,border:`1px solid ${biz.color}20`}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20}}>
            <span style={{fontSize:22}}>{biz.emoji}</span>
            <span style={{fontWeight:600,fontSize:16,color:biz.color}}>{biz.name}</span>
            <span style={{color:T.textDim,fontSize:14}}>— {MONTHS_FULL[selMonth-1]} {selYear}</span>
            {hasData&&<span style={{marginLeft:'auto',fontSize:11,color:'#2ECC71',background:'#2ECC7115',padding:'3px 10px',borderRadius:20,border:'1px solid #2ECC7130'}}>✓ Has existing data</span>}
          </div>
          <div className="form-grid-header" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:12,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>
            <div style={{fontSize:11,color:T.textDim,letterSpacing:1}}>KPI</div>
            <div style={{fontSize:11,color:T.textDim,letterSpacing:1}}>ACTUAL VALUE</div>
            <div style={{fontSize:11,color:'#F5A623',letterSpacing:1}}>🎯 MONTHLY TARGET</div>
          </div>
          <div style={{display:'grid',gap:14}}>
            {biz.kpis.map(kpi=>(
              <div key={kpi} className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,alignItems:'center'}}>
                <div>
                  <div style={{fontSize:14,color:T.textMid}}>{kpi}</div>
                  <div style={{fontSize:10,color:T.textFaint,marginTop:2}}>{getKpiType(kpi)==='snapshot'?'◈ Snapshot':'◆ Cumulative'}</div>
                </div>
                <input type="number" placeholder="Actual" value={localValues[kpi]||''} onChange={e=>setLocalValues(p=>({...p,[kpi]:e.target.value}))} style={{...inp}}/>
                <input type="number" placeholder="Target" value={localTargets[kpi]||''} onChange={e=>setLocalTargets(p=>({...p,[kpi]:e.target.value}))} style={{...inpTarget}}/>
              </div>
            ))}
          </div>
          <button onClick={handleSave} style={{...btn(biz.color),width:'100%',marginTop:20,padding:13,fontSize:15}}>
            Save {MONTHS_FULL[selMonth-1]} {selYear} Data
          </button>
        </div>
      )}
    </div>
  );
}

// ─── MANAGE VIEW ─────────────────────────────────────────────
function ManageView({businesses,saveBusiness,deleteBusiness,changeLog}) {
  const [selId,setSelId] = useState(businesses[0]?.id||'');
  const [form,setForm] = useState({});
  const [tab,setTab] = useState('edit');
  const [confirmDelete,setConfirmDelete] = useState(false);
  const biz=businesses.find(b=>b.id===selId);
  useEffect(()=>{if(biz)setForm({...biz});},[selId]); // eslint-disable-line react-hooks/exhaustive-deps
  const update=(f,v)=>setForm(p=>({...p,[f]:v}));
  const updateCurrent=(k,v)=>setForm(p=>({...p,current:{...p.current,[k]:parseFloat(v)||0}}));
  const updateCommitItem=(i,v)=>setForm(p=>{const arr=[...(p.commitmentItems||[])];arr[i]=v;return{...p,commitmentItems:arr};});
  const addCommitItem=()=>setForm(p=>({...p,commitmentItems:[...(p.commitmentItems||[]),'']}));
  const removeCommitItem=(i)=>setForm(p=>({...p,commitmentItems:(p.commitmentItems||[]).filter((_,j)=>j!==i)}));
  const bizLog=changeLog.filter(l=>l.bizId===selId);
  const selStyle={width:'100%',background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:'10px 14px',color:T.text,fontSize:14,boxSizing:'border-box'};

  return (
    <div style={{maxWidth:700}}>
      <h1 style={{fontSize:22,fontWeight:700,marginBottom:6}}>Manage Businesses</h1>
      <p style={{color:T.textDim,fontSize:14,marginBottom:24}}>Edit settings, update base data, view change history.</p>
      <div style={{marginBottom:20}}>
        <label style={label}>SELECT BUSINESS</label>
        <select value={selId} onChange={e=>{setSelId(e.target.value);setConfirmDelete(false);}} style={{...selStyle,width:'auto',minWidth:240}}>{businesses.map(b=><option key={b.id} value={b.id}>{b.emoji} {b.name}</option>)}</select>
      </div>
      {form.id&&(<>
        <div style={{display:'flex',gap:4,marginBottom:20}}>
          {[['edit','⚙️ Profile'],['current','📊 Base Data'],['history','📋 History']].map(([t,l])=>(
            <button key={t} onClick={()=>setTab(t)} style={{padding:'9px 18px',background:tab===t?'#F5A623':T.card,color:tab===t?'#000':T.textDim,border:`1px solid ${tab===t?'#F5A623':T.border}`,borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600}}>{l}</button>
          ))}
        </div>
        {tab==='edit'&&(
          <div style={{background:T.card,borderRadius:12,padding:24,border:`1px solid ${form.color||T.border}20`}}>
            <div style={{display:'grid',gap:16}}>
              <div><label style={label}>BUSINESS NAME</label><input style={{...inp}} value={form.name||''} onChange={e=>update('name',e.target.value)}/></div>
              <div><label style={label}>NORTH STAR GOAL</label><input style={{...inp}} value={form.goal||''} onChange={e=>update('goal',e.target.value)}/></div>
              <div><label style={label}>MILESTONE</label><input style={{...inp}} placeholder="e.g. 100K AED MRR" value={form.milestone||''} onChange={e=>update('milestone',e.target.value)}/></div>
              <div><label style={label}>DEADLINE</label><input type="date" style={{...inp}} value={form.deadline||''} onChange={e=>update('deadline',e.target.value)}/></div>
              <div><label style={label}>DEADLINE SET ON</label><input type="date" style={{...inp}} value={form.deadlineSetAt||''} onChange={e=>update('deadlineSetAt',e.target.value)}/></div>
              <div><label style={label}>CLICKUP PROJECT URL</label><input type="text" placeholder="https://app.clickup.com/..." style={{...inp}} value={form.clickupUrl||''} onChange={e=>update('clickupUrl',e.target.value)}/></div>
              <div>
                <label style={label}>DAILY COMMITMENT ITEMS</label>
                <div style={{display:'grid',gap:8}}>
                  {(form.commitmentItems||[]).map((item,i)=>(
                    <div key={i} style={{display:'flex',gap:8}}>
                      <input style={{...inp,flex:1}} placeholder={`Commitment ${i+1} (e.g. Post 1 Reel)`} value={item} onChange={e=>updateCommitItem(i,e.target.value)}/>
                      <button onClick={()=>removeCommitItem(i)} style={{background:'#1a0808',border:'1px solid #E74C3C30',color:'#E74C3C',borderRadius:8,padding:'0 12px',cursor:'pointer',fontSize:14}}>✕</button>
                    </div>
                  ))}
                  <button onClick={addCommitItem} style={{background:T.bg,border:`1px dashed ${T.border}`,color:T.textDim,borderRadius:8,padding:'8px 12px',cursor:'pointer',fontSize:13,textAlign:'left'}}>+ Add commitment item</button>
                </div>
              </div>
              <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={label}>MOMENTUM</label><select style={selStyle} value={form.momentum||''} onChange={e=>update('momentum',e.target.value)}>{MOMENTUMS.map(m=><option key={m}>{m}</option>)}</select></div>
                <div><label style={label}>PRIORITY</label><select style={selStyle} value={form.priority||''} onChange={e=>update('priority',e.target.value)}>{PRIORITIES.map(p=><option key={p}>{p}</option>)}</select></div>
              </div>
              <div><label style={label}>EMOJI</label><div style={{display:'flex',flexWrap:'wrap',gap:8}}>{EMOJIS.map(e=><button key={e} onClick={()=>update('emoji',e)} style={{fontSize:20,background:form.emoji===e?T.border:'transparent',border:form.emoji===e?`1px solid #F5A623`:`1px solid ${T.border}`,borderRadius:8,padding:'6px 10px',cursor:'pointer'}}>{e}</button>)}</div></div>
              <div><label style={label}>COLOR</label><div style={{display:'flex',flexWrap:'wrap',gap:8}}>{COLORS.map(c=><button key={c} onClick={()=>update('color',c)} style={{width:30,height:30,borderRadius:'50%',background:c,border:form.color===c?'3px solid #fff':'2px solid transparent',cursor:'pointer'}}/>)}</div></div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:20}}>
              <button onClick={()=>saveBusiness(form,biz)} style={{...btn(form.color||'#F5A623'),flex:1,padding:13}}>Save Changes</button>
              {!confirmDelete
                ?<button onClick={()=>setConfirmDelete(true)} style={{padding:'12px 20px',background:'#1a0808',color:'#E74C3C',border:'1px solid #E74C3C30',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:14}}>Delete</button>
                :<button onClick={()=>{deleteBusiness(selId);setConfirmDelete(false);setSelId(businesses.filter(b=>b.id!==selId)[0]?.id||'');}} style={{padding:'12px 20px',background:'#E74C3C',color:'#fff',border:'none',borderRadius:8,cursor:'pointer',fontWeight:700,fontSize:14}}>Confirm Delete</button>
              }
            </div>
          </div>
        )}
        {tab==='current'&&(
          <div style={{background:T.card,borderRadius:12,padding:24,border:`1px solid ${form.color||T.border}20`}}>
            <p style={{color:T.textDim,fontSize:14,marginBottom:20}}>Set your starting/base values. Snapshot KPIs show this as current value. Cumulative KPIs add monthly entries on top of this.</p>
            <div style={{display:'grid',gap:14}}>
              {(form.kpis||[]).map(kpi=>(
                <div key={kpi} style={{display:'flex',alignItems:'center',gap:14}}>
                  <div style={{minWidth:200}}>
                    <div style={{fontSize:14,color:T.textMid}}>{kpi}</div>
                    <div style={{fontSize:10,color:T.textFaint,marginTop:2}}>{getKpiType(kpi)==='snapshot'?'◈ Snapshot':'◆ Cumulative'}</div>
                  </div>
                  <input type="number" value={form.current?.[kpi]||''} onChange={e=>updateCurrent(kpi,e.target.value)} style={{...inp,flex:1}} placeholder="Base value"/>
                </div>
              ))}
            </div>
            <button onClick={()=>saveBusiness(form,biz)} style={{...btn(form.color||'#F5A623'),width:'100%',marginTop:20,padding:13}}>Update Base Data</button>
          </div>
        )}
        {tab==='history'&&(
          <div style={{background:T.card,borderRadius:12,padding:24,border:`1px solid ${T.border}`}}>
            <div style={{fontSize:14,fontWeight:600,color:T.textMid,marginBottom:16}}>Change History — {biz?.name}</div>
            {bizLog.length===0&&<p style={{color:T.textFaint,fontSize:14}}>No changes logged yet.</p>}
            <div style={{display:'grid',gap:8,maxHeight:400,overflowY:'auto'}}>
              {bizLog.map((log,i)=>(
                <div key={i} style={{background:T.bg,borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'center',gap:12,fontSize:13}}>
                  <div style={{flex:1,lineHeight:1.6}}>
                    <span style={{color:T.textDim}}>{log.field}: </span>
                    <span style={{color:'#E74C3C',fontWeight:600}}>{log.oldVal}</span>
                    <span style={{color:T.textDim}}> → </span>
                    <span style={{color:'#2ECC71',fontWeight:600}}>{log.newVal}</span>
                  </div>
                  <div style={{color:T.textFaint,fontSize:11,whiteSpace:'nowrap'}}>{log.date}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>)}
    </div>
  );
}

// ─── SETTINGS VIEW ───────────────────────────────────────────
function SettingsView({user,logoUrl,saveLogoUrl,changeLog,businesses,bottomTabs,saveBottomTabs}) { // eslint-disable-line no-unused-vars
  const [newUrl,setNewUrl] = useState(logoUrl||'');
  const [tabs,setTabs] = useState(bottomTabs||DEFAULT_BOTTOM_TABS);
  const updateTab = (idx,view) => { const next=[...tabs]; next[idx]=view; setTabs(next); };
  return (
    <div style={{maxWidth:600}}>
      <h1 style={{fontSize:22,fontWeight:700,marginBottom:6}}>Settings</h1>
      <p style={{color:T.textDim,fontSize:14,marginBottom:24}}>Customize your Singlemass.</p>
      <div style={card}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Account</div>
        <div style={{display:'flex',gap:16,alignItems:'center'}}>
          {user?.photoURL
            ? <img src={user.photoURL} alt="" style={{width:64,height:64,objectFit:'cover',borderRadius:'50%',border:`1px solid ${T.border}`}}/>
            : <div style={{width:64,height:64,borderRadius:'50%',background:T.bg,border:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,fontWeight:700,color:T.textMid,flexShrink:0}}>
                {user?.displayName
                  ? user.displayName.split(' ').filter(Boolean).map(w=>w[0]).join('').toUpperCase().slice(0,2)
                  : <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4.4 3.6-8 8-8s8 3.6 8 8"/></svg>}
              </div>
          }
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:15,fontWeight:600,color:T.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{user?.displayName||'—'}</div>
            <div style={{fontSize:13,color:T.textDim,marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{user?.email||'—'}</div>
          </div>
        </div>
      </div>
      <div style={card}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Logo</div>
        <div style={{display:'flex',gap:16,alignItems:'center',marginBottom:16}}>
          {logoUrl?<img src={logoUrl} alt="Logo" style={{width:64,height:64,objectFit:'contain',borderRadius:10,background:T.bg,border:`1px solid ${T.border}`}}/>:<div style={{width:64,height:64,background:T.bg,borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,border:`1px solid ${T.border}`}}>⚡</div>}
          <div style={{flex:1}}>
            <p style={{color:T.textDim,fontSize:13,marginBottom:8}}>Paste a direct image URL (.png, .jpg, .webp):</p>
            <input style={{...inp}} placeholder="https://i.imgur.com/yourlogo.png" value={newUrl} onChange={e=>setNewUrl(e.target.value)}/>
          </div>
        </div>
        <button onClick={()=>saveLogoUrl(newUrl)} style={btn()}>Save Logo</button>
      </div>
      <div style={card}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>Mobile Quick Bar</div>
        <p style={{color:T.textDim,fontSize:13,marginBottom:16}}>Choose the 3 tabs shown in the sticky bottom bar on mobile.</p>
        <div style={{display:'grid',gap:12,marginBottom:16}}>
          {[0,1,2].map(idx=>(
            <div key={idx} style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:11,color:T.textFaint,fontFamily:T.mono,width:50,flexShrink:0}}>TAB {idx+1}</span>
              <select style={{...inp}} value={tabs[idx]} onChange={e=>updateTab(idx,e.target.value)}>
                {BOTTOM_TAB_OPTIONS.map(o=><option key={o.view} value={o.view}>{o.label}</option>)}
              </select>
            </div>
          ))}
        </div>
        <button onClick={()=>saveBottomTabs(tabs)} style={btn()}>Save Quick Bar</button>
      </div>
      <div style={card}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Full Change Log</div>
        {changeLog.length===0&&<p style={{color:T.textFaint,fontSize:14}}>No changes logged yet.</p>}
        <div style={{display:'grid',gap:8,maxHeight:400,overflowY:'auto'}}>
          {changeLog.map((log,i)=>(
            <div key={i} style={{background:T.bg,borderRadius:8,padding:'12px 16px',fontSize:13}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                <span style={{color:'#F5A623',fontWeight:600}}>{log.bizName}</span>
                <span style={{color:T.textFaint,fontSize:11}}>{log.date}</span>
              </div>
              <span style={{color:T.textDim}}>{log.field}: </span>
              <span style={{color:'#E74C3C',fontWeight:600}}>{log.oldVal}</span>
              <span style={{color:T.textDim}}> → </span>
              <span style={{color:'#2ECC71',fontWeight:600}}>{log.newVal}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── PIPELINE VIEW ────────────────────────────────────────────
// • Leads persisted to Firebase: /funnels/{funnelId}/leads/{leadId}
// • Funnels persisted to Firebase: /funnels/{funnelId}
// • Stage 1 (Qualified)  → +1 "New Leads" current month (reversed if moved back)
// • Stage 4 (Closed)     → +1 "Active Clients" base + current month
// • qualifiedCounted / convertedCounted flags → each counted once per lead
// • Multi-funnel — each funnel links to any business independently
// • ByForce funnel auto-seeded on first load
function PipelineView({businesses, monthlyData, saveBusiness, saveMonthlyData, showNotif}) {
  const STAGES = ['Convos','Qualified','Links Sent','Booked','Closed'];
  const STAGE_COLORS = ['#93c5fd','#60a5fa','#3b82f6','#2563eb','#1d4ed8'];
  const QUALIFIED_STAGE = 1;
  const CLOSED_STAGE = 4;
  const QUALIFIED_KPI = 'New Leads';
  const CONVERTED_KPI = 'Active Clients';

  const [funnels, setFunnels] = React.useState([]);
  const [activeFunnelId, setActiveFunnelId] = React.useState(null);
  const [leads, setLeads] = React.useState([]);
  const [loadingFunnels, setLoadingFunnels] = React.useState(true);
  const [loadingLeads, setLoadingLeads] = React.useState(false);
  const [showNewFunnel, setShowNewFunnel] = React.useState(false);
  const [newFunnel, setNewFunnel] = React.useState({name:'', bizId:''});
  const [newLead, setNewLead] = React.useState({name:'', value:''});
  const [convertPopup, setConvertPopup] = React.useState(null);

  const activeFunnel = funnels.find(f => f.id === activeFunnelId);
  const linkedBiz = businesses.find(b => b.id === activeFunnel?.bizId);

  React.useEffect(() => { loadFunnelsData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadFunnelsData() {
    try {
      const snap = await getDocs(collection(db,'funnels'));
      const data = snap.docs.map(d => ({...d.data(), id:d.id}));
      if (data.length === 0) {
        const seed = {id:'byforce-pipeline', name:'ByForce Pipeline', bizId:'byforce', createdAt:Date.now()};
        await setDoc(doc(db,'funnels',seed.id), seed);
        setFunnels([seed]);
        setActiveFunnelId(seed.id);
      } else {
        setFunnels(data);
        setActiveFunnelId(data[0].id);
      }
    } catch(e) { console.error('loadFunnels', e); }
    setLoadingFunnels(false);
  }

  React.useEffect(() => {
    if (!activeFunnelId) return;
    loadLeadsData(activeFunnelId);
  }, [activeFunnelId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadLeadsData(funnelId) {
    setLoadingLeads(true);
    try {
      const snap = await getDocs(collection(db,'funnels',funnelId,'leads'));
      setLeads(snap.docs.map(d => ({...d.data(), id:d.id})));
    } catch(e) { console.error('loadLeads', e); }
    setLoadingLeads(false);
  }

  async function persistLead(funnelId, lead) {
    await setDoc(doc(db,'funnels',funnelId,'leads',lead.id), lead);
  }

  async function addLead() {
    if (!newLead.name.trim() || !activeFunnelId) return;
    const lead = {
      id: `lead_${Date.now()}`,
      name: newLead.name.trim(),
      value: parseFloat(newLead.value) || 0,
      stage: 0,
      qualifiedCounted: false,
      convertedCounted: false,
      createdAt: Date.now(),
    };
    await persistLead(activeFunnelId, lead);
    setLeads(prev => [...prev, lead]);
    setNewLead({name:'', value:''});
    showNotif(`Lead "${lead.name}" added`);
  }

  async function removeLead(leadId) {
    try {
      await deleteDoc(doc(db,'funnels',activeFunnelId,'leads',leadId));
      setLeads(prev => prev.filter(l => l.id !== leadId));
    } catch(e) { showNotif('Error removing lead','error'); }
  }

  async function moveLead(leadId, dir) {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    const newStage = Math.max(0, Math.min(STAGES.length - 1, lead.stage + dir));
    if (newStage === lead.stage) return;
    if (newStage === CLOSED_STAGE && lead.stage !== CLOSED_STAGE) {
      setConvertPopup({lead, newStage});
      return;
    }
    await applyStageChange(lead, newStage, false);
  }

  async function applyStageChange(lead, newStage, confirmConvert) {
    const cm = new Date().getMonth() + 1;
    const cy = new Date().getFullYear();
    let updated = {...lead, stage: newStage};

    if (linkedBiz) {
      const toQualified    = newStage === QUALIFIED_STAGE && lead.stage < QUALIFIED_STAGE;
      const fromQualified  = lead.stage === QUALIFIED_STAGE && newStage < QUALIFIED_STAGE;
      const passingQualified = newStage > QUALIFIED_STAGE && lead.stage < QUALIFIED_STAGE;

      if (toQualified && !lead.qualifiedCounted) {
        updated.qualifiedCounted = true;
        await bumpKpi(linkedBiz, QUALIFIED_KPI, cm, cy, +1);
        showNotif(`+1 ${QUALIFIED_KPI} → ${linkedBiz.name}`);
      }
      if (fromQualified && lead.qualifiedCounted) {
        updated.qualifiedCounted = false;
        await bumpKpi(linkedBiz, QUALIFIED_KPI, cm, cy, -1);
        showNotif(`-1 ${QUALIFIED_KPI} reversed`);
      }
      if (passingQualified && !lead.qualifiedCounted) {
        updated.qualifiedCounted = true;
        await bumpKpi(linkedBiz, QUALIFIED_KPI, cm, cy, +1);
      }
      if (newStage === CLOSED_STAGE && !lead.convertedCounted && confirmConvert) {
        updated.convertedCounted = true;
        await bumpKpi(linkedBiz, CONVERTED_KPI, cm, cy, +1);
        await bumpBase(linkedBiz, CONVERTED_KPI, +1);
        showNotif(`🎉 ${lead.name} converted → +1 ${CONVERTED_KPI}`);
      }
    }

    await persistLead(activeFunnelId, updated);
    setLeads(prev => prev.map(l => l.id === lead.id ? updated : l));
    setConvertPopup(null);
  }

  // FIX 5: silent=true suppresses "Data saved!" toast spam on every stage change
  async function bumpKpi(biz, kpi, month, year, delta) {
    if (!biz.kpis.includes(kpi)) return;
    const key = `${biz.id}_${year}_${month}`;
    const existing = monthlyData[key] || {};
    const newVal = Math.max(0, (existing[kpi] || 0) + delta);
    await saveMonthlyData(biz.id, month, year, {...existing, [kpi]: newVal}, existing._targets || {}, existing, true);
  }

  async function bumpBase(biz, kpi, delta) {
    if (!biz.kpis.includes(kpi)) return;
    const updatedBiz = {...biz, current: {...biz.current, [kpi]: Math.max(0, (biz.current?.[kpi] || 0) + delta)}};
    await saveBusiness(updatedBiz, biz);
  }

  async function createFunnel() {
    if (!newFunnel.name.trim()) return;
    const id = `funnel_${Date.now()}`;
    const funnel = {id, name:newFunnel.name.trim(), bizId:newFunnel.bizId||'', createdAt:Date.now()};
    await setDoc(doc(db,'funnels',id), funnel);
    setFunnels(prev => [...prev, funnel]);
    setActiveFunnelId(id);
    setNewFunnel({name:'', bizId:''});
    setShowNewFunnel(false);
    showNotif(`Funnel "${funnel.name}" created!`);
  }

  const stageCounts = STAGES.map((_,i) => leads.filter(l => l.stage === i).length);
  const maxCount = Math.max(...stageCounts, 1);
  const totalPipeline = leads.reduce((s,l) => s + (l.value||0), 0);
  const closedLeads = leads.filter(l => l.stage === CLOSED_STAGE);
  const closedValue = closedLeads.reduce((s,l) => s + (l.value||0), 0);
  const convRate = leads.length > 0 ? Math.round((closedLeads.length / leads.length) * 100) : 0;

  if (loadingFunnels) return (
    <div style={{textAlign:'center',color:T.textDim,marginTop:80,fontFamily:T.mono,letterSpacing:2}}>LOADING PIPELINE...</div>
  );

  return (
    <div style={{maxWidth:900}}>
      {convertPopup && (
        <div style={{position:'fixed',inset:0,background:'#000000cc',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:T.card,borderRadius:16,padding:32,width:440,border:`1px solid ${T.border}`}}>
            <div style={{fontSize:18,fontWeight:700,marginBottom:12}}>Close This Lead?</div>
            <div style={{color:T.textMid,fontSize:14,marginBottom:6}}>
              <strong style={{color:T.text}}>{convertPopup.lead.name}</strong> → moving to <strong style={{color:'#1d4ed8'}}>Closed</strong>
            </div>
            <div style={{color:T.textMid,fontSize:14,marginBottom:20}}>
              Value: <strong style={{color:'#2ECC71'}}>AED {(convertPopup.lead.value||0).toLocaleString()}</strong>
            </div>
            {linkedBiz && (
              <div style={{background:T.bg,borderRadius:8,padding:14,marginBottom:20,fontSize:13,border:`1px solid ${T.border}`}}>
                <div style={{color:T.textDim,marginBottom:8}}>Will update <strong style={{color:linkedBiz.color}}>{linkedBiz.emoji} {linkedBiz.name}</strong>:</div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  <div style={{color:'#2ECC71'}}>✓ +1 <strong>{CONVERTED_KPI}</strong> (base + this month)</div>
                  {!convertPopup.lead.qualifiedCounted && <div style={{color:'#F5A623'}}>✓ +1 <strong>{QUALIFIED_KPI}</strong> (auto-counted)</div>}
                </div>
              </div>
            )}
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>applyStageChange(convertPopup.lead, convertPopup.newStage, true)} style={{...btn('#2ECC71'),flex:1,padding:12}}>✓ Confirm & Update KPIs</button>
              <button onClick={()=>applyStageChange(convertPopup.lead, convertPopup.newStage, false)} style={{flex:1,padding:12,background:T.bg,color:T.text,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:14}}>Move Only</button>
              <button onClick={()=>setConvertPopup(null)} style={{padding:'12px 14px',background:'transparent',color:T.textDim,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer',fontSize:14}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:700,marginBottom:4}}>Pipeline & Funnels</h1>
          <p style={{color:T.textDim,fontSize:14}}>Leads persisted · KPIs auto-synced on stage changes</p>
        </div>
        <button onClick={()=>setShowNewFunnel(true)} style={{...btn('#F5A623'),display:'flex',alignItems:'center',gap:8}}>+ New Funnel</button>
      </div>

      {showNewFunnel && (
        <div style={{...card, border:`1px solid #F5A62340`, marginBottom:20}}>
          <div style={{fontSize:14,fontWeight:600,color:'#F5A623',marginBottom:16}}>Create New Funnel</div>
          <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
            <div>
              <label style={label}>FUNNEL NAME</label>
              <input style={{...inp}} placeholder="e.g. Clevene Sales" value={newFunnel.name} onChange={e=>setNewFunnel(p=>({...p,name:e.target.value}))}/>
            </div>
            <div>
              <label style={label}>LINK TO BUSINESS (optional)</label>
              <select value={newFunnel.bizId} onChange={e=>setNewFunnel(p=>({...p,bizId:e.target.value}))} style={{...inp}}>
                <option value="">Standalone (no KPI sync)</option>
                {businesses.map(b=><option key={b.id} value={b.id}>{b.emoji} {b.name}</option>)}
              </select>
            </div>
          </div>
          {newFunnel.bizId && (()=>{const biz=businesses.find(b=>b.id===newFunnel.bizId);return(
            <div style={{background:T.bg,borderRadius:8,padding:12,marginBottom:16,fontSize:13,color:T.textDim,border:`1px solid ${T.border}`}}>
              <div style={{color:T.textMid,marginBottom:6}}>Auto KPI sync for <strong style={{color:biz?.color}}>{biz?.name}</strong>:</div>
              <div>• Lead → <strong style={{color:'#60a5fa'}}>Qualified</strong> → +1 <strong>New Leads</strong></div>
              <div>• Lead → <strong style={{color:'#1d4ed8'}}>Closed</strong> → +1 <strong>Active Clients</strong></div>
            </div>
          );})()}
          <div style={{display:'flex',gap:10}}>
            <button onClick={createFunnel} style={btn('#F5A623')}>Create Funnel</button>
            <button onClick={()=>setShowNewFunnel(false)} style={{padding:'10px 16px',background:'transparent',color:T.textDim,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer',fontSize:14}}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{display:'flex',gap:6,marginBottom:20,flexWrap:'wrap'}}>
        {funnels.map(f=>{
          const biz=businesses.find(b=>b.id===f.bizId);
          const isActive=f.id===activeFunnelId;
          const accentColor=biz?.color||'#F5A623';
          return (
            <button key={f.id} onClick={()=>setActiveFunnelId(f.id)} style={{padding:'8px 16px',background:isActive?`${accentColor}15`:T.card,border:`1px solid ${isActive?accentColor+'60':T.border}`,borderRadius:8,cursor:'pointer',color:isActive?accentColor:T.textDim,fontSize:13,fontWeight:isActive?700:400,display:'flex',alignItems:'center',gap:6,transition:'all 0.15s'}}>
              <span>{biz?biz.emoji:'🔽'}</span>
              {f.name}
              {biz&&<div style={{width:6,height:6,borderRadius:'50%',background:biz.color,boxShadow:isActive?`0 0 6px ${biz.color}`:'none'}}/>}
            </button>
          );
        })}
      </div>

      {linkedBiz && (
        <div style={{background:`${linkedBiz.color}08`,borderRadius:10,padding:'10px 16px',marginBottom:20,border:`1px solid ${linkedBiz.color}20`,display:'flex',alignItems:'center',gap:10,fontSize:13,flexWrap:'wrap'}}>
          <span style={{fontSize:18}}>{linkedBiz.emoji}</span>
          <span style={{color:T.textDim}}>Synced with</span>
          <strong style={{color:linkedBiz.color}}>{linkedBiz.name}</strong>
          <span style={{color:T.textDim,marginLeft:4}}>Qualified → <strong style={{color:'#60a5fa'}}>+{QUALIFIED_KPI}</strong> · Closed → <strong style={{color:'#2ECC71'}}>+{CONVERTED_KPI}</strong></span>
          <div style={{marginLeft:'auto',fontSize:12,color:T.textDim,display:'flex',gap:14}}>
            <span>Leads this month: <strong style={{color:'#60a5fa'}}>{linkedBiz.current?.[QUALIFIED_KPI]||0}</strong></span>
            <span>Active clients: <strong style={{color:'#2ECC71'}}>{linkedBiz.current?.[CONVERTED_KPI]||0}</strong></span>
          </div>
        </div>
      )}

      <div className="kpi-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:24}}>
        {[['Total Pipeline',`AED ${totalPipeline.toLocaleString()}`,'#E74C3C'],['Active Leads',leads.filter(l=>l.stage<CLOSED_STAGE).length,'#F5A623'],['Closed Won',`AED ${closedValue.toLocaleString()}`,'#2ECC71'],['Conv. Rate',`${convRate}%`,'#9B59B6']].map(([l,v,c])=>(
          <div key={l} style={{background:T.card,borderRadius:10,padding:16,border:`1px solid ${T.border}`}}>
            <div style={{fontSize:12,color:T.textDim,marginBottom:6}}>{l}</div>
            <div style={{fontSize:20,fontWeight:700,color:c,fontFamily:T.mono}}>{v}</div>
          </div>
        ))}
      </div>

      <div style={card}>
        <svg viewBox="0 0 600 120" style={{width:'100%',height:120,display:'block'}}>
          {STAGES.map((stage,i)=>{
            const count=stageCounts[i];const nextCount=i<STAGES.length-1?stageCounts[i+1]:count*0.3;
            const segW=600/STAGES.length;const x1=i*segW;const midY=60;
            const h1=Math.max((count/maxCount)*80,12);const h2=Math.max((nextCount/maxCount)*80,12);
            const drop=i>0&&stageCounts[i-1]>0?Math.round(((stageCounts[i-1]-count)/stageCounts[i-1])*100):null;
            const points=`${x1},${midY-h1/2} ${x1+segW},${midY-h2/2} ${x1+segW},${midY+h2/2} ${x1},${midY+h1/2}`;
            return (
              <g key={stage}>
                <defs><linearGradient id={`sg${i}`} x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={STAGE_COLORS[i]} stopOpacity="0.85"/><stop offset="100%" stopColor={STAGE_COLORS[Math.min(i+1,STAGE_COLORS.length-1)]} stopOpacity="0.75"/></linearGradient></defs>
                <polygon points={points} fill={`url(#sg${i})`}/>
                <text x={x1+segW*0.3} y={midY-h1/2-8} textAnchor="middle" fill={T.textDim} fontSize="10">{stage}</text>
                <text x={x1+segW*0.3} y={midY+5} textAnchor="middle" fill="white" fontSize="15" fontWeight="bold">{count}</text>
                {drop!==null&&<text x={x1+4} y={midY-h1/2-8} textAnchor="middle" fill={drop>30?'#E74C3C':'#F5A623'} fontSize="10" fontWeight="bold">-{drop}%</text>}
              </g>
            );
          })}
        </svg>
      </div>

      <div style={card}>
        <div style={{fontSize:14,fontWeight:600,color:T.textMid,marginBottom:14}}>
          Add Lead to <span style={{color:linkedBiz?.color||'#F5A623'}}>{activeFunnel?.name||'Pipeline'}</span>
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <input placeholder="Lead name / company" value={newLead.name} onChange={e=>setNewLead(p=>({...p,name:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&addLead()} style={{...inp,flex:2,minWidth:150}}/>
          <input type="number" placeholder="Value AED" value={newLead.value} onChange={e=>setNewLead(p=>({...p,value:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&addLead()} style={{...inp,flex:1,minWidth:100}}/>
          <button onClick={addLead} style={btn(linkedBiz?.color||'#E74C3C')}>Add Lead</button>
        </div>
      </div>

      {loadingLeads ? (
        <div style={{textAlign:'center',color:T.textDim,padding:40,fontFamily:T.mono,letterSpacing:2}}>LOADING LEADS...</div>
      ) : leads.length === 0 ? (
        <div style={{textAlign:'center',color:T.textFaint,padding:40,fontSize:14,border:`1px dashed ${T.border}`,borderRadius:12}}>No leads yet — add your first one above ☝️</div>
      ) : (
        <div style={{display:'grid',gap:8}}>
          {[...leads].sort((a,b)=>b.createdAt-a.createdAt).map(lead=>(
            <div key={lead.id} style={{background:T.card,borderRadius:10,padding:'14px 18px',display:'flex',alignItems:'center',gap:14,border:`1px solid ${lead.stage===CLOSED_STAGE?'#2ECC7130':lead.stage>=QUALIFIED_STAGE?STAGE_COLORS[lead.stage]+'30':T.border}`,opacity:lead.stage===CLOSED_STAGE?0.75:1,transition:'all 0.2s'}}>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:600,color:T.text}}>{lead.name}</div>
                <div style={{fontSize:12,color:T.textDim,marginTop:3,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                  <span>AED {(lead.value||0).toLocaleString()}</span>
                  {lead.qualifiedCounted&&<span style={{fontSize:10,color:'#60a5fa',background:'#60a5fa15',padding:'1px 7px',borderRadius:4,border:'1px solid #60a5fa30'}}>✓ Lead counted</span>}
                  {lead.convertedCounted&&<span style={{fontSize:10,color:'#2ECC71',background:'#2ECC7115',padding:'1px 7px',borderRadius:4,border:'1px solid #2ECC7130'}}>✓ Client counted</span>}
                </div>
              </div>
              <div style={{background:`${STAGE_COLORS[lead.stage]}20`,color:STAGE_COLORS[lead.stage],fontSize:12,padding:'4px 12px',borderRadius:20,fontWeight:600,border:`1px solid ${STAGE_COLORS[lead.stage]}40`,minWidth:80,textAlign:'center'}}>{STAGES[lead.stage]}</div>
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>moveLead(lead.id,-1)} disabled={lead.stage===0} style={{background:T.bg,border:`1px solid ${T.border}`,color:lead.stage===0?T.textFaint:T.textDim,borderRadius:6,padding:'5px 10px',cursor:lead.stage===0?'default':'pointer',fontSize:13}}>←</button>
                <button onClick={()=>moveLead(lead.id,1)} disabled={lead.stage===CLOSED_STAGE} style={{background:lead.stage===CLOSED_STAGE-1?'#2ECC7115':T.bg,border:`1px solid ${lead.stage===CLOSED_STAGE-1?'#2ECC71':T.border}`,color:lead.stage===CLOSED_STAGE?T.textFaint:lead.stage===CLOSED_STAGE-1?'#2ECC71':T.textDim,borderRadius:6,padding:'5px 10px',cursor:lead.stage===CLOSED_STAGE?'default':'pointer',fontSize:13}}>→</button>
                <button onClick={()=>removeLead(lead.id)} style={{background:'transparent',border:'none',color:T.textFaint,cursor:'pointer',fontSize:16,padding:'4px 6px'}}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── YEAR VIEW ───────────────────────────────────────────────
// ─── CONSISTENCY VIEW ────────────────────────────────────────
function hexAlpha(pct) {
  const v = Math.round(Math.max(0,Math.min(100,pct))/100*255);
  return v.toString(16).padStart(2,'0');
}

function buildYearGrid(year) {
  const start = new Date(year,0,1);
  const end = new Date(year,11,31);
  const gridStart = new Date(start);
  gridStart.setDate(gridStart.getDate()-gridStart.getDay());
  const weeks = [];
  let cur = new Date(gridStart);
  while (cur <= end) {
    const week = [];
    for (let d=0; d<7; d++) { week.push(new Date(cur)); cur.setDate(cur.getDate()+1); }
    weeks.push(week);
  }
  return { weeks, start, end };
}

function ConsistencyHeatmap({biz,dailyCommitments}) {
  const year = new Date().getFullYear();
  const { weeks, start, end } = buildYearGrid(year);
  // Hover tooltips (title=) don't work on touch — tap-to-select gives the same
  // info via a readout below the grid, which works for both mouse and touch.
  const [selected, setSelected] = React.useState(null);
  return (
    <div>
      <div style={{display:'flex',gap:3,overflowX:'auto',paddingBottom:4}}>
        {weeks.map((week,wi)=>(
          <div key={wi} style={{display:'flex',flexDirection:'column',gap:3}}>
            {week.map((date,di)=>{
              if (date<start||date>end) return <div key={di} className="heatmap-cell" style={{width:11,height:11}}/>;
              const key = `${biz.id}_${getLocalDateKey(date)}`;
              const entry = dailyCommitments[key];
              const hasData = entry && entry.totalCount>0;
              const ratio = hasData ? entry.completedCount/entry.totalCount : null;
              const bg = hasData ? `${biz.color}${hexAlpha(15+ratio*85)}` : T.border;
              const isSelected = selected && getLocalDateKey(selected)===getLocalDateKey(date);
              return (
                <div key={di} className="heatmap-cell" onClick={()=>setSelected(date)}
                  title={`${date.toDateString()}${hasData?` · ${entry.completedCount}/${entry.totalCount}`:''}`}
                  style={{width:11,height:11,borderRadius:2,background:bg,border:isSelected?`1px solid #fff`:`1px solid ${hasData?biz.color+'30':T.border}`,boxShadow:isSelected?'0 0 0 1px #fff':'none',cursor:'pointer'}}/>
              );
            })}
          </div>
        ))}
      </div>
      {selected&&(()=>{
        const entry = dailyCommitments[`${biz.id}_${getLocalDateKey(selected)}`];
        const hasData = entry && entry.totalCount>0;
        return (
          <div style={{marginTop:8,fontSize:12,color:T.textMid}}>
            <strong style={{color:T.text}}>{selected.toDateString()}</strong>
            {hasData ? <span style={{color:biz.color}}> · {entry.completedCount}/{entry.totalCount} completed</span> : <span style={{color:T.textFaint}}> · no data</span>}
          </div>
        );
      })()}
    </div>
  );
}

function TrendStat({label,trend}) {
  const arrow = trend.delta==null?'':trend.delta>0?'▲':trend.delta<0?'▼':'■';
  const deltaColor = trend.delta==null?T.textFaint:trend.delta>0?'#2ECC71':trend.delta<0?'#E74C3C':T.textDim;
  return (
    <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:14,textAlign:'center',minWidth:96}}>
      <div style={{fontSize:9,color:T.textDim,letterSpacing:1,fontFamily:T.mono,marginBottom:6}}>{label}</div>
      <div style={{fontSize:18,fontWeight:700,color:T.text,fontFamily:T.mono}}>{trend.current!=null?`${trend.current}%`:'—'}</div>
      <div style={{fontSize:11,color:deltaColor,marginTop:4,fontFamily:T.mono}}>
        {trend.delta!=null?`${arrow} ${Math.abs(trend.delta)}%`:'no prior data'}
      </div>
    </div>
  );
}

function GoalChainRow({label,color,isLast,children}) {
  return (
    <div style={{display:'flex',gap:12}}>
      <div style={{display:'flex',flexDirection:'column',alignItems:'center',width:14,flexShrink:0}}>
        <div style={{width:10,height:10,borderRadius:'50%',background:color,boxShadow:`0 0 6px ${color}80`,flexShrink:0,marginTop:2}}/>
        {!isLast&&<div style={{flex:1,width:2,background:`${color}40`,marginTop:4}}/>}
      </div>
      <div style={{flex:1,paddingBottom:isLast?0:16,minWidth:0}}>
        <div style={{fontSize:9,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginBottom:4}}>{label}</div>
        {children}
      </div>
    </div>
  );
}

function BigPictureProgress({color,pct}) {
  return (
    <div style={{marginTop:6}}>
      <div style={{height:2,background:T.border,borderRadius:1}}>
        <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:0,opacity:0.7}}/>
      </div>
      <div style={{fontSize:10,color:T.textDim,marginTop:3}}>{pct.toFixed(1)}%</div>
    </div>
  );
}

function GoalChain({biz,getMonthlyTarget,getMonthlyActual,getYearTotal}) {
  const pk = biz.kpis[0];
  const mTarget = getMonthlyTarget(biz.id,pk);
  const mActual = getMonthlyActual(biz.id,pk);
  const mPct = mTarget>0?Math.min((mActual/mTarget)*100,100):0;
  const items = biz.commitmentItems||[];

  // North Star / Milestone progress: snapshot-type KPIs (MRR, Followers) use this
  // month's actual directly; cumulative-type KPIs (Sales Count, Orders) sum the
  // whole year so far, since no single month's value represents the running total.
  const bigPictureActual = getKpiType(pk)==='snapshot' ? mActual : getYearTotal(biz.id,pk);
  const nsTarget = parseGoalNumber(biz.goal);
  const msTarget = parseGoalNumber(biz.milestone);
  const nsPct = nsTarget>0 ? Math.min((bigPictureActual/nsTarget)*100,100) : 0;
  const msPct = msTarget>0 ? Math.min((bigPictureActual/msTarget)*100,100) : 0;

  return (
    <div style={{marginBottom:20}}>
      <GoalChainRow label="NORTH STAR" color={biz.color}>
        <div style={{fontSize:16,fontWeight:700,color:T.text}}>{biz.goal}</div>
        {nsTarget!=null&&<BigPictureProgress color={BAR_FILL} pct={nsPct}/>}
      </GoalChainRow>
      <GoalChainRow label="MILESTONE" color={biz.color}>
        {biz.milestone
          ?<div style={{fontSize:14,fontWeight:600,color:T.textMid}}>{biz.milestone}</div>
          :<div style={{fontSize:13,color:T.textFaint}}>No milestone set</div>}
        {biz.milestone&&msTarget!=null&&<BigPictureProgress color={BAR_FILL} pct={msPct}/>}
      </GoalChainRow>
      <GoalChainRow label={`GOAL (THIS MONTH — ${pk})`} color={biz.color}>
        {mTarget>0?(<>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}>
            <span style={{color:T.textDim}}>Actual: <strong style={{color:'#ffffff'}}>{Number(mActual).toLocaleString()}</strong></span>
            <span style={{color:T.textDim}}>Target: <strong style={{color:T.textMid}}>{Number(mTarget).toLocaleString()}</strong></span>
          </div>
          <div style={{height:3,background:T.border,borderRadius:2}}>
            <div style={{height:'100%',width:`${mPct}%`,background:BAR_FILL,borderRadius:0,boxShadow:`0 0 6px ${BAR_FILL}80`}}/>
          </div>
          <div style={{fontSize:11,color:'#ffffff',marginTop:6,fontWeight:600}}>{mPct.toFixed(1)}% of target</div>
        </>):<div style={{fontSize:13,color:T.textFaint}}>No target set — add in Enter Monthly Data</div>}
      </GoalChainRow>
      <GoalChainRow label="DAILY ACTION(S)" color={biz.color} isLast>
        {items.length>0
          ?<ul style={{margin:0,paddingLeft:18,display:'grid',gap:4}}>{items.map(item=><li key={item} style={{fontSize:13,color:T.textMid}}>{item}</li>)}</ul>
          :<div style={{fontSize:13,color:T.textFaint}}>No daily actions set — add in Manage Businesses</div>}
      </GoalChainRow>
    </div>
  );
}

function ConsistencyView({businesses,dailyCommitments,toggleCommitmentItem,calculateStreak,getPeriodTrends,getMonthlyTarget,getMonthlyActual,getYearTotal}) {
  const todayKey = getLocalDateKey();
  return (
    <div>
      <h1 style={{fontSize:22,fontWeight:700,marginBottom:4}}>Consistency</h1>
      <p style={{color:T.textDim,fontSize:14,marginBottom:24}}>Daily commitments, streaks, and a year-long heatmap per business.</p>
      {businesses.map(biz=>{
        const items = biz.commitmentItems||[];
        const todayEntry = dailyCommitments[`${biz.id}_${todayKey}`];
        const { currentStreak, longestStreak } = calculateStreak(biz.id);
        const trends = items.length>0 ? getPeriodTrends(biz.id) : null;
        const bizEntries = Object.values(dailyCommitments).filter(d=>d.businessId===biz.id&&d.totalCount>0);
        const totalTrackedDays = bizEntries.length;
        const itemStats = items.map(item=>{
          const completedDays = bizEntries.filter(e=>e.checked?.[item]).length;
          const pct = totalTrackedDays>0?Math.round((completedDays/totalTrackedDays)*100):0;
          return { item, completedDays, pct };
        }).sort((a,b)=>b.pct-a.pct);
        return (
          <div key={biz.id} style={{background:T.card,borderRadius:12,padding:20,marginBottom:16,border:`1px solid ${biz.color}15`}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
              {getBizIcon(biz.name,biz.color,20)}
              <span style={{fontWeight:600,fontSize:15,color:biz.color}}>{biz.name}</span>
            </div>

            <GoalChain biz={biz} getMonthlyTarget={getMonthlyTarget} getMonthlyActual={getMonthlyActual} getYearTotal={getYearTotal}/>

            {items.length===0?(
              <p style={{color:T.textFaint,fontSize:13}}>No daily commitments set — add some in Manage Businesses.</p>
            ):(<>
              <div style={{display:'flex',gap:24,flexWrap:'wrap',marginBottom:20}}>
                <div style={{flex:'1 1 220px',minWidth:220}}>
                  <div style={{fontSize:10,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginBottom:8}}>TODAY'S CHECKLIST</div>
                  <div style={{display:'grid',gap:8}}>
                    {items.map(item=>{
                      const checked = !!todayEntry?.checked?.[item];
                      return (
                        <label key={item} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13,color:checked?T.text:T.textMid}}>
                          <input type="checkbox" checked={checked} onChange={()=>toggleCommitmentItem(biz.id,todayKey,item)} style={{width:16,height:16,accentColor:biz.color,cursor:'pointer'}}/>
                          <span style={{textDecoration:checked?'line-through':'none',opacity:checked?0.7:1}}>{item}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div style={{display:'flex',gap:12}}>
                  <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:16,textAlign:'center',minWidth:96}}>
                    <div className="stat-number" style={{fontSize:22,fontWeight:700,color:biz.color,fontFamily:T.mono}}>🔥 {currentStreak}</div>
                    <div style={{fontSize:10,color:T.textDim,marginTop:4}}>Current streak</div>
                  </div>
                  <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:16,textAlign:'center',minWidth:96}}>
                    <div className="stat-number" style={{fontSize:22,fontWeight:700,color:T.text,fontFamily:T.mono}}>{longestStreak}</div>
                    <div style={{fontSize:10,color:T.textDim,marginTop:4}}>Longest streak</div>
                  </div>
                </div>
              </div>

              <div style={{fontSize:10,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginBottom:8}}>TRENDS — COMPLETION RATE</div>
              <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:20}}>
                <TrendStat label="WoW (7d)" trend={trends.wow}/>
                <TrendStat label="MoM (30d)" trend={trends.mom}/>
                <TrendStat label="YoY (365d)" trend={trends.yoy}/>
              </div>

              <div style={{fontSize:10,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginBottom:8}}>{new Date().getFullYear()} ACTIVITY</div>
              <ConsistencyHeatmap biz={biz} dailyCommitments={dailyCommitments}/>

              <div style={{fontSize:10,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginTop:20,marginBottom:8}}>ITEM BREAKDOWN — {totalTrackedDays} TRACKED DAY{totalTrackedDays!==1?'S':''}</div>
              {totalTrackedDays===0?(
                <p style={{color:T.textFaint,fontSize:12}}>No tracked days yet.</p>
              ):(
                <div style={{display:'grid',gap:8}}>
                  {itemStats.map(({item,completedDays,pct})=>(
                    <div key={item}>
                      <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:T.textMid,marginBottom:4}}>
                        <span>{item}</span>
                        <span style={{color:pct>=70?'#2ECC71':pct>=40?'#F5A623':'#E74C3C',fontWeight:600,fontFamily:T.mono}}>{completedDays}/{totalTrackedDays} · {pct}%</span>
                      </div>
                      <div style={{height:4,background:T.bg,borderRadius:2}}>
                        <div style={{height:'100%',width:`${pct}%`,background:pct>=70?'#2ECC71':pct>=40?'#F5A623':'#E74C3C',borderRadius:2,transition:'width 0.4s'}}/>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>)}
          </div>
        );
      })}
    </div>
  );
}

function YearView({businesses,monthlyData}) {
  const [viewYear,setViewYear] = useState(new Date().getFullYear());
  const currentMonth=new Date().getMonth();
  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:24}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:700,marginBottom:4}}>Year Overview</h1>
          <p style={{color:T.textDim,fontSize:14}}>Month-by-month goal achievement heatmap.</p>
        </div>
        <select value={viewYear} onChange={e=>setViewYear(parseInt(e.target.value))} style={{marginLeft:'auto',width:'auto',flexShrink:0,background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 14px',color:T.textMid,fontSize:13}}>{YEARS.map(y=><option key={y} value={y}>{y}</option>)}</select>
      </div>
      {businesses.map(biz=>{
        const pk=biz.kpis[0];const target=biz.target?.[pk]||1;const mTarget=target/12;
        return (
          <div key={biz.id} style={{background:T.card,borderRadius:12,padding:20,marginBottom:16,border:`1px solid ${biz.color}15`}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
              <span style={{fontSize:20}}>{biz.emoji}</span>
              <span style={{fontWeight:600,fontSize:15,color:biz.color}}>{biz.name}</span>
              <span style={{fontSize:12,color:T.textDim}}>— {pk}</span>
            </div>
            <div className="year-grid" style={{display:'grid',gridTemplateColumns:'repeat(12,1fr)',gap:8}}>
              {MONTHS.map((m,i)=>{
                const val=monthlyData[`${biz.id}_${viewYear}_${i+1}`]?.[pk];
                const isFuture=viewYear===new Date().getFullYear()&&i>currentMonth;
                const hitRate=val!==undefined?val/mTarget:null;
                const bg=isFuture?T.bg:val===undefined?T.bg:hitRate>=1?'#0a2a1a':hitRate>=0.7?'#1a1500':'#1a0808';
                const textColor=isFuture?T.textFaint:val===undefined?T.textFaint:hitRate>=1?'#2ECC71':hitRate>=0.7?'#F5A623':'#E74C3C';
                const lbl=isFuture?'—':val===undefined?'?':hitRate>=1?'✓':'✗';
                return (
                  <div key={m} style={{background:bg,borderRadius:8,padding:'10px 6px',textAlign:'center',border:`1px solid ${val!==undefined&&!isFuture?textColor+'20':T.border}`}}>
                    <div style={{fontSize:10,color:T.textDim,marginBottom:4}}>{m}</div>
                    <div style={{fontSize:16,fontWeight:700,color:textColor}}>{lbl}</div>
                    {val!==undefined&&<div style={{fontSize:10,color:textColor,marginTop:3}}>{Number(val).toLocaleString()}</div>}
                  </div>
                );
              })}
            </div>
            <div style={{display:'flex',gap:16,marginTop:12,fontSize:12}}>
              <span style={{color:'#2ECC71'}}>✓ Hit goal</span>
              <span style={{color:'#F5A623'}}>~ Partial</span>
              <span style={{color:'#E74C3C'}}>✗ Missed</span>
              <span style={{color:T.textFaint}}>— Future</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── SIMULATION VIEW ─────────────────────────────────────────
function SimulationView({businesses,monthlyData,getKpiValue}) {
  const [simTab,setSimTab] = useState('overview');
  const [headcount,setHeadcount] = useState([]);
  const [showAddRole,setShowAddRole] = useState(false);
  const [newRole,setNewRole] = useState({role:'',count:1,salary:0,bizId:''});
  const [tools,setTools] = useState(500);
  const [bizExtras,setBizExtras] = useState({});

  const cm=new Date().getMonth()+1,cy=new Date().getFullYear();

  const getBizRevenue=(biz)=>{
    const rKpis=['MRR AED','Revenue AED'];
    for(const kpi of rKpis){if(biz.kpis.includes(kpi)){for(let m=cm;m>=1;m--){const v=monthlyData[`${biz.id}_${cy}_${m}`]?.[kpi];if(v!==undefined&&v>0)return v;}return biz.current?.[kpi]||0;}}
    return 0;
  };

  const getExtra=(bizId,field)=>bizExtras[bizId]?.[field]??0;
  const setExtra=(bizId,field,val)=>setBizExtras(prev=>({...prev,[bizId]:{...(prev[bizId]||{}),[field]:parseFloat(val)||0}}));

  const totalRevenue=businesses.reduce((s,b)=>s+getBizRevenue(b),0);
  const totalAdSpend=businesses.reduce((s,b)=>s+getExtra(b.id,'adSpend'),0);
  const totalCOGS=businesses.reduce((s,b)=>s+getExtra(b.id,'cogs'),0);
  const totalPayroll=headcount.reduce((s,h)=>s+(h.count*h.salary),0);
  const totalCosts=totalPayroll+totalAdSpend+totalCOGS+tools;
  const netProfit=totalRevenue-totalCosts;
  const margin=totalRevenue>0?(netProfit/totalRevenue)*100:0;
  const payrollPct=totalRevenue>0?(totalPayroll/totalRevenue)*100:0;
  const totalHC=headcount.reduce((s,h)=>s+h.count,0);
  const TARGET=36700;

  const fmt=(n)=>'AED '+Math.round(n||0).toLocaleString();
  const pct=(n)=>Math.round(n||0)+'%';

  const addRole=()=>{ if(!newRole.role)return; setHeadcount(prev=>[...prev,{...newRole,id:Date.now()}]); setNewRole({role:'',count:1,salary:0,bizId:''}); setShowAddRole(false); };

  const waterfallData=[
    {name:'Revenue',value:totalRevenue,color:'#2ECC71'},
    {name:'Payroll',value:-totalPayroll,color:'#E74C3C'},
    {name:'Ad Spend',value:-totalAdSpend,color:'#F5A623'},
    {name:'COGS',value:-totalCOGS,color:'#E67E22'},
    {name:'Tools',value:-tools,color:T.textDim},
    {name:'Net Profit',value:netProfit,color:netProfit>=0?'#9B59B6':'#E74C3C'},
  ];
  const maxWF=Math.max(...waterfallData.map(d=>Math.abs(d.value)),1);
  const projData=Array.from({length:6},(_,i)=>({month:['Now','M+1','M+2','M+3','M+4','M+5'][i],revenue:Math.round(totalRevenue*(1+i*0.08)),costs:Math.round(totalCosts*(1+i*0.04)),profit:Math.round(totalRevenue*(1+i*0.08)-totalCosts*(1+i*0.04))}));

  const scenarios=[
    {label:'Scenario A',sub:'3×12,000',revenue:36000,payroll:16500,adSpend:0,cogs:0,color:'#9B59B6',badge:'Best Margin'},
    {label:'Scenario B ⭐',sub:'4×9,000',revenue:36000,payroll:19500,adSpend:0,cogs:0,color:'#3498DB',badge:'Recommended'},
    {label:'Scenario C',sub:'5×7,500',revenue:37500,payroll:23000,adSpend:0,cogs:0,color:'#2ECC71',badge:'Most Resilient'},
    {label:'B + Edemyx',sub:'4 Agency + Digital',revenue:41000,payroll:20500,adSpend:1000,cogs:200,color:'#F5A623',badge:'High Upside'},
    {label:'Conservative',sub:'2×9,000',revenue:18000,payroll:11500,adSpend:0,cogs:0,color:'#E67E22',badge:'Low Risk'},
    {label:'Full Scale',sub:'6 Clients + All',revenue:65000,payroll:32000,adSpend:3500,cogs:1500,color:'#E91E63',badge:'12-Month Goal'},
  ];

  const tabBtn=(t)=>({padding:'8px 18px',background:simTab===t?'#F5A62320':T.card,border:simTab===t?'1px solid #F5A62360':`1px solid ${T.border}`,borderBottom:'none',color:simTab===t?'#F5A623':T.textDim,borderRadius:'8px 8px 0 0',cursor:'pointer',fontSize:13,fontWeight:600});
  const selStyle={background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 12px',color:T.textMid,fontSize:13};

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:700,marginBottom:4}}>Financial Decision Dashboard</h1>
          <p style={{color:T.textDim,fontSize:14}}>Live P&L · Unit Economics · Scenario Planning</p>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:11,color:T.textDim,marginBottom:4,fontFamily:T.mono,letterSpacing:2}}>TARGET</div>
          <div style={{fontSize:22,fontWeight:800,color:totalRevenue>=TARGET?'#2ECC71':'#F5A623',fontFamily:T.mono}}>AED {TARGET.toLocaleString()}/mo</div>
          <div style={{fontSize:12,color:totalRevenue>=TARGET?'#2ECC71':T.textDim,marginTop:3}}>{totalRevenue>=TARGET?'🎯 TARGET HIT!':`Gap: AED ${(TARGET-totalRevenue).toLocaleString()}`}</div>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12,marginBottom:24}}>
        {[
          {label:'Total Revenue',val:fmt(totalRevenue),color:'#2ECC71',sub:`${businesses.filter(b=>getBizRevenue(b)>0).length} streams`},
          {label:'Total Costs',val:fmt(totalCosts),color:'#E74C3C',sub:`Payroll: ${fmt(totalPayroll)}`},
          {label:'Net Profit',val:fmt(netProfit),color:netProfit>=0?'#9B59B6':'#E74C3C',sub:netProfit<0?'In the red':'Take-home'},
          {label:'Margin',val:pct(margin),color:margin>40?'#2ECC71':margin>20?'#F5A623':'#E74C3C',sub:margin>40?'Excellent':margin>20?'Acceptable':'Low'},
          {label:'Payroll %',val:pct(payrollPct),color:payrollPct<50?'#2ECC71':payrollPct<60?'#F5A623':'#E74C3C',sub:payrollPct>60?'Too high':payrollPct>50?'Watch':'Healthy'},
          {label:'Headcount',val:totalHC,color:'#3498DB',sub:'Session only'},
        ].map(k=>(
          <div key={k.label} style={{background:T.card,borderRadius:12,padding:'16px 18px',border:`1px solid ${k.color}20`}}>
            <div style={{fontSize:20,fontWeight:800,color:k.color,fontFamily:T.mono}}>{k.val}</div>
            <div style={{fontSize:13,color:T.textMid,marginTop:4}}>{k.label}</div>
            <div style={{fontSize:11,color:T.textDim,marginTop:2}}>{k.sub}</div>
          </div>
        ))}
      </div>
      <div style={{display:'flex',gap:4,borderBottom:`1px solid ${T.border}`,marginBottom:24}}>
        {['overview','projects','headcount','unit economics','scenarios'].map(t=>(
          <button key={t} onClick={()=>setSimTab(t)} style={tabBtn(t)}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>
        ))}
      </div>

      {simTab==='overview'&&(<div>
        <div style={card}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:20}}>P&L Waterfall</div>
          <div style={{display:'flex',gap:8,alignItems:'flex-end',height:180,marginBottom:12}}>
            {waterfallData.map((d,i)=>{const h=Math.max((Math.abs(d.value)/maxWF)*150,4);return(
              <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
                <div style={{fontSize:11,color:d.color,fontWeight:700,textAlign:'center',fontFamily:T.mono}}>{Math.abs(d.value)>=1000?(d.value/1000).toFixed(1)+'K':d.value}</div>
                <div style={{width:'100%',background:d.color,height:`${h}px`,borderRadius:'4px 4px 0 0',opacity:0.85}}/>
                <div style={{fontSize:11,color:T.textDim,textAlign:'center'}}>{d.name}</div>
              </div>
            );})}
          </div>
        </div>
        <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:20}}>
          <div style={card}>
            <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Revenue by Stream</div>
            {businesses.filter(b=>getBizRevenue(b)>0).length===0?<p style={{color:T.textDim,fontSize:14}}>No revenue data yet</p>:(()=>{
              const total=businesses.reduce((s,b)=>s+getBizRevenue(b),0);let cum=0;
              const slices=businesses.filter(b=>getBizRevenue(b)>0).map(b=>{const val=getBizRevenue(b);const angle=(val/total)*360;const s={name:b.name,val,color:b.color,angle,startAngle:cum};cum+=angle;return s;});
              const p2xy=(angle,r)=>({x:120+r*Math.cos((angle-90)*Math.PI/180),y:120+r*Math.sin((angle-90)*Math.PI/180)});
              return <div style={{display:'flex',gap:16,alignItems:'center'}}>
                <svg viewBox="0 0 240 240" style={{width:160,height:160,flexShrink:0}}>
                  {slices.map((s,i)=>{const st=p2xy(s.startAngle,90);const en=p2xy(s.startAngle+s.angle,90);const lg=s.angle>180?1:0;return <path key={i} d={`M120,120 L${st.x},${st.y} A90,90 0 ${lg},1 ${en.x},${en.y} Z`} fill={s.color} stroke={T.card} strokeWidth="2"/>;  })}
                  <circle cx={120} cy={120} r={48} fill={T.card}/>
                </svg>
                <div style={{display:'grid',gap:8}}>{slices.map(s=><div key={s.name} style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}><div style={{width:10,height:10,borderRadius:2,background:s.color,flexShrink:0}}/><span style={{color:T.textMid}}>{s.name}</span><span style={{color:s.color,fontWeight:700,marginLeft:'auto'}}>{fmt(s.val)}</span></div>)}</div>
              </div>;
            })()}
          </div>
          <div style={card}>
            <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Cost Breakdown</div>
            {(()=>{
              const costData=[{name:'Payroll',val:totalPayroll,color:'#E74C3C'},{name:'Ad Spend',val:totalAdSpend,color:'#F5A623'},{name:'COGS',val:totalCOGS,color:'#E67E22'},{name:'Tools',val:tools,color:T.textDim}].filter(d=>d.val>0);
              if(!costData.length)return <p style={{color:T.textDim,fontSize:14}}>Add costs in Projects & Headcount tabs</p>;
              const total=costData.reduce((s,d)=>s+d.val,0);let cum=0;
              const slices=costData.map(d=>{const angle=(d.val/total)*360;const s={...d,angle,startAngle:cum};cum+=angle;return s;});
              const p2xy=(angle,r)=>({x:120+r*Math.cos((angle-90)*Math.PI/180),y:120+r*Math.sin((angle-90)*Math.PI/180)});
              return <div style={{display:'flex',gap:16,alignItems:'center'}}>
                <svg viewBox="0 0 240 240" style={{width:160,height:160,flexShrink:0}}>
                  {slices.map((s,i)=>{const st=p2xy(s.startAngle,90);const en=p2xy(s.startAngle+s.angle,90);const lg=s.angle>180?1:0;return <path key={i} d={`M120,120 L${st.x},${st.y} A90,90 0 ${lg},1 ${en.x},${en.y} Z`} fill={s.color} stroke={T.card} strokeWidth="2"/>;  })}
                  <circle cx={120} cy={120} r={48} fill={T.card}/>
                </svg>
                <div style={{display:'grid',gap:8}}>{slices.map(s=><div key={s.name} style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}><div style={{width:10,height:10,borderRadius:2,background:s.color,flexShrink:0}}/><span style={{color:T.textMid}}>{s.name}</span><span style={{color:s.color,fontWeight:700,marginLeft:'auto'}}>{fmt(s.val)}</span></div>)}</div>
              </div>;
            })()}
          </div>
        </div>
        <div style={card}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>6-Month Projection</div>
          <p style={{color:T.textDim,fontSize:12,marginBottom:16}}>8% monthly revenue growth, 4% cost growth assumption</p>
          <div style={{display:'flex',gap:6,alignItems:'flex-end',height:140,marginBottom:8}}>
            {projData.map((d,i)=>{const mxV=Math.max(...projData.map(x=>x.revenue),1);return(
              <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                <div style={{display:'flex',gap:2,alignItems:'flex-end',width:'100%'}}>
                  <div style={{flex:1,background:'#2ECC71',height:`${Math.max((d.revenue/mxV)*110,3)}px`,borderRadius:'2px 2px 0 0',opacity:0.8}}/>
                  <div style={{flex:1,background:'#E74C3C',height:`${Math.max((d.costs/mxV)*110,3)}px`,borderRadius:'2px 2px 0 0',opacity:0.8}}/>
                  <div style={{flex:1,background:d.profit>=0?'#9B59B6':'#E74C3C',height:`${Math.max((Math.abs(d.profit)/mxV)*110,3)}px`,borderRadius:'2px 2px 0 0',opacity:0.8}}/>
                </div>
                <div style={{fontSize:10,color:T.textDim}}>{d.month}</div>
              </div>
            );})}
          </div>
          <div style={{display:'flex',gap:16,fontSize:12}}>{[['Revenue','#2ECC71'],['Costs','#E74C3C'],['Profit','#9B59B6']].map(([l,c])=><div key={l} style={{display:'flex',alignItems:'center',gap:6}}><div style={{width:10,height:10,borderRadius:2,background:c}}/><span style={{color:T.textDim}}>{l}</span></div>)}</div>
        </div>
        <div style={card}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Full P&L Statement</div>
          {[
            {label:'Gross Revenue',val:totalRevenue,color:'#2ECC71'},
            {label:'− Payroll',val:totalPayroll,color:'#E74C3C'},
            {label:'− Ad Spend',val:totalAdSpend,color:'#F5A623'},
            {label:'− COGS',val:totalCOGS,color:'#E67E22'},
            {label:'− Tools & Software',val:tools,color:T.textDim},
            {label:'= NET PROFIT',val:netProfit,color:netProfit>=0?'#9B59B6':'#E74C3C',bold:true},
          ].map((row,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:14,padding:'11px 0',borderBottom:i<5?`1px solid ${T.border}`:'none'}}>
              <div style={{width:200,fontSize:row.bold?15:14,fontWeight:row.bold?800:400,color:row.bold?T.text:T.textDim}}>{row.label}</div>
              <div style={{width:150,fontSize:row.bold?18:15,fontWeight:700,color:row.color,textAlign:'right',fontFamily:T.mono}}>{fmt(row.val)}</div>
              <div style={{flex:1,height:4,background:T.bg,borderRadius:2}}><div style={{height:'100%',width:`${totalRevenue>0?Math.min((Math.abs(row.val)/totalRevenue)*100,100):0}%`,background:row.color,borderRadius:2}}/></div>
              <div style={{width:44,fontSize:12,color:T.textDim,textAlign:'right'}}>{totalRevenue>0?Math.round(Math.abs(row.val)/totalRevenue*100)+'%':'—'}</div>
            </div>
          ))}
        </div>
      </div>)}

      {simTab==='projects'&&(<div>
        <div style={{fontSize:16,fontWeight:600,marginBottom:20}}>Revenue Streams — Live Data</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:20,marginBottom:20}}>
          {businesses.map(b=>{
            const revenue=getBizRevenue(b);const adSpend=getExtra(b.id,'adSpend');const cogs=getExtra(b.id,'cogs');
            const bizPayroll=headcount.filter(h=>h.bizId===b.id).reduce((s,h)=>s+h.count*h.salary,0);
            const totalCost=adSpend+cogs+bizPayroll;const profit=revenue-totalCost;
            const mg=revenue>0?(profit/revenue)*100:0;const roas=adSpend>0?(revenue/adSpend):0;
            return (
              <div key={b.id} style={{background:T.card,borderRadius:12,padding:20,border:`1px solid ${b.color}30`}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
                  <span style={{fontSize:22}}>{b.emoji}</span>
                  <div style={{flex:1}}><div style={{fontWeight:700,fontSize:15,color:b.color}}>{b.name}</div><div style={{fontSize:12,color:T.textDim,marginTop:2}}>{b.momentum}</div></div>
                  <div style={{fontSize:12,color:mg>40?'#2ECC71':mg>0?'#F5A623':'#E74C3C',background:`${mg>40?'#2ECC71':mg>0?'#F5A623':'#E74C3C'}15`,padding:'3px 10px',borderRadius:20,fontWeight:600}}>{Math.round(mg)}% margin</div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
                  <div><div style={{fontSize:11,color:T.textDim,marginBottom:4}}>Revenue (from data)</div><div style={{fontSize:18,fontWeight:700,color:'#2ECC71',fontFamily:T.mono}}>{fmt(revenue)}</div></div>
                  <div><div style={{fontSize:11,color:T.textDim,marginBottom:4}}>Net Profit</div><div style={{fontSize:18,fontWeight:700,color:profit>=0?'#9B59B6':'#E74C3C',fontFamily:T.mono}}>{fmt(profit)}</div></div>
                </div>
                <div style={{display:'grid',gap:10}}>
                  {[['Ad Spend/mo (AED)','adSpend'],['COGS/mo (AED)','cogs']].map(([lbl,field])=>(
                    <div key={field} style={{display:'flex',alignItems:'center',gap:10}}>
                      <label style={{fontSize:13,color:T.textDim,flex:1}}>{lbl}</label>
                      <input type="number" value={getExtra(b.id,field)||''} onChange={e=>setExtra(b.id,field,e.target.value)} placeholder="0" style={{...inp,width:110,textAlign:'right'}}/>
                    </div>
                  ))}
                </div>
                {adSpend>0&&<div style={{marginTop:12,padding:'8px 12px',background:roas>3?'#0a2a1a':'#1a1000',border:`1px solid ${roas>3?'#2ECC71':'#F5A623'}25`,borderRadius:8,fontSize:13}}><span style={{color:T.textDim}}>ROAS: </span><strong style={{color:roas>3?'#2ECC71':'#F5A623'}}>{roas.toFixed(2)}x</strong><span style={{color:T.textDim,marginLeft:8}}>{roas>3?'✓ Healthy':'⚠ Below 3x'}</span></div>}
              </div>
            );
          })}
        </div>
        <div style={{background:T.card,borderRadius:12,padding:20,border:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontSize:14,color:T.textMid}}>🛠 Tools & Software (shared monthly cost)</span>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:13,color:T.textDim}}>AED</span>
            <input type="number" value={tools} onChange={e=>setTools(parseFloat(e.target.value)||0)} style={{...inp,width:110,textAlign:'right'}}/>
          </div>
        </div>
      </div>)}

      {simTab==='headcount'&&(<div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <div>
            <div style={{fontSize:16,fontWeight:600}}>Team & Payroll</div>
            <div style={{fontSize:12,color:T.textDim,marginTop:4}}>Session only — not saved between visits</div>
          </div>
          <button onClick={()=>setShowAddRole(!showAddRole)} style={{...btn('#2ECC71'),color:'#000'}}>+ Add Role</button>
        </div>
        <p style={{color:T.textDim,fontSize:14,marginBottom:20}}>Total: <strong style={{color:payrollPct>60?'#E74C3C':'#2ECC71'}}>{fmt(totalPayroll)} ({pct(payrollPct)} of revenue)</strong> · {totalHC} people</p>
        {showAddRole&&(
          <div style={{...card,border:`1px solid #2ECC7130`}}>
            <div style={{fontSize:14,fontWeight:600,color:'#2ECC71',marginBottom:14}}>New Team Member</div>
            <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:12}}>
              {[['Role','role','text'],['Count','count','number'],['Salary/person','salary','number']].map(([lbl,field,type])=>(
                <div key={field}><label style={label}>{lbl.toUpperCase()}</label><input type={type} value={newRole[field]} onChange={e=>setNewRole(p=>({...p,[field]:type==='number'?parseFloat(e.target.value)||0:e.target.value}))} style={{...inp}}/></div>
              ))}
              <div><label style={label}>ASSIGN TO</label><select value={newRole.bizId} onChange={e=>setNewRole(p=>({...p,bizId:e.target.value}))} style={{...inp}}><option value="">Shared</option>{businesses.map(b=><option key={b.id} value={b.id}>{b.emoji} {b.name}</option>)}</select></div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:16}}>
              <button onClick={addRole} style={btn('#2ECC71')}>Add</button>
              <button onClick={()=>setShowAddRole(false)} style={{padding:'10px 16px',background:'transparent',color:T.textDim,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer',fontSize:14}}>Cancel</button>
            </div>
          </div>
        )}
        <div style={{background:T.card,borderRadius:12,border:`1px solid ${T.border}`,overflow:'hidden',marginBottom:20}}>
          <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{background:T.bg}}>{['Role','Business','Count','Salary/person','Total','% Rev',''].map(h=><th key={h} style={{padding:'12px 14px',textAlign:'left',fontSize:11,color:T.textDim,letterSpacing:1,fontWeight:600}}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {headcount.length===0&&<tr><td colSpan={7} style={{padding:24,textAlign:'center',color:T.textFaint,fontSize:14}}>No team members added yet</td></tr>}
              {headcount.map(h=>{
                const biz=businesses.find(b=>b.id===h.bizId);const rowCost=h.count*h.salary;const rowPct=totalRevenue>0?(rowCost/totalRevenue)*100:0;
                return <tr key={h.id} style={{borderBottom:`1px solid ${T.border}`}}>
                  <td style={{padding:'11px 14px',fontSize:14,color:T.text}}>{h.role}</td>
                  <td style={{padding:'11px 14px'}}>{biz?<span style={{fontSize:12,color:biz.color,background:`${biz.color}15`,padding:'2px 8px',borderRadius:6}}>{biz.name}</span>:<span style={{color:T.textDim,fontSize:12}}>Shared</span>}</td>
                  <td style={{padding:'11px 14px'}}><input type="number" value={h.count} onChange={e=>setHeadcount(prev=>prev.map(x=>x.id===h.id?{...x,count:parseFloat(e.target.value)||0}:x))} style={{...inp,width:60}}/></td>
                  <td style={{padding:'11px 14px'}}><input type="number" value={h.salary} onChange={e=>setHeadcount(prev=>prev.map(x=>x.id===h.id?{...x,salary:parseFloat(e.target.value)||0}:x))} style={{...inp,width:110}}/></td>
                  <td style={{padding:'11px 14px',color:'#E74C3C',fontWeight:600,fontSize:14,fontFamily:T.mono}}>{fmt(rowCost)}</td>
                  <td style={{padding:'11px 14px',fontSize:13,color:rowPct>15?'#F5A623':T.textDim}}>{pct(rowPct)}</td>
                  <td style={{padding:'11px 14px'}}><button onClick={()=>setHeadcount(prev=>prev.filter(x=>x.id!==h.id))} style={{background:'transparent',border:'none',color:T.textDim,cursor:'pointer',fontSize:16}}>✕</button></td>
                </tr>;
              })}
              {headcount.length>0&&<tr style={{background:T.bg}}>
                <td colSpan={4} style={{padding:'12px 14px',fontWeight:700,fontSize:14,color:T.text}}>TOTAL PAYROLL</td>
                <td style={{padding:'12px 14px',color:'#E74C3C',fontWeight:800,fontSize:15,fontFamily:T.mono}}>{fmt(totalPayroll)}</td>
                <td style={{padding:'12px 14px',color:payrollPct>60?'#E74C3C':payrollPct>50?'#F5A623':'#2ECC71',fontWeight:700,fontSize:14}}>{pct(payrollPct)}</td>
                <td/>
              </tr>}
            </tbody>
          </table>
          </div>
        </div>
        <div style={{background:T.card,borderRadius:12,padding:24,border:`1px solid ${T.border}`,textAlign:'center'}}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Payroll Health</div>
          <div style={{fontSize:52,fontWeight:900,color:payrollPct<50?'#2ECC71':payrollPct<60?'#F5A623':'#E74C3C',fontFamily:T.mono}}>{pct(payrollPct)}</div>
          <div style={{fontSize:14,color:T.textDim,marginBottom:16}}>of total revenue</div>
          <div style={{display:'flex',gap:8,justifyContent:'center',flexWrap:'wrap'}}>
            {[['< 50%','#2ECC71','Healthy'],['50–60%','#F5A623','Watch'],['> 60%','#E74C3C','Danger']].map(([range,col,lbl])=>(
              <div key={lbl} style={{background:`${col}15`,border:`1px solid ${col}30`,borderRadius:8,padding:'4px 14px',fontSize:12,color:col}}>{range} {lbl}</div>
            ))}
          </div>
        </div>
      </div>)}

      {simTab==='unit economics'&&(<div>
        <div style={{fontSize:16,fontWeight:600,marginBottom:20}}>Unit Economics — Per Business & Overall</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:20,marginBottom:20}}>
          {businesses.map(b=>{
            const revenue=getBizRevenue(b);const adSpend=getExtra(b.id,'adSpend');const cogs=getExtra(b.id,'cogs');
            const bizPayroll=headcount.filter(h=>h.bizId===b.id).reduce((s,h)=>s+h.count*h.salary,0);
            const totalCost=adSpend+cogs+bizPayroll;const profit=revenue-totalCost;
            const mg=revenue>0?(profit/revenue)*100:0;const hc=headcount.filter(h=>h.bizId===b.id).reduce((s,h)=>s+h.count,0);const roas=adSpend>0?(revenue/adSpend):0;
            return (
              <div key={b.id} style={{background:T.card,borderRadius:12,padding:20,border:`2px solid ${b.color}25`}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}><span style={{fontSize:22}}>{b.emoji}</span><div style={{fontWeight:700,fontSize:15,color:b.color}}>{b.name}</div></div>
                <div className="kpi-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                  {[['Revenue',fmt(revenue),b.color],['Total Cost',fmt(totalCost),'#E74C3C'],['Net Profit',fmt(profit),profit>=0?'#2ECC71':'#E74C3C'],['Margin',pct(mg),mg>40?'#2ECC71':mg>0?'#F5A623':'#E74C3C'],['Ad Spend',fmt(adSpend),T.textDim],['COGS',fmt(cogs),T.textDim],['Payroll',fmt(bizPayroll),'#E74C3C'],['Headcount',hc,'#3498DB'],['Rev/Employee',fmt(hc>0?revenue/hc:revenue),'#9B59B6']].map(([lbl,val,col])=>(
                    <div key={lbl} style={{background:T.bg,borderRadius:8,padding:'10px 12px'}}><div style={{fontSize:10,color:T.textDim,marginBottom:3}}>{lbl}</div><div style={{fontSize:14,fontWeight:700,color:col,fontFamily:T.mono}}>{val}</div></div>
                  ))}
                </div>
                {adSpend>0&&<div style={{marginTop:10,padding:'8px 12px',background:roas>3?'#0a2a1a':'#1a1000',border:`1px solid ${roas>3?'#2ECC71':'#F5A623'}25`,borderRadius:8,fontSize:13}}>ROAS: <strong style={{color:roas>3?'#2ECC71':'#F5A623'}}>{roas.toFixed(2)}x</strong><span style={{color:T.textDim,marginLeft:8}}>{roas>3?'✓ Healthy':'⚠ Below 3x'}</span></div>}
              </div>
            );
          })}
        </div>
        <div style={card}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Business-Wide Metrics</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12}}>
            {[['Revenue / Employee',fmt(totalRevenue/Math.max(1,totalHC)),'#9B59B6'],['Profit / Employee',fmt(netProfit/Math.max(1,totalHC)),netProfit>0?'#2ECC71':'#E74C3C'],['Break-even Revenue',fmt(totalCosts),'#F5A623'],['Monthly Burn',fmt(totalCosts),'#E74C3C'],['Gross Margin',totalRevenue>0?pct(((totalRevenue-totalAdSpend-totalCOGS)/totalRevenue)*100):'—','#2ECC71'],['Avg Salary',fmt(totalPayroll/Math.max(1,totalHC)),T.textDim],['Target Gap',fmt(Math.max(0,TARGET-totalRevenue)),totalRevenue>=TARGET?'#2ECC71':'#F5A623'],['Cost per AED Earned',totalRevenue>0?(totalCosts/totalRevenue).toFixed(2):'—',totalRevenue>0&&totalCosts/totalRevenue<0.6?'#2ECC71':'#E74C3C']].map(([lbl,val,col])=>(
              <div key={lbl} style={{background:T.bg,borderRadius:10,padding:14}}>
                <div style={{fontSize:11,color:T.textDim,marginBottom:4}}>{lbl}</div>
                <div style={{fontSize:17,fontWeight:800,color:col,fontFamily:T.mono}}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>)}

      {simTab==='scenarios'&&(<div>
        <div style={{fontSize:16,fontWeight:600,marginBottom:6}}>Scenario Comparison</div>
        <p style={{color:T.textDim,fontSize:14,marginBottom:24}}>Pre-built scenarios vs your live numbers.</p>
        <div style={card}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Live Numbers vs Scenario B</div>
          {[['Revenue',totalRevenue,36000,'#2ECC71',false],['Net Profit',netProfit,16000,'#9B59B6',false],['Margin %',Math.round(margin),44,'#3498DB',false],['Payroll %',Math.round(payrollPct),50,'#F5A623',true]].map(([lbl,current,target,color,low])=>{
            const isGood=low?current<=target:current>=target;
            return (
              <div key={lbl} style={{marginBottom:18}}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:14,marginBottom:6}}>
                  <span style={{color:T.textMid}}>{lbl}</span>
                  <span><span style={{color,fontWeight:700}}>{current>999?fmt(current):current+(lbl.includes('%')?'%':'')}</span><span style={{color:T.textDim}}> / {target>999?fmt(target):target+(lbl.includes('%')?'%':'')}</span><span style={{marginLeft:8}}>{isGood?'✅':'⬆️'}</span></span>
                </div>
                <div style={{height:5,background:T.bg,borderRadius:3}}><div style={{height:'100%',width:`${Math.min((current/Math.max(target,1))*100,100)}%`,background:isGood?color:'#E74C3C',borderRadius:3,transition:'width 0.5s'}}/></div>
              </div>
            );
          })}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:16}}>
          {[...scenarios,{label:'YOUR LIVE',sub:'Current state',revenue:totalRevenue,payroll:totalPayroll,adSpend:totalAdSpend,cogs:totalCOGS,color:'#F5A623',badge:'Live Now'}].map((sc,i)=>{
            const cost=sc.payroll+sc.adSpend+sc.cogs+tools;const profit=sc.revenue-cost;
            const mg=sc.revenue>0?(profit/sc.revenue)*100:0;const pp=sc.revenue>0?(sc.payroll/sc.revenue)*100:0;
            return (
              <div key={i} style={{background:T.card,borderRadius:12,padding:20,border:`1px solid ${sc.color}30`}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14}}>
                  <div><div style={{fontWeight:700,fontSize:15,color:sc.color}}>{sc.label}</div><div style={{fontSize:12,color:T.textDim,marginTop:2}}>{sc.sub}</div></div>
                  <span style={{fontSize:10,color:sc.color,background:`${sc.color}15`,padding:'2px 8px',borderRadius:6,border:`1px solid ${sc.color}30`}}>{sc.badge}</span>
                </div>
                {[['Revenue',fmt(sc.revenue),sc.color],['Payroll',fmt(sc.payroll),'#E74C3C'],['Net Profit',fmt(profit),profit>=0?'#2ECC71':'#E74C3C'],['Margin',pct(mg),mg>40?'#2ECC71':'#F5A623'],['Payroll %',pct(pp),pp<50?'#2ECC71':pp<60?'#F5A623':'#E74C3C']].map(([lbl,val,col])=>(
                  <div key={lbl} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:`1px solid ${T.border}`,fontSize:13}}>
                    <span style={{color:T.textDim}}>{lbl}</span>
                    <span style={{fontWeight:600,color:col,fontFamily:T.mono}}>{val}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>)}
    </div>
  );
}

// ─── CLIENTS VIEW ─────────────────────────────────────────────
function ClientsView({biz, monthlyData, saveMonthlyData, showNotif, setByforceClients, byforceInvoices}) {
  const [clients, setClients] = React.useState([]);
  const [loadingClients, setLoadingClients] = React.useState(true);
  const [showAdd, setShowAdd] = React.useState(false);
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [hoveredId, setHoveredId] = React.useState(null);
  const [editClient, setEditClient] = React.useState(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);

  React.useEffect(() => { if (biz?.id) loadClients(); }, [biz?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadClients() {
    setLoadingClients(true);
    try {
      const snap = await getDocs(collection(db, 'businesses', biz.id, 'clients'));
      const loaded = snap.docs.map(d => ({...d.data(), id: d.id}));
      setClients(loaded);
      if (setByforceClients) setByforceClients(loaded);
    } catch(e) { console.error(e); }
    setLoadingClients(false);
  }

  async function addClient(clientData) {
    try {
      const cm = new Date().getMonth() + 1;
      const cy = new Date().getFullYear();
      const clientId = `client_${Date.now()}`;
      const client = {...clientData, id: clientId, createdAt: Date.now(), createdMonth: cm, createdYear: cy};
      await setDoc(doc(db, 'businesses', biz.id, 'clients', clientId), client);
      const newClients = [...clients, client];
      setClients(newClients);
      if (setByforceClients) setByforceClients(newClients);
      await syncKpis(newClients, cm, cy);
      showNotif(`Client "${clientData.name}" added!`);
      setShowAdd(false);
    } catch(e) { showNotif('Error adding client', 'error'); }
  }

  async function updateClient(clientData) {
    try {
      const updated = {...editClient, ...clientData};
      await updateDoc(doc(db, 'businesses', biz.id, 'clients', editClient.id), clientData);
      const newClients = clients.map(c => c.id === editClient.id ? updated : c);
      setClients(newClients);
      if (setByforceClients) setByforceClients(newClients);
      const cm = new Date().getMonth() + 1, cy = new Date().getFullYear();
      await syncKpis(newClients, cm, cy);
      showNotif(`Client "${clientData.name}" updated!`);
      setEditClient(null);
    } catch(e) { showNotif('Error updating client', 'error'); }
  }

  async function deleteClient(clientId) {
    try {
      const invSnap = await getDocs(collection(db, 'businesses', biz.id, 'clients', clientId, 'invoices'));
      await Promise.all(invSnap.docs.map(d => deleteDoc(d.ref)));
      await deleteDoc(doc(db, 'businesses', biz.id, 'clients', clientId));
      const newClients = clients.filter(c => c.id !== clientId);
      setClients(newClients);
      if (setByforceClients) setByforceClients(newClients);
      const cm = new Date().getMonth() + 1, cy = new Date().getFullYear();
      await syncKpis(newClients, cm, cy);
      showNotif('Client deleted.');
      setDeleteConfirm(null);
    } catch(e) { showNotif('Error deleting client', 'error'); }
  }

  async function syncKpis(currentClients, cm, cy) {
    const hasKpi = (k) => biz.kpis && biz.kpis.includes(k);
    const key = `${biz.id}_${cy}_${cm}`;
    const existing = monthlyData[key] || {};
    const active = currentClients.filter(c => {
      if (c.status !== 'Active') return false;
      const {year:ey,month:em}=parseClientEnd(c);
      if(ey&&(ey<cy||(ey===cy&&em<cm))) return false;
      return true;
    });
    const closedThisMonth = currentClients.filter(c=>{const{year:sy,month:sm}=parseClientStart(c);return sy===cy&&sm===cm;}).length;
    const newLeads = existing['New Leads'] || 0;
    const convRate = newLeads > 0 ? parseFloat(((closedThisMonth / newLeads) * 100).toFixed(1)) : 0;
    const updates = {...existing};
    // MRR is invoice-based — auto-sync in App handles it; skip here to avoid overwrite with stale retainer data
    if (hasKpi('Active Clients')) updates['Active Clients'] = active.length;
    if (hasKpi('Closed Deals')) updates['Closed Deals'] = closedThisMonth;
    if (hasKpi('Conversion Rate')) updates['Conversion Rate'] = convRate;
    await saveMonthlyData(biz.id, cm, cy, updates, existing._targets || {}, existing, true);
  }

  if (!biz) return null;
  if (selectedClient) return <ClientDetailView client={selectedClient} bizId={biz.id} biz={biz} onBack={() => { setSelectedClient(null); loadClients(); }} showNotif={showNotif}/>;

  const now = new Date();
  const cm0 = now.getMonth()+1, cy0 = now.getFullYear();
  const activeCount = clients.filter(c=>{
    if(c.status!=='Active') return false;
    const{year:ey,month:em}=parseClientEnd(c);
    return !(ey&&(ey<cy0||(ey===cy0&&em<cm0)));
  }).length;
  const currentMonthMrr = (byforceInvoices||[]).filter(inv=>{
    if(inv.status!=='Paid') return false;
    const p=parseInvoiceMonth(inv); return p&&p.year===cy0&&p.month===cm0;
  }).reduce((s,inv)=>s+(inv.amount||0),0);
  const statusColor = s => s === 'Active' ? '#2ECC71' : s === 'Paused' ? '#F5A623' : '#E74C3C';

  return (
    <div>
      {showAdd && <AddClientModal biz={biz} onSave={addClient} onClose={() => setShowAdd(false)}/>}
      {editClient && <AddClientModal biz={biz} onSave={updateClient} onClose={() => setEditClient(null)} initialData={editClient} editMode/>}
      {deleteConfirm && (
        <div style={{position:'fixed',inset:0,background:'#000000cc',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:T.card,borderRadius:14,padding:28,width:380,maxWidth:'92vw',border:`1px solid #E74C3C40`}}>
            <h3 style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:10}}>Delete Client?</h3>
            <p style={{fontSize:14,color:T.textDim,marginBottom:20,lineHeight:1.6}}>Are you sure you want to delete this client? This will also delete all their invoices.</p>
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => deleteClient(deleteConfirm)} style={{...btn('#E74C3C'),flex:1,padding:12}}>Delete</button>
              <button onClick={() => setDeleteConfirm(null)} style={{padding:'12px 20px',background:T.bg,color:T.textDim,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer',fontSize:14}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:4}}>
            <span style={{fontSize:28}}>{biz.emoji}</span>
            <h1 style={{fontSize:22,fontWeight:700,color:biz.color}}>Clients — {biz.name}</h1>
          </div>
          <p style={{color:T.textDim,fontSize:14}}>{activeCount} active · {clients.length} total</p>
        </div>
        <button onClick={() => setShowAdd(true)} style={{...btn(biz.color)}}>+ Add Client</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12,marginBottom:24}}>
        {[
          ['Active Clients', activeCount, '#2ECC71'],
          ['Total Clients', clients.length, biz.color],
          ['Paused', clients.filter(c => c.status === 'Paused').length, '#F5A623'],
          ['Churned', clients.filter(c => c.status === 'Churned').length, '#E74C3C'],
          ['Monthly MRR', `AED ${currentMonthMrr.toLocaleString()}`, '#F5A623'],
        ].map(([l, v, c]) => (
          <div key={l} style={{background:T.card,borderRadius:10,padding:'14px 16px',border:`1px solid ${T.border}`}}>
            <div style={{fontSize:11,color:T.textDim,marginBottom:6,letterSpacing:1}}>{l}</div>
            <div style={{fontSize:20,fontWeight:700,color:c,fontFamily:T.mono}}>{v}</div>
          </div>
        ))}
      </div>
      {loadingClients ? (
        <div style={{textAlign:'center',color:T.textDim,padding:60,fontFamily:T.mono,letterSpacing:2}}>LOADING CLIENTS...</div>
      ) : clients.length === 0 ? (
        <div style={{textAlign:'center',color:T.textFaint,padding:60,border:`1px dashed ${T.border}`,borderRadius:12,fontSize:14}}>No clients yet — add your first one above ☝️</div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
          {clients.map(client => {
            const initials = client.name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
            const sc = statusColor(client.status);
            const accent = client.cardColor || sc;
            const isHovered = hoveredId === client.id;
            const isExpired = client.contractEndDate && new Date(client.contractEndDate+'T00:00:00') < new Date() && client.status === 'Active';
            return (
              <div key={client.id}
                onClick={() => setSelectedClient(client)}
                onMouseEnter={() => setHoveredId(client.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{position:'relative',background:`radial-gradient(140% 100% at 0% 0%,${accent}20 0%,${accent}08 35%,rgba(8,13,22,0.75) 65%)`,backdropFilter:'blur(16px)',WebkitBackdropFilter:'blur(16px)',borderRadius:12,padding:20,border:`1px solid ${isHovered ? accent+'50' : accent+'22'}`,cursor:'pointer',transition:'all 0.2s',boxShadow:isHovered?`0 8px 24px ${accent}18`:`0 4px 16px ${accent}08`}}>
                <div style={{position:'absolute',top:10,right:10,display:'flex',gap:4,zIndex:1}}>
                  <button
                    onClick={e => { e.stopPropagation(); setEditClient(client); }}
                    title="Edit client"
                    style={{background:'transparent',border:'none',cursor:'pointer',color:T.textFaint,padding:'3px 5px',borderRadius:4,lineHeight:1,display:'flex',alignItems:'center',transition:'color 0.15s'}}
                    onMouseEnter={e => e.currentTarget.style.color = T.text}
                    onMouseLeave={e => e.currentTarget.style.color = T.textFaint}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.21a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); setDeleteConfirm(client.id); }}
                    title="Delete client"
                    style={{background:'transparent',border:'none',cursor:'pointer',color:'#E74C3C70',padding:'3px 5px',borderRadius:4,lineHeight:1,display:'flex',alignItems:'center',transition:'color 0.15s'}}
                    onMouseEnter={e => e.currentTarget.style.color = '#E74C3C'}
                    onMouseLeave={e => e.currentTarget.style.color = '#E74C3C70'}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zm13-15h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                  </button>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
                  {client.logo
                    ? <img src={client.logo} alt="" style={{width:42,height:42,borderRadius:'50%',objectFit:'cover',border:`1px solid ${accent}35`,flexShrink:0}}/>
                    : <div style={{width:42,height:42,borderRadius:'50%',background:`${accent}18`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,fontWeight:700,color:accent,border:`1px solid ${accent}35`,flexShrink:0}}>{initials}</div>
                  }
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:600,color:T.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{client.name}</div>
                    <div style={{fontSize:12,color:T.textDim,marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{client.company || '—'}</div>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:3,alignItems:'flex-end',flexShrink:0}}>
                    <div style={{background:`${sc}15`,color:sc,fontSize:10,padding:'3px 9px',borderRadius:12,fontWeight:600,border:`1px solid ${sc}30`,whiteSpace:'nowrap'}}>{client.status}</div>
                    {isExpired&&<div style={{background:'#F5A62318',color:'#F5A623',fontSize:9,padding:'2px 8px',borderRadius:10,fontWeight:700,border:'1px solid #F5A62330',whiteSpace:'nowrap'}}>EXPIRED</div>}
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:12,marginBottom:10}}>
                  <div><div style={{color:T.textFaint,marginBottom:2,fontSize:10}}>Retainer/mo</div><div style={{color:'#2ECC71',fontWeight:600,fontFamily:T.mono}}>AED {(client.monthlyRetainer||0).toLocaleString()}</div></div>
                  <div><div style={{color:T.textFaint,marginBottom:2,fontSize:10}}>Contract</div><div style={{color:T.textMid,fontSize:11}}>{fmtContractPeriod(client)}</div></div>
                  <div><div style={{color:T.textFaint,marginBottom:2,fontSize:10}}>Industry</div><div style={{color:T.textMid}}>{client.industry||'—'}</div></div>
                  <div><div style={{color:T.textFaint,marginBottom:2,fontSize:10}}>Website</div><div style={{color:accent,fontSize:11,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{client.website||'—'}</div></div>
                </div>
                {client.services?.filter(Boolean).length > 0 && (
                  <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                    {client.services.filter(Boolean).map((s,i) => (
                      <span key={i} style={{fontSize:10,color:accent,background:`${accent}12`,padding:'2px 8px',borderRadius:10,border:`1px solid ${accent}28`}}>{s}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── ADD CLIENT MODAL ─────────────────────────────────────────
function AddClientModal({biz, onSave, onClose, initialData, editMode}) {
  const INDUSTRIES = ['Technology','Marketing','E-commerce','Education','Healthcare','Finance','Real Estate','Retail','Food & Beverage','Consulting','Other'];
  const [form, setForm] = React.useState(initialData ? {
    name: initialData.name || '',
    company: initialData.company || '',
    industry: initialData.industry || 'Technology',
    monthlyRetainer: initialData.monthlyRetainer ?? '',
    serviceType: initialData.serviceType || '',
    contractStartDate: initialData.contractStartDate || initialData.startDate || '',
    contractEndDate: initialData.contractEndDate || '',
    status: initialData.status || 'Active',
    notes: initialData.notes || '',
    logo: initialData.logo || '',
    website: initialData.website || '',
    cardColor: initialData.cardColor || biz.color || '#E74C3C',
    services: initialData.services || [],
  } : {name:'', company:'', industry:'Technology', monthlyRetainer:'', serviceType:'', contractStartDate:'', contractEndDate:'', status:'Active', notes:'', logo:'', website:'', cardColor:biz.color||'#E74C3C', services:[]});
  const upd = (k, v) => setForm(p => ({...p, [k]: v}));

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => upd('logo', ev.target.result);
    reader.readAsDataURL(file);
  };

  const addService = () => upd('services', [...form.services, '']);
  const updateService = (i, v) => { const s=[...form.services]; s[i]=v; upd('services',s); };
  const removeService = (i) => upd('services', form.services.filter((_,j)=>j!==i));

  const contractDuration = () => {
    if (!form.contractStartDate || !form.contractEndDate) return null;
    const s=new Date(form.contractStartDate+'T00:00:00'), e=new Date(form.contractEndDate+'T00:00:00');
    const months=(e.getFullYear()-s.getFullYear())*12+(e.getMonth()-s.getMonth())+1;
    return months>0?`${months} month${months!==1?'s':''}`:null;
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    onSave({...form, monthlyRetainer: parseFloat(form.monthlyRetainer)||0, services: form.services.filter(Boolean)});
  };

  return (
    <div style={{position:'fixed',inset:0,background:'#000000cc',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:T.card,borderRadius:16,padding:32,width:600,maxWidth:'94vw',maxHeight:'90vh',overflowY:'auto',border:`1px solid ${T.border}`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <h2 style={{fontSize:18,fontWeight:700}}>{editMode ? 'Edit Client' : 'Add Client'} — <span style={{color:biz.color}}>{biz.name}</span></h2>
          <button onClick={onClose} style={{background:'none',border:'none',color:T.textDim,cursor:'pointer',fontSize:20,lineHeight:1}}>✕</button>
        </div>
        <div style={{display:'grid',gap:14}}>
          {/* Name + Company */}
          <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><label style={label}>FULL NAME *</label><input style={{...inp}} value={form.name} onChange={e=>upd('name',e.target.value)} placeholder="e.g. Ahmed Al Rashid"/></div>
            <div><label style={label}>COMPANY NAME</label><input style={{...inp}} value={form.company} onChange={e=>upd('company',e.target.value)} placeholder="e.g. TechCorp LLC"/></div>
          </div>
          {/* Industry + Website */}
          <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><label style={label}>INDUSTRY</label><select style={{...inp}} value={form.industry} onChange={e=>upd('industry',e.target.value)}>{INDUSTRIES.map(i=><option key={i}>{i}</option>)}</select></div>
            <div><label style={label}>WEBSITE URL</label><input style={{...inp}} value={form.website} onChange={e=>upd('website',e.target.value)} placeholder="https://example.com"/></div>
          </div>
          {/* Retainer */}
          <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><label style={label}>MONTHLY RETAINER AED</label><input type="number" style={{...inp}} value={form.monthlyRetainer} onChange={e=>upd('monthlyRetainer',e.target.value)} placeholder="0"/></div>
            <div><label style={label}>STATUS</label><select style={{...inp}} value={form.status} onChange={e=>upd('status',e.target.value)}><option>Active</option><option>Paused</option><option>Churned</option></select></div>
          </div>
          {/* Contract Dates */}
          <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><label style={label}>CONTRACT START DATE</label><input type="date" style={{...inp}} value={form.contractStartDate} onChange={e=>upd('contractStartDate',e.target.value)}/></div>
            <div><label style={label}>CONTRACT END DATE</label><input type="date" style={{...inp}} value={form.contractEndDate} onChange={e=>upd('contractEndDate',e.target.value)}/></div>
          </div>
          {contractDuration()&&<div style={{background:'#0a1a0a',border:'1px solid #2ECC7130',borderRadius:8,padding:'8px 14px',fontSize:13,color:'#2ECC71',fontWeight:600}}>Duration: {contractDuration()}</div>}
          {/* Logo + Card Color */}
          <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:12,alignItems:'flex-start'}}>
            <div>
              <label style={label}>CLIENT LOGO (optional)</label>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                {form.logo
                  ? <img src={form.logo} alt="" style={{width:44,height:44,borderRadius:'50%',objectFit:'cover',border:`1px solid ${T.border}`}}/>
                  : <div style={{width:44,height:44,borderRadius:'50%',background:T.bg,border:`1px dashed ${T.border}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,color:T.textFaint}}>IMG</div>
                }
                <label style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 14px',color:T.textDim,cursor:'pointer',fontSize:13,whiteSpace:'nowrap'}}>
                  Upload Logo
                  <input type="file" accept="image/*" onChange={handleLogoChange} style={{display:'none'}}/>
                </label>
                {form.logo && <button onClick={()=>upd('logo','')} style={{background:'transparent',border:'none',color:'#E74C3C',cursor:'pointer',fontSize:18}}>✕</button>}
              </div>
            </div>
            <div>
              <label style={label}>CARD COLOR</label>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <input type="color" value={form.cardColor} onChange={e=>upd('cardColor',e.target.value)} style={{width:44,height:44,padding:2,background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer'}}/>
                <div style={{width:36,height:36,borderRadius:8,background:form.cardColor,border:`1px solid ${form.cardColor}40`}}/>
              </div>
            </div>
          </div>
          {/* Services */}
          <div>
            <label style={label}>SERVICES</label>
            <div style={{display:'grid',gap:6}}>
              {form.services.map((s,i)=>(
                <div key={i} style={{display:'flex',gap:8}}>
                  <input value={s} onChange={e=>updateService(i,e.target.value)} placeholder={`Service ${i+1}`} style={{...inp,flex:1}}/>
                  <button onClick={()=>removeService(i)} style={{background:'#1a0808',border:'1px solid #E74C3C30',color:'#E74C3C',borderRadius:8,padding:'0 12px',cursor:'pointer',fontSize:16,flexShrink:0}}>✕</button>
                </div>
              ))}
              <button onClick={addService} style={{background:'transparent',border:`1px dashed ${T.border}`,color:T.textDim,borderRadius:8,padding:'8px',cursor:'pointer',fontSize:13,textAlign:'left'}}>+ Add Service</button>
            </div>
          </div>
          {/* Notes */}
          <div><label style={label}>NOTES</label><textarea style={{...inp,resize:'vertical',minHeight:64}} value={form.notes} onChange={e=>upd('notes',e.target.value)} placeholder="Any additional notes..."/></div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:20}}>
          <button onClick={handleSave} disabled={!form.name.trim()} style={{...btn(biz.color),flex:1,padding:13,opacity:form.name.trim()?1:0.5}}>{editMode ? 'Save Changes' : 'Add Client'}</button>
          <button onClick={onClose} style={{padding:'12px 20px',background:T.bg,color:T.textDim,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer',fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── CLIENT DETAIL VIEW ───────────────────────────────────────
function ClientDetailView({client, bizId, biz, onBack, showNotif}) {
  const defaultInvoiceMonth = `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`;
  const [invoices, setInvoices] = React.useState([]);
  const [showInvForm, setShowInvForm] = React.useState(false);
  const [invForm, setInvForm] = React.useState({invoiceMonth:defaultInvoiceMonth, amount:client.monthlyRetainer||'', status:'Pending', dueDate:''});

  React.useEffect(() => { loadInvoices(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadInvoices() {
    try {
      const snap = await getDocs(collection(db, 'businesses', bizId, 'clients', client.id, 'invoices'));
      setInvoices(snap.docs.map(d=>({...d.data(),id:d.id})).sort((a,b)=>b.createdAt-a.createdAt));
    } catch(e) { console.error(e); }
  }

  async function createInvoice() {
    if (!invForm.amount) return;
    try {
      const num = `INV-${Date.now().toString().slice(-6)}`;
      const inv = {...invForm, invoiceNumber:num, amount:parseFloat(invForm.amount)||0, createdAt:Date.now()};
      const ref = doc(collection(db,'businesses',bizId,'clients',client.id,'invoices'));
      await setDoc(ref, {...inv, id:ref.id});
      setInvoices(prev=>[{...inv,id:ref.id},...prev]);
      setInvForm({invoiceMonth:defaultInvoiceMonth, amount:client.monthlyRetainer||'', status:'Pending', dueDate:''});
      setShowInvForm(false);
      showNotif('Invoice created!');
    } catch(e) { showNotif('Error creating invoice','error'); }
  }

  async function updateInvoiceStatus(invId, newStatus) {
    try {
      await updateDoc(doc(db,'businesses',bizId,'clients',client.id,'invoices',invId), {status:newStatus});
      setInvoices(prev=>prev.map(i=>i.id===invId?{...i,status:newStatus}:i));
      showNotif('Invoice updated!');
    } catch(e) { showNotif('Error updating invoice','error'); }
  }

  const exportInvoicePdf = async (inv) => {
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF();
      const accent = client.cardColor || biz?.color || '#E74C3C';
      const r=parseInt(accent.slice(1,3),16), g=parseInt(accent.slice(3,5),16), b2=parseInt(accent.slice(5,7),16);
      pdf.setFillColor(5,8,16); pdf.rect(0,0,210,297,'F');
      pdf.setFillColor(r,g,b2); pdf.rect(0,0,210,8,'F');
      pdf.setFontSize(18); pdf.setTextColor(255,255,255);
      pdf.text('INVOICE', 20, 24);
      pdf.setFontSize(11); pdf.setTextColor(r,g,b2);
      pdf.text(biz?.name||'', 20, 34);
      pdf.setDrawColor(r,g,b2); pdf.setLineWidth(0.4);
      pdf.line(20,40,190,40);
      const rows=[['Invoice #',inv.invoiceNumber||''],['Client',client.name||''],['Company',client.company||'—'],['Month',inv.month||''],['Amount','AED '+(inv.amount||0).toLocaleString()],['Status',inv.status||''],['Due Date',inv.dueDate||'—']];
      rows.forEach(([k,v],i)=>{
        pdf.setFontSize(10); pdf.setTextColor(74,96,128);
        pdf.text(k+':',20,54+i*12);
        pdf.setTextColor(192,208,224);
        pdf.text(String(v),75,54+i*12);
      });
      const services=(client.services||[]).filter(Boolean);
      if(services.length){
        pdf.setFontSize(11); pdf.setTextColor(255,255,255);
        pdf.text('Services',20,142);
        pdf.line(20,146,190,146);
        services.forEach((s,i)=>{ pdf.setFontSize(10); pdf.setTextColor(192,208,224); pdf.text(`• ${s}`,24,156+i*10); });
      }
      if(client.notes){
        pdf.setFontSize(11); pdf.setTextColor(255,255,255);
        pdf.text('Notes',20,210);
        pdf.line(20,214,190,214);
        pdf.setFontSize(10); pdf.setTextColor(160,176,192);
        pdf.splitTextToSize(client.notes,160).forEach((l,i)=>pdf.text(l,20,222+i*8));
      }
      pdf.save(`${inv.invoiceNumber||'invoice'}.pdf`);
      showNotif('Invoice PDF exported!');
    } catch(e) { console.error(e); showNotif('PDF export failed','error'); }
  };

  const initials = client.name.split(' ').filter(Boolean).map(w=>w[0]).join('').toUpperCase().slice(0,2);
  const sc = client.status==='Active'?'#2ECC71':client.status==='Paused'?'#F5A623':'#E74C3C';
  const accent = client.cardColor || sc;
  const invStatusColor = s => s==='Paid'?'#2ECC71':s==='Pending'?'#F5A623':'#E74C3C';

  return (
    <div>
      <button onClick={onBack} style={{background:'none',border:`1px solid ${T.border}`,color:T.textDim,borderRadius:8,padding:'7px 16px',cursor:'pointer',fontSize:13,marginBottom:20}}>← Back to Clients</button>
      <div style={{background:`radial-gradient(140% 100% at 0% 0%,${accent}20 0%,${accent}08 35%,rgba(8,13,22,0.75) 65%)`,backdropFilter:'blur(16px)',WebkitBackdropFilter:'blur(16px)',borderRadius:12,padding:24,marginBottom:20,border:`1px solid ${accent}22`}}>
        <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:20}}>
          {client.logo
            ? <img src={client.logo} alt="" style={{width:56,height:56,borderRadius:'50%',objectFit:'cover',border:`1px solid ${accent}35`,flexShrink:0}}/>
            : <div style={{width:56,height:56,borderRadius:'50%',background:`${accent}18`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,fontWeight:700,color:accent,border:`1px solid ${accent}35`,flexShrink:0}}>{initials}</div>
          }
          <div style={{flex:1}}>
            <h1 style={{fontSize:22,fontWeight:700,color:T.text,marginBottom:4}}>{client.name}</h1>
            <div style={{fontSize:13,color:T.textDim}}>{[client.company,client.industry].filter(Boolean).join(' · ')}</div>
            {client.website && <a href={client.website} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:accent,textDecoration:'none',marginTop:3,display:'block'}}>{client.website}</a>}
          </div>
          <div style={{display:'flex',gap:10,flexShrink:0,alignItems:'center'}}>
            <div style={{width:14,height:14,borderRadius:3,background:accent,border:`1px solid ${accent}60`,title:'Card color'}}/>
            <div style={{background:`${sc}15`,color:sc,fontSize:12,padding:'5px 14px',borderRadius:12,fontWeight:600,border:`1px solid ${sc}30`}}>{client.status}</div>
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12,marginBottom:client.services?.filter(Boolean).length?16:0}}>
          {(()=>{
            const s=client.contractStartDate||client.startDate;
            const e=client.contractEndDate;
            const months=s&&e?Math.max(0,(new Date(e).getFullYear()-new Date(s).getFullYear())*12+(new Date(e).getMonth()-new Date(s).getMonth())+1):0;
            const totalValue=months*(client.monthlyRetainer||0);
            const fmt=d=>{const[y,m]=d.split('-').map(Number);return `${MONTHS[m-1]} ${y}`;};
            return [
              ['Monthly Retainer',`AED ${(client.monthlyRetainer||0).toLocaleString()}`,'#2ECC71'],
              ['Contract Start',s?fmt(s):'—',T.textMid],
              ['Contract End',e?fmt(e):'—',T.textMid],
              ['Total Value',months>0?`AED ${totalValue.toLocaleString()}`:'—','#F5A623'],
            ].map(([l,v,c])=>(
              <div key={l} style={{background:T.bg,borderRadius:8,padding:'12px 14px'}}>
                <div style={{fontSize:10,color:T.textDim,marginBottom:4,letterSpacing:1}}>{l.toUpperCase()}</div>
                <div style={{fontSize:14,fontWeight:600,color:c}}>{v}</div>
              </div>
            ));
          })()}
        </div>
        {client.services?.filter(Boolean).length>0 && (
          <div style={{marginTop:14}}>
            <div style={{fontSize:10,color:T.textDim,letterSpacing:2,marginBottom:8,fontFamily:T.mono}}>SERVICES</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {client.services.filter(Boolean).map((s,i)=>(
                <span key={i} style={{fontSize:12,color:accent,background:`${accent}12`,padding:'4px 12px',borderRadius:10,border:`1px solid ${accent}30`,fontWeight:500}}>{s}</span>
              ))}
            </div>
          </div>
        )}
        {client.notes&&<div style={{marginTop:14,padding:'12px 14px',background:T.bg,borderRadius:8,fontSize:13,color:T.textMid,borderLeft:`2px solid ${T.border}`,lineHeight:1.6}}>{client.notes}</div>}
      </div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div style={{fontSize:15,fontWeight:600}}>Invoices</div>
        <button onClick={()=>setShowInvForm(!showInvForm)} style={{...btn(biz?.color||'#F5A623')}}>+ Create Invoice</button>
      </div>
      {showInvForm&&(
        <div style={{background:T.card,borderRadius:12,padding:20,marginBottom:16,border:`1px solid ${T.border}`}}>
          <div className="form-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:12}}>
            <div><label style={label}>MONTH</label><input type="month" style={{...inp}} value={invForm.invoiceMonth} onChange={e=>setInvForm(p=>({...p,invoiceMonth:e.target.value}))}/></div>
            <div><label style={label}>AMOUNT AED</label><input type="number" style={{...inp}} value={invForm.amount} onChange={e=>setInvForm(p=>({...p,amount:e.target.value}))}/></div>
            <div><label style={label}>STATUS</label><select style={{...inp}} value={invForm.status} onChange={e=>setInvForm(p=>({...p,status:e.target.value}))}><option>Paid</option><option>Pending</option><option>Overdue</option></select></div>
            <div><label style={label}>DUE DATE</label><input type="date" style={{...inp}} value={invForm.dueDate} onChange={e=>setInvForm(p=>({...p,dueDate:e.target.value}))}/></div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={createInvoice} style={{...btn(biz?.color||'#F5A623')}}>Save Invoice</button>
            <button onClick={()=>setShowInvForm(false)} style={{padding:'10px 16px',background:'transparent',color:T.textDim,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer',fontSize:14}}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{background:T.card,borderRadius:12,border:`1px solid ${T.border}`,overflow:'hidden'}}>
        <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead>
            <tr style={{background:T.bg}}>
              {['Invoice #','Month','Amount AED','Status','Due Date',''].map(h=>(
                <th key={h} style={{padding:'11px 16px',textAlign:'left',fontSize:11,color:T.textDim,letterSpacing:1,fontWeight:600,borderBottom:`1px solid ${T.border}`}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.length===0&&<tr><td colSpan={6} style={{padding:32,textAlign:'center',color:T.textFaint,fontSize:14}}>No invoices yet — create your first one above</td></tr>}
            {invoices.map(inv=>{
              const ic=invStatusColor(inv.status);
              return (
                <tr key={inv.id} style={{borderBottom:`1px solid ${T.border}`}}>
                  <td style={{padding:'11px 16px',color:T.textDim,fontFamily:T.mono,fontSize:12}}>{inv.invoiceNumber}</td>
                  <td style={{padding:'11px 16px',color:T.textMid}}>{fmtInvoiceMonth(inv)}</td>
                  <td style={{padding:'11px 16px',color:'#2ECC71',fontWeight:600,fontFamily:T.mono}}>AED {(inv.amount||0).toLocaleString()}</td>
                  <td style={{padding:'11px 16px'}}>
                    <select
                      value={inv.status}
                      onChange={e=>updateInvoiceStatus(inv.id,e.target.value)}
                      style={{background:`${ic}15`,color:ic,fontSize:11,padding:'3px 8px',borderRadius:10,fontWeight:600,border:`1px solid ${ic}30`,cursor:'pointer',outline:'none'}}
                    >
                      <option value="Paid">Paid</option>
                      <option value="Pending">Pending</option>
                      <option value="Overdue">Overdue</option>
                    </select>
                  </td>
                  <td style={{padding:'11px 16px',color:T.textDim}}>{inv.dueDate||'—'}</td>
                  <td style={{padding:'11px 12px'}}>
                    <button onClick={()=>exportInvoicePdf(inv)} style={{background:T.bg,border:`1px solid ${T.border}`,color:T.textDim,borderRadius:6,padding:'4px 12px',cursor:'pointer',fontSize:11,whiteSpace:'nowrap'}}>Export</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

// ─── BUSINESS WIZARD ─────────────────────────────────────────
function BusinessWizard({onSave,onClose}) {
  const [step,setStep] = useState(1);
  const [form,setForm] = useState({name:'',emoji:'⚡',color:'#F5A623',goal:'',deadline:'',momentum:'Building',priority:'First',kpis:['']});
  const upd=(k,v)=>setForm(p=>({...p,[k]:v}));
  const updKpi=(i,v)=>setForm(p=>{const k=[...p.kpis];k[i]=v;return{...p,kpis:k};});
  const removeKpi=(i)=>setForm(p=>({...p,kpis:p.kpis.filter((_,j)=>j!==i)}));
  const handleSave=()=>{
    const cleanKpis=form.kpis.filter(k=>k.trim());const current={};const target={};
    cleanKpis.forEach(k=>{current[k]=0;});if(cleanKpis[0])target[cleanKpis[0]]=0;
    const id=form.name.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'')+'-'+Date.now();
    onSave({...form,id,kpis:cleanKpis,current,target});
  };

  return (
    <div style={{position:'fixed',inset:0,background:'#000000cc',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:T.card,borderRadius:16,padding:32,width:500,maxWidth:'90vw',maxHeight:'85vh',overflowY:'auto',border:`1px solid ${T.border}`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <h2 style={{fontSize:18,fontWeight:700}}>Add New Business</h2>
          <div style={{fontSize:12,color:T.textDim}}>Step {step} of 3</div>
        </div>
        <div style={{display:'flex',gap:4,marginBottom:24}}>{[1,2,3].map(s=><div key={s} style={{flex:1,height:3,borderRadius:2,background:s<=step?'#F5A623':T.border}}/>)}</div>
        {step===1&&(<div style={{display:'grid',gap:16}}>
          <div><label style={label}>BUSINESS NAME *</label><input style={{...inp}} placeholder="e.g. My Agency" value={form.name} onChange={e=>upd('name',e.target.value)}/></div>
          <div><label style={label}>NORTH STAR GOAL</label><input style={{...inp}} placeholder="e.g. 100K AED MRR" value={form.goal} onChange={e=>upd('goal',e.target.value)}/></div>
          <div><label style={label}>MILESTONE</label><input style={{...inp}} placeholder="e.g. 10K AED MRR" value={form.milestone||''} onChange={e=>upd('milestone',e.target.value)}/></div>
          <div><label style={label}>DEADLINE</label><input type="date" style={{...inp}} value={form.deadline} onChange={e=>upd('deadline',e.target.value)}/></div>
          <div><label style={label}>DEADLINE SET ON</label><input type="date" style={{...inp}} value={form.deadlineSetAt||''} onChange={e=>upd('deadlineSetAt',e.target.value)}/></div>
          <div><label style={label}>CLICKUP PROJECT URL</label><input type="text" placeholder="https://app.clickup.com/..." style={{...inp}} value={form.clickupUrl||''} onChange={e=>upd('clickupUrl',e.target.value)}/></div>
          <div className="form-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><label style={label}>MOMENTUM</label><select style={{...inp}} value={form.momentum} onChange={e=>upd('momentum',e.target.value)}>{MOMENTUMS.map(m=><option key={m}>{m}</option>)}</select></div>
            <div><label style={label}>PRIORITY</label><select style={{...inp}} value={form.priority} onChange={e=>upd('priority',e.target.value)}>{PRIORITIES.map(p=><option key={p}>{p}</option>)}</select></div>
          </div>
        </div>)}
        {step===2&&(<div style={{display:'grid',gap:20}}>
          <div><label style={label}>CHOOSE EMOJI</label><div style={{display:'flex',flexWrap:'wrap',gap:8}}>{EMOJIS.map(e=><button key={e} onClick={()=>upd('emoji',e)} style={{fontSize:22,background:form.emoji===e?T.border:'transparent',border:form.emoji===e?`1px solid #F5A623`:`1px solid ${T.border}`,borderRadius:8,padding:'6px 10px',cursor:'pointer'}}>{e}</button>)}</div></div>
          <div><label style={label}>CHOOSE COLOR</label><div style={{display:'flex',flexWrap:'wrap',gap:8}}>{COLORS.map(c=><button key={c} onClick={()=>upd('color',c)} style={{width:32,height:32,borderRadius:'50%',background:c,border:form.color===c?'3px solid #fff':'2px solid transparent',cursor:'pointer'}}/>)}</div></div>
          <div style={{background:T.bg,borderRadius:10,padding:16,borderTop:`2px solid ${form.color}`,border:`1px solid ${form.color}20`}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}><span style={{fontSize:26}}>{form.emoji}</span><div><div style={{fontWeight:700,fontSize:15,color:form.color}}>{form.name||'Business Name'}</div><div style={{fontSize:13,color:T.textDim,marginTop:2}}>{form.goal||'Your goal here'}</div></div></div>
          </div>
        </div>)}
        {step===3&&(<div style={{display:'grid',gap:12}}>
          <p style={{color:T.textMid,fontSize:14,marginBottom:4}}>Add KPIs to track. First one becomes your primary metric.</p>
          {form.kpis.map((kpi,i)=>(
            <div key={i} style={{display:'flex',gap:8}}>
              <input style={{...inp,flex:1}} placeholder={`KPI ${i+1} (e.g. Revenue AED)`} value={kpi} onChange={e=>updKpi(i,e.target.value)}/>
              {form.kpis.length>1&&<button onClick={()=>removeKpi(i)} style={{background:'#1a0808',border:'1px solid #E74C3C30',color:'#E74C3C',borderRadius:8,padding:'0 12px',cursor:'pointer',fontSize:14}}>✕</button>}
            </div>
          ))}
          <button onClick={()=>setForm(p=>({...p,kpis:[...p.kpis,'']}))} style={{background:'transparent',border:`1px dashed ${T.border}`,color:T.textDim,borderRadius:8,padding:'8px',cursor:'pointer',fontSize:14}}>+ Add KPI</button>
        </div>)}
        <div style={{display:'flex',gap:12,marginTop:24}}>
          {step>1&&<button onClick={()=>setStep(s=>s-1)} style={{flex:1,padding:'12px',background:T.bg,color:T.text,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:14}}>← Back</button>}
          {step<3&&<button onClick={()=>setStep(s=>s+1)} disabled={!form.name} style={{flex:1,padding:'12px',background:form.name?'#F5A623':T.border,color:'#000',border:'none',borderRadius:8,cursor:'pointer',fontWeight:700,fontSize:14}}>Next →</button>}
          {step===3&&<button onClick={handleSave} style={{flex:1,padding:'12px',background:'#2ECC71',color:'#000',border:'none',borderRadius:8,cursor:'pointer',fontWeight:700,fontSize:14}}>✅ Create Business</button>}
          <button onClick={onClose} style={{padding:'12px 16px',background:'transparent',color:T.textDim,border:`1px solid ${T.border}`,borderRadius:8,cursor:'pointer',fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
// â”€â”€â”€ CRM COMPONENTS â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function CRMReportsView({biz, leads}) {
  const now = new Date();
  // Month-over-month filter: "all" (default, unfiltered) or a specific "YYYY-M" month key,
  // so efficiency (win rate, funnel shape, touch volume) can be compared month to month.
  const PERIODS = React.useMemo(() => {
    const list = [{key:'all', label:'All Time'}];
    for (let i=0;i<6;i++){
      const d = new Date(now.getFullYear(), now.getMonth()-i, 1);
      list.push({key:`${d.getFullYear()}-${d.getMonth()+1}`, y:d.getFullYear(), m:d.getMonth()+1,
        label: i===0?'This Month':i===1?'Last Month':d.toLocaleDateString('default',{month:'long',year:'numeric'})});
    }
    return list;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [periodKey, setPeriodKey] = React.useState('all');
  const period = PERIODS.find(p=>p.key===periodKey) || PERIODS[0];
  const prevPeriodOf = (p) => {
    if (!p || p.key==='all') return null;
    const d = new Date(p.y, p.m-2, 1);
    return {key:`${d.getFullYear()}-${d.getMonth()+1}`, y:d.getFullYear(), m:d.getMonth()+1, label:d.toLocaleDateString('default',{month:'long',year:'numeric'})};
  };
  const prevPeriod = prevPeriodOf(period);
  // Fixed calendar-month reference (independent of the dropdown) so the "leads this month"
  // tile keeps showing real momentum even while the selector is parked on "All Time".
  const thisCalMonth = {key:`${now.getFullYear()}-${now.getMonth()+1}`, y:now.getFullYear(), m:now.getMonth()+1};
  const lastCalMonth = prevPeriodOf(thisCalMonth);
  const inPeriod = (ts, p) => {
    if (!p) return false;
    if (p.key==='all') return true;
    const d = new Date(ts);
    return d.getFullYear()===p.y && d.getMonth()+1===p.m;
  };
  const createdIn = (p) => p.key==='all' ? leads : leads.filter(l=>inPeriod(l.createdAt,p));
  const lostTimestamp = (l) => { const act=(l.activities||[]).slice().reverse().find(a=>a.type==='stage_change'&&a.to==='Lost'); return act?act.timestamp:(l.lastStageChange||l.createdAt); };
  const closedIn = (p) => leads.filter(l=>l.status==='converted' && l.convertedAt && inPeriod(l.convertedAt,p));
  const lostIn = (p) => leads.filter(l=>l.status==='lost' && inPeriod(lostTimestamp(l),p));
  const touchesIn = (p) => leads.reduce((s,l)=>s+(l.activities||[]).filter(a=>a.type==='touch'&&inPeriod(a.timestamp,p)).length,0);

  const activeLeads = leads.filter(l=>l.status!=='converted'&&l.status!=='lost'); // always the live open-pipeline snapshot
  const newLeads = createdIn(period); // leads created within the selected period (or all, if unfiltered)
  const prevNewLeads = prevPeriod ? createdIn(prevPeriod) : [];
  const closedWon = closedIn(period);
  const lost = lostIn(period);
  const totalDecided = closedWon.length + lost.length;
  const winRate = totalDecided>0?Math.round(closedWon.length/totalDecided*100):0;
  const avgDaysToClose = closedWon.length>0?Math.round(closedWon.filter(l=>l.convertedAt&&l.createdAt).reduce((s,l)=>s+(l.convertedAt-l.createdAt)/86400000,0)/closedWon.length):0;

  const prevClosedWon = prevPeriod ? closedIn(prevPeriod) : [];
  const prevLost = prevPeriod ? lostIn(prevPeriod) : [];
  const prevTotalDecided = prevClosedWon.length + prevLost.length;
  const prevWinRate = prevTotalDecided>0 ? Math.round(prevClosedWon.length/prevTotalDecided*100) : null;
  const winRateDeltaPts = (period.key!=='all' && prevWinRate!=null) ? winRate - prevWinRate : null;

  const sourceMap = {};
  closedWon.forEach(l=>{
    if(!l.source) return;
    if(!sourceMap[l.source]) sourceMap[l.source] = {count:0,value:0};
    sourceMap[l.source].count++;
    sourceMap[l.source].value += (l.dealValue||0);
  });
  const bestSourceEntry = Object.entries(sourceMap).sort((a,b)=>b[1].value-a[1].value)[0];
  const bestDeal = period.key==='all'
    ? [...activeLeads].sort((a,b)=>(b.dealValue||0)-(a.dealValue||0))[0]
    : [...newLeads].sort((a,b)=>(b.dealValue||0)-(a.dealValue||0))[0];

  const pipelineValue = activeLeads.reduce((s,l)=>s+(l.dealValue||0),0); // live open pipeline — not period-filtered
  const newPipelineValue = newLeads.reduce((s,l)=>s+(l.dealValue||0),0); // value generated within the period
  const prevNewPipelineValue = prevNewLeads.reduce((s,l)=>s+(l.dealValue||0),0);

  const touchesInPeriodCount = touchesIn(period);
  const leadsTouchedInPeriod = new Set();
  leads.forEach(l=>(l.activities||[]).forEach(a=>{ if(a.type==='touch'&&inPeriod(a.timestamp,period)) leadsTouchedInPeriod.add(l.id); }));
  const avgTouches = period.key==='all'
    ? (activeLeads.length>0 ? activeLeads.reduce((s,l)=>s+(l.touchCount||0),0)/activeLeads.length : 0)
    : (leadsTouchedInPeriod.size>0 ? touchesInPeriodCount/leadsTouchedInPeriod.size : 0);

  const weeklyTouches = Array.from({length:8},(_,i)=>{
    const wStart=Date.now()-(7-i)*7*86400000, wEnd=Date.now()-(6-i)*7*86400000;
    return leads.reduce((s,l)=>s+(l.activities||[]).filter(a=>a.type==='touch'&&a.timestamp>=wStart&&a.timestamp<wEnd).length,0);
  });
  const maxWT = Math.max(...weeklyTouches,1);
  const lastWeek = weeklyTouches[6], thisWeek = weeklyTouches[7];
  const touchDeltaPct = lastWeek>0 ? ((thisWeek-lastWeek)/lastWeek)*100 : (thisWeek>0?100:null);

  // "Reached this stage or further" — monotonically non-increasing by construction, which is
  // what makes a funnel shape actually look like a funnel (raw per-stage counts can bounce
  // around and render as a crossing bowtie instead). Scoped to the selected month's cohort so
  // funnel shape can be compared MoM; "All Time" uses every lead ever created.
  const FUNNEL_STAGES = ['New Lead','Contacted','Proposal Sent','Negotiation','Closed Won'];
  const funnelSourceLeads = period.key==='all' ? leads : newLeads;
  // A lead later marked Lost still reached whatever stage it got to before falling out — crediting
  // it there (via its stage_change activity history) instead of dropping it entirely is what keeps
  // the earlier stages from undercounting and the shape from flattening into a single bar.
  const reachedStageIdx = (l) => {
    const current = FUNNEL_STAGES.indexOf(l.stage);
    if (current !== -1) return current;
    const reachedFromHistory = (l.activities||[])
      .filter(a=>a.type==='stage_change'&&FUNNEL_STAGES.includes(a.to))
      .reduce((max,a)=>Math.max(max, FUNNEL_STAGES.indexOf(a.to)), -1);
    return reachedFromHistory===-1 ? 0 : reachedFromHistory; // every lead starts out at New Lead
  };
  const funnelCounts = FUNNEL_STAGES.map((_,i)=>funnelSourceLeads.filter(l=>reachedStageIdx(l)>=i).length);

  const icon = (path) => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{path}</svg>;
  const trophyIcon = icon(<><path d="M4 2h8v3a4 4 0 01-8 0V2z"/><path d="M4 3H2v1a3 3 0 003 3M12 3h2v1a3 3 0 01-3 3"/><path d="M6 13h4M8 9v4"/><path d="M5 13h6v1H5z"/></>);
  const sourceIconEl = icon(<><path d="M2 8.5V3a1 1 0 011-1h5.5l6 6-5.5 5.5a1 1 0 01-1.4 0L2 8.5z"/><circle cx="5" cy="5" r="1"/></>);
  const checkIconEl = icon(<><circle cx="8" cy="8" r="6.5"/><path d="M5.5 8l2 2 3-4"/></>);
  const clockIconEl = icon(<><circle cx="8" cy="8" r="6.5"/><path d="M8 4.5V8l3 2"/></>);

  const winRateSub = period.key==='all'
    ? `${closedWon.length} won · ${lost.length} lost`
    : `${closedWon.length} won · ${lost.length} lost${winRateDeltaPts!=null?` · ${winRateDeltaPts>=0?'▲':'▼'} ${Math.abs(winRateDeltaPts)}pt vs ${prevPeriod.label}`:''}`;
  const metricRows = [
    {icon:trophyIcon, label:'Best Deal', value:bestDeal?`AED ${(bestDeal.dealValue||0).toLocaleString()}`:'—', sub:bestDeal?`${bestDeal.name} · ${bestDeal.stage||'New Lead'}`:(period.key==='all'?'No active leads yet':'No leads this period')},
    {icon:sourceIconEl, label:'Best Source', value:bestSourceEntry?bestSourceEntry[0]:'—', sub:bestSourceEntry?`${bestSourceEntry[1].count} deal${bestSourceEntry[1].count!==1?'s':''} · AED ${bestSourceEntry[1].value.toLocaleString()}`:'No closed deals yet'},
    {icon:checkIconEl, label:'Win Rate', value:`${winRate}%`, sub:winRateSub, subColor:winRate>=50?'#2ECC71':'#F5A623'},
    {icon:clockIconEl, label:'Avg Days to Close', value:avgDaysToClose||'—', sub:closedWon.length>0?`from ${closedWon.length} deal${closedWon.length!==1?'s':''}`:'No closed deals yet'},
  ];

  return (
    <div>
      <style>{`
        .crm-reports-hero { display:grid; grid-template-columns: 1.1fr 0.9fr; gap:20px; margin-bottom:20px; align-items:stretch; }
        @media (max-width:900px) { .crm-reports-hero { grid-template-columns: 1fr; } }
      `}</style>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,marginBottom:4,flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:26}}>{biz.emoji}</span>
          <h1 style={{fontSize:22,fontWeight:700,color:biz.color}}>Reports — {biz.name}</h1>
        </div>
        <select value={periodKey} onChange={e=>setPeriodKey(e.target.value)} style={{width:'auto',flexShrink:0,background:'rgba(255,255,255,0.04)',border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 14px',color:T.text,fontSize:12,fontFamily:T.mono,cursor:'pointer'}}>
          {PERIODS.map(p=><option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </div>
      <p style={{color:T.textDim,fontSize:13,marginBottom:20}}>{activeLeads.length} active lead{activeLeads.length!==1?'s':''} · AED {pipelineValue.toLocaleString()} pipeline</p>

      <div className="crm-reports-hero">
        <div style={{background:`radial-gradient(140% 100% at 0% 0%,${biz.color}1c 0%,${biz.color}08 35%,rgba(8,13,22,0.75) 65%)`,backdropFilter:'blur(18px)',WebkitBackdropFilter:'blur(18px)',border:`1px solid ${biz.color}28`,borderRadius:16,padding:'22px 24px',boxShadow:`0 8px 28px ${biz.color}12, inset 0 1px 0 rgba(255,255,255,0.04)`,display:'flex',flexDirection:'column'}}>
          <div style={{fontSize:10,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginBottom:4}}>PIPELINE FUNNEL</div>
          <div style={{fontSize:11,color:CARD_DIM_TEXT,marginBottom:2}}>{period.key==='all'?'Leads that reached each stage · all time':`Leads created in ${period.label} · reached each stage`}</div>
          <FunnelHorn stages={FUNNEL_STAGES} counts={funnelCounts} color={biz.color} height={168}/>
          <div style={{display:'flex',gap:24,marginTop:18}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:10,color:CARD_DIM_TEXT,letterSpacing:1,fontFamily:T.mono,marginBottom:4}}>PIPELINE VALUE</div>
              <div style={{fontSize:26,fontWeight:800,color:T.text,fontFamily:T.mono,whiteSpace:'nowrap'}}><AnimatedNumber value={pipelineValue} prefix="AED "/></div>
              <div style={{fontSize:11,color:CARD_DIM_TEXT,marginTop:4}}>
                {period.key==='all'
                  ? `${activeLeads.length} active lead${activeLeads.length!==1?'s':''}`
                  : `+AED ${newPipelineValue.toLocaleString()} added${prevNewPipelineValue>0?` (${newPipelineValue>=prevNewPipelineValue?'▲':'▼'} vs AED ${prevNewPipelineValue.toLocaleString()})`:''}`}
              </div>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:10,color:CARD_DIM_TEXT,letterSpacing:1,fontFamily:T.mono,marginBottom:4}}>TOUCHPOINTS / LEAD</div>
              <div style={{display:'flex',alignItems:'baseline',gap:8}}>
                <div style={{fontSize:26,fontWeight:800,color:T.text,fontFamily:T.mono}}><AnimatedNumber value={avgTouches} decimals={1}/></div>
                {period.key==='all' && touchDeltaPct!=null&&(
                  <span style={{fontSize:11,fontWeight:700,fontFamily:T.mono,color:touchDeltaPct>=0?'#2ECC71':'#E74C3C',background:touchDeltaPct>=0?'rgba(46,204,113,0.12)':'rgba(231,76,60,0.12)',borderRadius:20,padding:'2px 8px',whiteSpace:'nowrap'}}>
                    {touchDeltaPct>=0?'▲':'▼'} {Math.abs(touchDeltaPct).toFixed(0)}%
                  </span>
                )}
              </div>
              <div style={{fontSize:11,color:CARD_DIM_TEXT,marginTop:4}}>
                {period.key==='all' ? `${thisWeek} touch${thisWeek!==1?'es':''} this week` : `${touchesInPeriodCount} touch${touchesInPeriodCount!==1?'es':''} in ${period.label}`}
              </div>
            </div>
          </div>
        </div>

        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:'20px 22px',display:'flex',flexDirection:'column'}}>
          <div style={{fontSize:10,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginBottom:4}}>KEY METRICS</div>
          <div style={{flex:1,display:'flex',flexDirection:'column'}}>
            {metricRows.map((row,i)=>(
              <div key={row.label} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,padding:'14px 10px',margin:'0 -10px',borderRadius:8,background:i%2===1?'rgba(255,255,255,0.02)':'transparent',flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:10,color:CARD_DIM_TEXT,fontSize:13,minWidth:0}}>
                  <span style={{flexShrink:0,display:'flex',color:biz.color}}>{row.icon}</span>
                  <span style={{whiteSpace:'nowrap'}}>{row.label}</span>
                </div>
                <div style={{textAlign:'right',minWidth:0}}>
                  <div style={{fontSize:15,fontWeight:700,color:T.text,fontFamily:T.mono,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{row.value}</div>
                  {row.sub&&<div style={{fontSize:11,color:row.subColor||CARD_DIM_TEXT,marginTop:2}}>{row.sub}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:20}}>
        <div style={{background:T.card,borderRadius:10,padding:'16px 18px',border:`1px solid ${T.border}`}}>
          <div style={{fontSize:10,color:T.textDim,letterSpacing:2,marginBottom:6}}>{period.key==='all'?'LEADS THIS MONTH':`LEADS · ${period.label.toUpperCase()}`}</div>
          <div style={{fontSize:22,fontWeight:700,color:biz.color,fontFamily:T.mono}}>{(period.key==='all'?createdIn(thisCalMonth):newLeads).length}</div>
          <div style={{fontSize:11,color:T.textFaint,marginTop:4}}>{period.key==='all'?`vs ${createdIn(lastCalMonth).length} last month`:`vs ${prevNewLeads.length} in ${prevPeriod.label}`}</div>
        </div>
        <div style={{background:T.card,borderRadius:10,padding:'16px 18px',border:`1px solid ${T.border}`}}>
          <div style={{fontSize:10,color:T.textDim,letterSpacing:2,marginBottom:6}}>TOTAL PIPELINE VALUE</div>
          <div style={{fontSize:22,fontWeight:700,color:'#2ECC71',fontFamily:T.mono}}>AED {pipelineValue.toLocaleString()}</div>
          <div style={{fontSize:11,color:T.textFaint,marginTop:4}}>across {activeLeads.length} active lead{activeLeads.length!==1?'s':''}</div>
        </div>
        <div style={{background:T.card,borderRadius:10,padding:'16px 18px',border:`1px solid ${T.border}`}}>
          <div style={{fontSize:10,color:T.textDim,letterSpacing:2,marginBottom:6}}>CLOSED WON</div>
          <div style={{fontSize:22,fontWeight:700,color:'#2ECC71',fontFamily:T.mono}}>{closedWon.length}</div>
          <div style={{fontSize:11,color:T.textFaint,marginTop:4}}>AED {closedWon.reduce((s,l)=>s+(l.dealValue||0),0).toLocaleString()} total value</div>
        </div>
        <div style={{background:T.card,borderRadius:10,padding:'16px 18px',border:`1px solid ${T.border}`}}>
          <div style={{fontSize:10,color:T.textDim,letterSpacing:2,marginBottom:6}}>LOST</div>
          <div style={{fontSize:22,fontWeight:700,color:'#E74C3C',fontFamily:T.mono}}>{lost.length}</div>
          <div style={{fontSize:11,color:T.textFaint,marginTop:4}}>{totalDecided>0?`${winRate}% win rate`:'No decided leads yet'}</div>
        </div>
      </div>

      <div style={{background:T.card,borderRadius:16,padding:'20px 22px',border:`1px solid ${T.border}`}}>
        <div style={{fontSize:10,color:T.textDim,letterSpacing:2,fontFamily:T.mono,marginBottom:14}}>TOUCH ACTIVITY (8 WEEKS)</div>
        <div style={{display:'flex',gap:6,alignItems:'flex-end',height:80}}>
          {weeklyTouches.map((t,i)=>(
            <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
              <div style={{width:'100%',background:`${biz.color}20`,borderRadius:3,height:68,display:'flex',alignItems:'flex-end',overflow:'hidden'}}>
                <div style={{width:'100%',background:biz.color,height:`${Math.round(t/maxWT*100)}%`,transition:'height 0.5s'}}/>
              </div>
              <div style={{fontSize:9,color:T.textFaint}}>{t}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CRMView({biz, leads, showNotif, onConvertToClient}) {
  const [subView, setSubView] = React.useState('kanban');
  const [selectedLeadId, setSelectedLeadId] = React.useState(null);
  const [showAddLead, setShowAddLead] = React.useState(false);
  const [editLead, setEditLead] = React.useState(null);
  const [draggedId, setDraggedId] = React.useState(null);
  const [dragOverStage, setDragOverStage] = React.useState(null);
  // Touch-only "long-press to lift, tap a column to drop" — additive, doesn't touch
  // the HTML5 drag-and-drop state/handlers above (those don't fire on touch at all).
  const [liftedId, setLiftedId] = React.useState(null);
  const longPressTimer = React.useRef(null);
  const touchMoved = React.useRef(false);
  const [stageFilter, setStageFilter] = React.useState('');
  const [sourceFilter, setSourceFilter] = React.useState('');
  const [sortField, setSortField] = React.useState('createdAt');
  const [sortDir, setSortDir] = React.useState('desc');
  const [showConvert, setShowConvert] = React.useState(null);
  const [touchModalLead, setTouchModalLead] = React.useState(null);
  const [showImport, setShowImport] = React.useState(false);
  // Optimistic stage moves — applied instantly on drop/drag so a card jumps to its new
  // column right away instead of snapping back until the Firestore round-trip completes.
  // Reconciled (cleared) below once the real `leads` prop confirms the move.
  const [pendingStage, setPendingStage] = React.useState({});

  const today = new Date().toISOString().split('T')[0];
  const activeLeads = leads.filter(l => l.status !== 'converted' && l.status !== 'lost');
  // Kanban shows everything except converted-to-client leads, so the "Lost" column can
  // actually display cards moved into it (activeLeads above excludes lost leads entirely).
  const kanbanLeads = leads.filter(l => l.status !== 'converted');
  const displayStage = (lead) => pendingStage[lead.id] || lead.stage || 'New Lead';
  const selectedLead = leads.find(l => l.id === selectedLeadId) || null;

  React.useEffect(() => {
    setPendingStage(prev => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      let changed = false;
      const next = {...prev};
      for (const id of ids) {
        const lead = leads.find(l => l.id === id);
        if (!lead || lead.stage === next[id]) { delete next[id]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [leads]);

  async function addLead(leadData) {
    try {
      const id = `lead_${Date.now()}`;
      const lead = {...leadData, id, createdAt: Date.now(), status: 'active', touchCount: 0, activities: [{type:'created', timestamp:Date.now()}]};
      await setDoc(doc(db, 'businesses', 'byforce', 'leads', id), lead);
      showNotif(`Lead "${leadData.name}" added!`);
      setShowAddLead(false);
    } catch(e) { showNotif('Error adding lead', 'error'); }
  }

  async function updateLead(id, updates) {
    try {
      await updateDoc(doc(db, 'businesses', 'byforce', 'leads', id), updates);
    } catch(e) { showNotif('Error updating lead', 'error'); }
  }

  async function deleteLead(id) {
    try {
      await deleteDoc(doc(db, 'businesses', 'byforce', 'leads', id));
      showNotif('Lead deleted.');
      if (selectedLeadId === id) setSelectedLeadId(null);
    } catch(e) { showNotif('Error deleting lead', 'error'); }
  }

  async function updateLeadStage(leadId, newStage) {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    const currentStage = pendingStage[leadId] || lead.stage || 'New Lead';
    if (currentStage === newStage) return;
    setPendingStage(p=>({...p,[leadId]:newStage}));
    const activity = {type:'stage_change', from: lead.stage||'New Lead', to: newStage, timestamp: Date.now()};
    const updates = {stage: newStage, lastStageChange: Date.now(), activities: [...(lead.activities||[]), activity]};
    if (newStage === 'Lost') updates.status = 'lost';
    await updateLead(leadId, updates);
    showNotif(`Moved to ${newStage}`);
  }

  async function logTouch(leadId, touchType, note, nextFollowUp) {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    const activity = {type:'touch', touchType, note, timestamp: Date.now()};
    const updates = {touchCount:(lead.touchCount||0)+1, lastContactDate:today, activities:[...(lead.activities||[]),activity]};
    if (nextFollowUp) updates.nextFollowUpDate = nextFollowUp;
    await updateLead(leadId, updates);
    showNotif('Touch logged!');
  }

  const handleDrop = async (e, stage) => {
    e.preventDefault();
    if (draggedId) await updateLeadStage(draggedId, stage);
    setDraggedId(null); setDragOverStage(null);
  };

  const LONG_PRESS_MS = 450;
  const handleCardTouchStart = (leadId) => {
    touchMoved.current = false;
    longPressTimer.current = setTimeout(() => {
      if (!touchMoved.current) setLiftedId(leadId);
    }, LONG_PRESS_MS);
  };
  const handleCardTouchMove = () => {
    touchMoved.current = true;
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };
  const handleCardTouchEnd = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };
  const handleColumnTap = async (stage) => {
    if (!liftedId) return;
    await updateLeadStage(liftedId, stage);
    setLiftedId(null);
  };
  const handleCardTap = (leadId) => {
    if (liftedId) { setLiftedId(null); return; } // tapping any card while lifted = cancel
    setSelectedLeadId(leadId);
  };

  const daysInStage = (lead) => {
    const ref = lead.lastStageChange || lead.createdAt || Date.now();
    return Math.floor((Date.now() - ref) / 86400000);
  };

  const isOverdue = (lead) => lead.nextFollowUpDate && lead.nextFollowUpDate < today;
  const isDueToday = (lead) => lead.nextFollowUpDate === today;

  const ARROW_STAGES = CRM_STAGES.filter(s=>s!=='Lost');
  // Shared column template — used for both the arrow header and the card columns below so each
  // header segment sits exactly above its own stage, and columns stretch to fill wide screens
  // instead of leaving a dead gap on the right (minmax lets them grow past 200px, but still
  // shrink-and-scroll below that on narrow viewports).
  const kanbanGridCols = `repeat(${CRM_STAGES.length}, minmax(200px, 1fr))`;

  const renderKanban = () => (
    <div style={{overflowX:'auto', paddingBottom:16}}>
      <div style={{minWidth:CRM_STAGES.length*200 + (CRM_STAGES.length-1)*12}}>
        <div style={{display:'grid', gridTemplateColumns:kanbanGridCols, marginBottom:16}}>
          {CRM_STAGES.map((stage,i)=>{
            const sc = CRM_STAGE_COLORS[stage];
            const count = kanbanLeads.filter(l=>displayStage(l)===stage).length;
            if (stage==='Lost') {
              return (
                <div key={stage} style={{marginLeft:12, background:'rgba(231,76,60,0.08)', border:'1px solid rgba(231,76,60,0.22)', borderRadius:10, padding:'10px 18px', textAlign:'center', display:'flex', flexDirection:'column', justifyContent:'center'}}>
                  <div style={{fontSize:10, fontWeight:700, color:'#E74C3C', letterSpacing:0.5}}>Lost</div>
                  <div style={{fontSize:17, fontWeight:800, fontFamily:T.mono, color:T.text}}>{count}</div>
                </div>
              );
            }
            const forwardIdx = ARROW_STAGES.indexOf(stage);
            const isLastForward = forwardIdx === ARROW_STAGES.length-1;
            const nextSc = !isLastForward ? CRM_STAGE_COLORS[ARROW_STAGES[forwardIdx+1]] : sc;
            return (
              <div key={stage} style={{
                position:'relative',
                background:`linear-gradient(90deg, ${sc}2c, ${nextSc}2c)`,
                padding:'10px 18px 10px 20px',
                marginLeft: forwardIdx>0 ? -14 : 0,
                zIndex: forwardIdx+1,
                clipPath: isLastForward
                  ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 14px 50%)'
                  : 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)',
              }}>
                <div style={{fontSize:10, fontWeight:700, color:sc, letterSpacing:0.5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{stage}</div>
                <div style={{fontSize:17, fontWeight:800, fontFamily:T.mono, color:T.text}}>{count}</div>
              </div>
            );
          })}
        </div>

        <div style={{display:'grid', gridTemplateColumns:kanbanGridCols, gap:12, minHeight:'calc(100vh - 280px)'}}>
        {CRM_STAGES.map(stage => {
          const sc = CRM_STAGE_COLORS[stage];
          const stageLeads = kanbanLeads.filter(l => displayStage(l) === stage);
          const totalVal = stageLeads.reduce((s,l)=>s+(l.dealValue||0),0);
          const isOver = dragOverStage === stage;
          const isLiftTarget = !!liftedId;
          return (
            <div key={stage}
              onDragOver={e=>{e.preventDefault(); if(dragOverStage!==stage) setDragOverStage(stage);}}
              onDragLeave={e=>{ if(!e.currentTarget.contains(e.relatedTarget)) setDragOverStage(null); }}
              onDrop={e=>handleDrop(e,stage)}
              onClick={()=>handleColumnTap(stage)}
              style={{borderRadius:12, overflow:'hidden',
                background:isOver?`${sc}14`:isLiftTarget?`${sc}0e`:'rgba(255,255,255,0.02)',
                backdropFilter:'blur(14px)', WebkitBackdropFilter:'blur(14px)',
                border:isLiftTarget?`1px dashed ${sc}70`:`1px solid ${isOver?sc+'40':T.border}`,
                transition:'background 0.15s, border-color 0.15s'}}>
              <div style={{padding:'12px 14px', background:`linear-gradient(180deg, ${sc}22, transparent)`}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4}}>
                  <div style={{fontSize:11, fontWeight:700, color:sc, letterSpacing:0.5}}>{stage}</div>
                  <div style={{background:`${sc}20`, color:sc, fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10}}>{stageLeads.length}</div>
                </div>
                {totalVal>0&&<div style={{fontSize:11, color:T.textFaint, fontFamily:T.mono}}>AED {totalVal.toLocaleString()}</div>}
              </div>
              <div style={{padding:8, display:'flex', flexDirection:'column', gap:8, minHeight:80}}>
                {stageLeads.length===0&&<div style={{textAlign:'center', padding:'20px 8px', color:T.textFaint, fontSize:11, borderRadius:8, border:`1px dashed ${T.border}`, margin:4}}>Drop here</div>}
                {stageLeads.map(lead => {
                  const ov = isOverdue(lead), dt = isDueToday(lead);
                  const statusColor = ov?'#E74C3C':dt?'#F5A623':null;
                  const isLifted = liftedId === lead.id;
                  return (
                    <div key={lead.id}
                      draggable
                      onDragStart={e=>{e.dataTransfer.effectAllowed='move'; setDraggedId(lead.id);}}
                      onDragEnd={()=>{setDraggedId(null); setDragOverStage(null);}}
                      onTouchStart={()=>handleCardTouchStart(lead.id)}
                      onTouchMove={handleCardTouchMove}
                      onTouchEnd={handleCardTouchEnd}
                      onClick={e=>{e.stopPropagation(); handleCardTap(lead.id);}}
                      style={{
                        background:isLifted
                          ? `radial-gradient(120% 120% at 0% 0%, ${sc}30 0%, rgba(26,32,53,0.92) 60%)`
                          : statusColor
                            ? `radial-gradient(120% 120% at 0% 0%, ${statusColor}22 0%, rgba(26,32,53,0.9) 55%)`
                            : 'linear-gradient(160deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015))',
                        backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)',
                        borderRadius:10, padding:'11px 13px',
                        border:isLifted?`1px solid ${sc}80`:statusColor?`1px solid ${statusColor}45`:'1px solid rgba(255,255,255,0.07)',
                        cursor:'pointer', opacity:draggedId===lead.id?0.4:1,
                        transform:isLifted?'scale(1.05)':'none',
                        boxShadow:isLifted?`0 8px 20px rgba(0,0,0,0.5), 0 0 0 2px ${sc}80`:statusColor?`0 0 14px ${statusColor}20`:'none',
                        animation:isLifted?'liftPulse 1.2s ease-in-out infinite':'none',
                        zIndex:isLifted?5:'auto', position:'relative',
                        transition:'opacity 0.15s, transform 0.15s, box-shadow 0.15s, background 0.15s'}}>
                      <div style={{fontSize:13, fontWeight:600, color:'#e9edf5', marginBottom:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{lead.name}</div>
                      {lead.company&&<div style={{fontSize:11, color:'#9aa6bd', marginBottom:5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{lead.company}</div>}
                      {lead.dealValue>0&&<div style={{fontSize:12, color:'#2ECC71', fontWeight:700, fontFamily:T.mono, marginBottom:5}}>AED {lead.dealValue.toLocaleString()}</div>}
                      <div style={{fontSize:10, color:'#8892a4'}}>{daysInStage(lead)}d · {lead.touchCount||0} touches</div>
                      {lead.nextFollowUpDate&&(
                        <div style={{marginTop:4, fontSize:10, color:ov?'#E74C3C':dt?'#F5A623':'#a0aec0', fontWeight:ov||dt?600:400}}>
                          {ov?'Overdue: ':dt?'Today: ':''}{lead.nextFollowUpDate}
                        </div>
                      )}
                      <div style={{marginTop:6, display:'flex', gap:4}} onClick={e=>e.stopPropagation()}>
                        <ContactBtns lead={lead} size={11}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );

  const renderTable = () => {
    let filtered = leads.filter(l => l.status !== 'converted');
    if (stageFilter) filtered = filtered.filter(l => (l.stage||'New Lead') === stageFilter);
    if (sourceFilter) filtered = filtered.filter(l => l.source === sourceFilter);
    filtered = [...filtered].sort((a,b)=>{
      let av=a[sortField]||0, bv=b[sortField]||0;
      if(typeof av==='string') av=av.toLowerCase();
      if(typeof bv==='string') bv=bv.toLowerCase();
      return sortDir==='asc'?(av>bv?1:av<bv?-1:0):(av<bv?1:av>bv?-1:0);
    });
    const th=(lbl,field)=>(
      <th key={lbl} onClick={()=>{setSortField(field); setSortDir(d=>d==='asc'?'desc':'asc');}}
        style={{padding:'10px 12px', textAlign:'left', fontSize:11, color:T.textDim, letterSpacing:1, fontWeight:600, cursor:'pointer', borderBottom:`1px solid ${T.border}`, whiteSpace:'nowrap', userSelect:'none'}}>
        {lbl}{sortField===field?(sortDir==='asc'?' ↑':' ↓'):''}
      </th>
    );
    return (
      <div>
        <div style={{display:'flex', gap:10, marginBottom:14, flexWrap:'wrap'}}>
          <select value={stageFilter} onChange={e=>setStageFilter(e.target.value)} style={{...inp,width:'auto',minWidth:130,padding:'8px 12px'}}>
            <option value="">All Stages</option>
            {CRM_STAGES.map(s=><option key={s}>{s}</option>)}
          </select>
          <select value={sourceFilter} onChange={e=>setSourceFilter(e.target.value)} style={{...inp,width:'auto',minWidth:130,padding:'8px 12px'}}>
            <option value="">All Sources</option>
            {LEAD_SOURCES.map(s=><option key={s}>{s}</option>)}
          </select>
          <div style={{fontSize:13, color:T.textDim, display:'flex', alignItems:'center'}}>{filtered.length} leads</div>
        </div>
        <div style={{background:T.card, borderRadius:12, border:`1px solid ${T.border}`, overflow:'hidden'}}>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
              <thead><tr style={{background:T.bg}}>
                {th('NAME','name')}{th('COMPANY','company')}{th('SOURCE','source')}{th('STAGE','stage')}{th('DEAL VALUE','dealValue')}{th('LAST CONTACT','lastContactDate')}{th('NEXT FOLLOW-UP','nextFollowUpDate')}{th('TOUCHES','touchCount')}
                <th style={{padding:'10px 12px', borderBottom:`1px solid ${T.border}`, textAlign:'left', fontSize:11, color:T.textDim, letterSpacing:1}}>ACTIONS</th>
              </tr></thead>
              <tbody>
                {filtered.length===0&&<tr><td colSpan={9} style={{padding:32, textAlign:'center', color:T.textFaint}}>No leads found</td></tr>}
                {filtered.map(lead=>{
                  const sc=CRM_STAGE_COLORS[lead.stage||'New Lead']||T.textDim;
                  const ov=isOverdue(lead), dt=isDueToday(lead);
                  return (
                    <tr key={lead.id} style={{borderBottom:`1px solid ${T.border}`, cursor:'pointer'}} onClick={()=>setSelectedLeadId(lead.id)}>
                      <td style={{padding:'10px 12px', color:T.text, fontWeight:500}}>{lead.name}</td>
                      <td style={{padding:'10px 12px', color:T.textDim}}>{lead.company||'—'}</td>
                      <td style={{padding:'10px 12px', color:T.textDim}}>{lead.source||'—'}</td>
                      <td style={{padding:'10px 12px'}}><span style={{background:`${sc}18`, color:sc, fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:600, border:`1px solid ${sc}30`}}>{lead.stage||'New Lead'}</span></td>
                      <td style={{padding:'10px 12px', color:'#2ECC71', fontFamily:T.mono, fontWeight:600}}>{lead.dealValue?`AED ${lead.dealValue.toLocaleString()}`:'—'}</td>
                      <td style={{padding:'10px 12px', color:T.textDim, fontSize:12}}>{lead.lastContactDate||'—'}</td>
                      <td style={{padding:'10px 12px', color:ov?'#E74C3C':dt?'#F5A623':T.textDim, fontSize:12, fontWeight:ov||dt?600:400}}>{lead.nextFollowUpDate||'—'}</td>
                      <td style={{padding:'10px 12px', color:T.textDim, textAlign:'center'}}>{lead.touchCount||0}</td>
                      <td style={{padding:'10px 8px'}} onClick={e=>e.stopPropagation()}>
                        <div style={{display:'flex', gap:6, alignItems:'center'}}>
                          <ContactBtns lead={lead} size={12}/>
                          <button className="tap-target" onClick={()=>setEditLead(lead)} style={{background:T.bg, border:`1px solid ${T.border}`, color:T.textDim, borderRadius:6, padding:'10px 14px', cursor:'pointer', fontSize:11}}>Edit</button>
                          <button className="tap-target" onClick={()=>deleteLead(lead.id)} style={{background:'#1a0808', border:'1px solid #E74C3C30', color:'#E74C3C', borderRadius:6, padding:'10px 14px', cursor:'pointer', fontSize:11}}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderToday = () => {
    const todayLeads = activeLeads.filter(l=>l.nextFollowUpDate&&l.nextFollowUpDate<=today).sort((a,b)=>(b.dealValue||0)-(a.dealValue||0));
    const totalPotential = todayLeads.reduce((s,l)=>s+(l.dealValue||0),0);
    return (
      <div>
        <div style={{background:`${biz.color}10`, border:`1px solid ${biz.color}30`, borderRadius:10, padding:'16px 20px', marginBottom:20}}>
          <div style={{fontSize:18, fontWeight:700, color:biz.color}}>{todayLeads.length} lead{todayLeads.length!==1?'s':''} need your attention today</div>
          {totalPotential>0&&<div style={{fontSize:13, color:T.textDim, marginTop:4}}>AED {totalPotential.toLocaleString()} potential pipeline</div>}
        </div>
        {todayLeads.length===0&&<div style={{textAlign:'center', padding:60, color:T.textFaint, fontSize:14, border:`1px dashed ${T.border}`, borderRadius:12}}>No follow-ups due. You're all caught up!</div>}
        <div style={{display:'grid', gap:12}}>
          {todayLeads.map(lead=>{
            const sc=CRM_STAGE_COLORS[lead.stage||'New Lead'];
            const ov=isOverdue(lead);
            return (
              <div key={lead.id} style={{background:T.card, borderRadius:10, padding:'14px 18px', border:`1px solid ${ov?'#E74C3C40':'#F5A62340'}`, borderLeft:`3px solid ${ov?'#E74C3C':'#F5A623'}`}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10}}>
                  <div>
                    <div style={{fontSize:15, fontWeight:600, color:T.text, marginBottom:2}}>{lead.name}</div>
                    <div style={{fontSize:12, color:T.textDim}}>{[lead.company, lead.source].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    {lead.dealValue>0&&<div style={{fontSize:14, color:'#2ECC71', fontWeight:700, fontFamily:T.mono}}>AED {lead.dealValue.toLocaleString()}</div>}
                    <span style={{background:`${sc}18`, color:sc, fontSize:10, padding:'2px 8px', borderRadius:10, fontWeight:600}}>{lead.stage||'New Lead'}</span>
                  </div>
                </div>
                <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                  <button onClick={()=>setTouchModalLead(lead)} style={{...btn(biz.color), padding:'7px 14px', fontSize:12}}>Mark Contacted</button>
                  <select value={lead.stage||'New Lead'} onChange={e=>updateLeadStage(lead.id,e.target.value)}
                    style={{background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 12px', color:T.textDim, fontSize:12, cursor:'pointer', outline:'none'}}>
                    {CRM_STAGES.map(s=><option key={s}>{s}</option>)}
                  </select>
                  <button onClick={()=>setSelectedLeadId(lead.id)} style={{background:'transparent', border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 12px', color:T.textDim, cursor:'pointer', fontSize:12}}>Details</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Reports moved to its own sidebar view — see CRMReportsView.

  return (
    <div>
      <style>{`@keyframes liftPulse{0%,100%{box-shadow:0 8px 20px rgba(0,0,0,0.5),0 0 0 2px rgba(255,255,255,0.5)}50%{box-shadow:0 8px 24px rgba(0,0,0,0.6),0 0 0 4px rgba(255,255,255,0.2)}}`}</style>
      {liftedId&&(
        <div onClick={()=>setLiftedId(null)} style={{position:'fixed',bottom:24,left:'50%',transform:'translateX(-50%)',background:'#1a2035',border:`1px solid ${T.border}`,borderRadius:24,padding:'10px 20px',fontSize:13,color:T.text,cursor:'pointer',boxShadow:'0 4px 20px rgba(0,0,0,0.6)',zIndex:50,display:'flex',alignItems:'center',gap:8}}>
          <span>Lead lifted — tap a column to move it</span>
          <span style={{color:T.textDim}}>✕ Cancel</span>
        </div>
      )}
      {(showAddLead||editLead)&&(
        <AddLeadModal biz={biz}
          onSave={editLead
            ? async (data)=>{await updateLead(editLead.id,data); setEditLead(null); showNotif('Lead updated!');}
            : addLead}
          onClose={()=>{setShowAddLead(false); setEditLead(null);}}
          initialData={editLead} editMode={!!editLead}/>
      )}
      {showConvert&&(
        <AddClientModal biz={biz}
          onSave={(clientData)=>onConvertToClient(showConvert,clientData).then(()=>setShowConvert(null))}
          onClose={()=>setShowConvert(null)}
          initialData={{name:showConvert.name||'', company:showConvert.company||'', monthlyRetainer:showConvert.dealValue||''}}
          editMode={false}/>
      )}
      {touchModalLead&&(
        <TouchModal biz={biz}
          onSave={(tt,note,next)=>{logTouch(touchModalLead.id,tt,note,next); setTouchModalLead(null);}}
          onClose={()=>setTouchModalLead(null)}/>
      )}
      {showImport&&(
        <ImportLeadsModal biz={biz}
          onImport={(count)=>{showNotif(`${count} lead${count!==1?'s':''} imported successfully`); setShowImport(false);}}
          onClose={()=>setShowImport(false)}/>
      )}
      {selectedLead&&(
        <>
          <div style={{position:'fixed', inset:0, background:'#00000060', zIndex:999}} onClick={()=>setSelectedLeadId(null)}/>
          <LeadDetailPanel
            lead={selectedLead}
            biz={biz}
            onClose={()=>setSelectedLeadId(null)}
            showNotif={showNotif}
            onUpdate={(id,updates)=>updateLead(id,updates)}
            onStageChange={updateLeadStage}
            onLogTouch={(tt,note,next)=>logTouch(selectedLead.id,tt,note,next)}
            onConvertToClient={(lead)=>setShowConvert(lead)}
            onDelete={()=>deleteLead(selectedLead.id)}/>
        </>
      )}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20}}>
        <div>
          <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:4}}>
            <span style={{fontSize:26}}>{biz.emoji}</span>
            <h1 style={{fontSize:22, fontWeight:700, color:biz.color}}>Leads — {biz.name}</h1>
          </div>
          <p style={{color:T.textDim, fontSize:13}}>{activeLeads.length} active leads · AED {activeLeads.reduce((s,l)=>s+(l.dealValue||0),0).toLocaleString()} pipeline</p>
        </div>
        <div style={{display:'flex', gap:10, alignItems:'center'}}>
          <div style={{display:'flex', background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:3, gap:2}}>
            {[['kanban','Kanban'],['table','Table'],['today','Today']].map(([v,l])=>(
              <button key={v} onClick={()=>setSubView(v)} style={{background:subView===v?T.card:'transparent', border:subView===v?`1px solid ${T.border}`:'1px solid transparent', borderRadius:6, padding:'6px 14px', color:subView===v?T.text:T.textDim, cursor:'pointer', fontSize:12, fontWeight:subView===v?600:400, transition:'all 0.15s'}}>{l}</button>
            ))}
          </div>
          <button onClick={()=>setShowImport(true)} style={{background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'9px 16px', color:T.textDim, cursor:'pointer', fontSize:13}}>Import CSV</button>
          <button onClick={()=>setShowAddLead(true)} style={{...btn(biz.color), padding:'9px 18px', fontSize:13}}>+ Add Lead</button>
        </div>
      </div>
      {subView==='kanban'&&renderKanban()}
      {subView==='table'&&renderTable()}
      {subView==='today'&&renderToday()}
    </div>
  );
}

function AddLeadModal({biz, onSave, onClose, initialData, editMode}) {
  const [form, setForm] = React.useState(initialData ? {
    name:initialData.name||'', company:initialData.company||'', source:initialData.source||'Instagram',
    phone:initialData.phone||'', email:initialData.email||'', instagram:initialData.instagram||'',
    dealValue:initialData.dealValue||'', stage:initialData.stage||'New Lead',
    lastContactDate:initialData.lastContactDate||'', nextFollowUpDate:initialData.nextFollowUpDate||'', notes:initialData.notes||'',
  } : {name:'', company:'', source:'Instagram', phone:'', email:'', instagram:'', dealValue:'', stage:'New Lead', lastContactDate:'', nextFollowUpDate:'', notes:''});

  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  const handleSave = () => { if (!form.name.trim()) return; onSave({...form, dealValue:parseFloat(form.dealValue)||0}); };

  return (
    <div style={{position:'fixed', inset:0, background:'#000000cc', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'}}>
      <div style={{background:T.card, borderRadius:16, padding:28, width:560, maxWidth:'94vw', maxHeight:'90vh', overflowY:'auto', border:`1px solid ${T.border}`}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18}}>
          <h2 style={{fontSize:17, fontWeight:700, color:T.text}}>{editMode?'Edit Lead':'Add Lead'} — <span style={{color:biz.color}}>{biz.name}</span></h2>
          <button onClick={onClose} style={{background:'none', border:'none', color:T.textDim, cursor:'pointer', fontSize:20}}>x</button>
        </div>
        <div style={{display:'grid', gap:12}}>
          <div className="form-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
            <div><label style={label}>NAME *</label><input style={inp} value={form.name} onChange={e=>upd('name',e.target.value)} placeholder="Lead name"/></div>
            <div><label style={label}>COMPANY</label><input style={inp} value={form.company} onChange={e=>upd('company',e.target.value)} placeholder="Company"/></div>
          </div>
          <div className="form-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
            <div><label style={label}>SOURCE</label><select style={inp} value={form.source} onChange={e=>upd('source',e.target.value)}>{LEAD_SOURCES.map(s=><option key={s}>{s}</option>)}</select></div>
            <div><label style={label}>STAGE</label><select style={inp} value={form.stage} onChange={e=>upd('stage',e.target.value)}>{CRM_STAGES.map(s=><option key={s}>{s}</option>)}</select></div>
          </div>
          <div className="form-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12}}>
            <div><label style={label}>PHONE</label><input style={inp} value={form.phone} onChange={e=>upd('phone',e.target.value)} placeholder="+971..."/></div>
            <div><label style={label}>EMAIL</label><input style={inp} value={form.email} onChange={e=>upd('email',e.target.value)} placeholder="email@..."/></div>
            <div><label style={label}>INSTAGRAM</label><input style={inp} value={form.instagram} onChange={e=>upd('instagram',e.target.value)} placeholder="@handle"/></div>
          </div>
          <div className="form-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12}}>
            <div><label style={label}>DEAL VALUE AED</label><input type="number" style={inp} value={form.dealValue} onChange={e=>upd('dealValue',e.target.value)} placeholder="0"/></div>
            <div><label style={label}>LAST CONTACT</label><input type="date" style={inp} value={form.lastContactDate} onChange={e=>upd('lastContactDate',e.target.value)}/></div>
            <div><label style={label}>NEXT FOLLOW-UP</label><input type="date" style={inp} value={form.nextFollowUpDate} onChange={e=>upd('nextFollowUpDate',e.target.value)}/></div>
          </div>
          <div><label style={label}>NOTES</label><textarea style={{...inp, resize:'vertical', minHeight:60}} value={form.notes} onChange={e=>upd('notes',e.target.value)} placeholder="Notes..."/></div>
        </div>
        <div style={{display:'flex', gap:10, marginTop:18}}>
          <button onClick={handleSave} disabled={!form.name.trim()} style={{...btn(biz.color), flex:1, padding:12, opacity:form.name.trim()?1:0.5}}>{editMode?'Save Changes':'Add Lead'}</button>
          <button onClick={onClose} style={{padding:'12px 18px', background:T.bg, color:T.textDim, border:`1px solid ${T.border}`, borderRadius:8, cursor:'pointer', fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function TouchModal({biz, onSave, onClose}) {
  const [touchType, setTouchType] = React.useState('Call');
  const [note, setNote] = React.useState('');
  const [nextFollowUp, setNextFollowUp] = React.useState('');
  const color = biz&&biz.color ? biz.color : '#F5A623';
  return (
    <div style={{position:'fixed', inset:0, background:'#000000cc', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center'}}>
      <div style={{background:T.card, borderRadius:14, padding:28, width:400, maxWidth:'94vw', border:`1px solid ${T.border}`}}>
        <h3 style={{fontSize:16, fontWeight:700, color:T.text, marginBottom:16}}>Log Touch</h3>
        <div style={{display:'grid', gap:12}}>
          <div>
            <label style={label}>TOUCH TYPE</label>
            <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
              {TOUCH_TYPES.map(t=>(
                <button key={t} onClick={()=>setTouchType(t)}
                  style={{background:touchType===t?`${color}20`:'transparent', border:`1px solid ${touchType===t?color:T.border}`, borderRadius:8, padding:'6px 14px', color:touchType===t?color:T.textDim, cursor:'pointer', fontSize:13}}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div><label style={label}>NOTES</label><textarea style={{...inp, resize:'vertical', minHeight:60}} value={note} onChange={e=>setNote(e.target.value)} placeholder="What happened?"/></div>
          <div><label style={label}>NEXT FOLLOW-UP DATE</label><input type="date" style={inp} value={nextFollowUp} onChange={e=>setNextFollowUp(e.target.value)}/></div>
        </div>
        <div style={{display:'flex', gap:10, marginTop:16}}>
          <button onClick={()=>onSave(touchType,note,nextFollowUp)} style={{...btn(color), flex:1, padding:12}}>Log Touch</button>
          <button onClick={onClose} style={{padding:'12px 18px', background:T.bg, color:T.textDim, border:`1px solid ${T.border}`, borderRadius:8, cursor:'pointer', fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function LeadDetailPanel({lead, biz, onClose, showNotif, onUpdate, onStageChange, onLogTouch, onConvertToClient, onDelete}) {
  const [showTouchForm, setShowTouchForm] = React.useState(false);
  const [touchType, setTouchType] = React.useState('Call');
  const [touchNote, setTouchNote] = React.useState('');
  const [touchNext, setTouchNext] = React.useState('');
  const [showNoteForm, setShowNoteForm] = React.useState(false);
  const [noteText, setNoteText] = React.useState('');
  const [editField, setEditField] = React.useState(null);
  const [editValue, setEditValue] = React.useState('');

  const sc = CRM_STAGE_COLORS[lead.stage||'New Lead']||'#3498DB';

  const handleLogTouch = () => {
    onLogTouch(touchType, touchNote, touchNext);
    setShowTouchForm(false); setTouchType('Call'); setTouchNote(''); setTouchNext('');
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    const activity = {type:'note', text:noteText, timestamp:Date.now()};
    onUpdate(lead.id, {activities:[...(lead.activities||[]),activity]});
    setNoteText(''); setShowNoteForm(false); showNotif('Note added!');
  };

  const startEdit = (field) => { setEditField(field); setEditValue(String(lead[field]||'')); };
  const commitEdit = (field, type) => {
    const val = type==='number' ? (parseFloat(editValue)||0) : editValue;
    onUpdate(lead.id, {[field]:val});
    setEditField(null);
  };

  const renderField = (field, lbl, type, options) => {
    const isEditing = editField === field;
    const val = lead[field] !== undefined ? String(lead[field]) : '';
    return (
      <div key={field}>
        <div style={{fontSize:10, color:T.textDim, letterSpacing:1, marginBottom:3}}>{lbl}</div>
        {isEditing ? (
          <div style={{display:'flex', gap:5}}>
            {options ? (
              <select autoFocus value={editValue} onChange={e=>setEditValue(e.target.value)}
                style={{...inp, flex:1, padding:'6px 10px', fontSize:13}}>
                {options.map(o=><option key={o}>{o}</option>)}
              </select>
            ) : (
              <input autoFocus type={type||'text'} value={editValue} onChange={e=>setEditValue(e.target.value)}
                style={{...inp, flex:1, padding:'6px 10px', fontSize:13}}/>
            )}
            <button onClick={()=>commitEdit(field,type)} style={{background:'#2ECC7120', border:'1px solid #2ECC7140', color:'#2ECC71', borderRadius:6, padding:'0 10px', cursor:'pointer', fontSize:13}}>ok</button>
            <button onClick={()=>setEditField(null)} style={{background:'transparent', border:`1px solid ${T.border}`, color:T.textDim, borderRadius:6, padding:'0 8px', cursor:'pointer', fontSize:12}}>x</button>
          </div>
        ) : (
          <div onClick={()=>startEdit(field)}
            style={{fontSize:13, color:val?T.text:T.textFaint, padding:'6px 10px', background:T.bg, borderRadius:6, border:`1px solid ${T.border}`, cursor:'text', minHeight:32}}>
            {val||(options?'Select...':'—')}
          </div>
        )}
      </div>
    );
  };

  const activities = [...(lead.activities||[])].sort((a,b)=>b.timestamp-a.timestamp);
  const fmtTime = ts => new Date(ts).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'});
  const actLabel = act => act.type==='touch'?`[${act.touchType||'touch'}]`:act.type==='stage_change'?'[stage]':act.type==='note'?'[note]':'[new]';
  const actText = act => {
    if(act.type==='touch') return act.note||act.touchType||'Touch logged';
    if(act.type==='stage_change') return `${act.from||'?'} -> ${act.to}`;
    if(act.type==='note') return act.text;
    return 'Lead created';
  };

  return (
    <div style={{position:'fixed', top:0, right:0, width:440, maxWidth:'100vw', height:'100vh', background:T.card, borderLeft:`1px solid ${T.border}`, boxShadow:`-24px 0 48px ${sc}10`, zIndex:1000, overflowY:'auto', display:'flex', flexDirection:'column'}}>
      <div style={{padding:'18px 20px', background:`linear-gradient(135deg, ${sc}22 0%, ${sc}06 45%, transparent 75%)`, flexShrink:0}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:17, fontWeight:700, color:T.text}}>{lead.name}</div>
            <div style={{fontSize:12, color:T.textDim, marginTop:2}}>{lead.company||'—'}</div>
          </div>
          <button onClick={onClose} style={{background:'none', border:'none', color:T.textDim, cursor:'pointer', fontSize:18, flexShrink:0, marginLeft:8}}>x</button>
        </div>
        <div style={{display:'flex', gap:8, marginTop:10, alignItems:'center', flexWrap:'wrap'}}>
          <span style={{background:`${sc}18`, color:sc, fontSize:11, padding:'3px 10px', borderRadius:10, fontWeight:700, border:`1px solid ${sc}30`}}>{lead.stage||'New Lead'}</span>
          {lead.dealValue>0&&<span style={{fontSize:13, color:'#2ECC71', fontWeight:700, fontFamily:T.mono}}>AED {(lead.dealValue||0).toLocaleString()}</span>}
          <span style={{fontSize:11, color:T.textFaint}}>{lead.touchCount||0} touches</span>
          {lead.status==='converted'&&<span style={{background:'#2ECC7120', color:'#2ECC71', fontSize:10, padding:'2px 8px', borderRadius:10, fontWeight:700}}>CONVERTED</span>}
          <div style={{marginLeft:'auto'}}><ContactBtns lead={lead} size={14}/></div>
        </div>
      </div>
      <div style={{flex:1, padding:'16px 20px', overflowY:'auto'}}>
        <div style={{display:'flex', gap:8, marginBottom:16, flexWrap:'wrap'}}>
          <button onClick={()=>setShowTouchForm(v=>!v)} style={{...btn(biz.color), padding:'8px 14px', fontSize:12}}>+ Log Touch</button>
          <button onClick={()=>setShowNoteForm(v=>!v)} style={{background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 14px', color:T.textDim, cursor:'pointer', fontSize:12}}>+ Note</button>
          <select value={lead.stage||'New Lead'} onChange={e=>onStageChange(lead.id,e.target.value)}
            style={{background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 12px', color:T.textDim, fontSize:12, cursor:'pointer', outline:'none'}}>
            {CRM_STAGES.map(s=><option key={s}>{s}</option>)}
          </select>
          {lead.stage==='Closed Won'&&lead.status!=='converted'&&(
            <button onClick={()=>onConvertToClient(lead)} style={{...btn('#2ECC71'), padding:'8px 14px', fontSize:12}}>Convert to Client</button>
          )}
        </div>
        {showTouchForm&&(
          <div style={{background:T.bg, borderRadius:8, padding:14, marginBottom:14, border:`1px solid ${T.border}`}}>
            <div style={{fontSize:10, color:T.textDim, letterSpacing:2, marginBottom:8}}>LOG TOUCH</div>
            <div style={{display:'flex', flexWrap:'wrap', gap:6, marginBottom:10}}>
              {TOUCH_TYPES.map(t=>(
                <button key={t} onClick={()=>setTouchType(t)}
                  style={{background:touchType===t?`${biz.color}20`:'transparent', border:`1px solid ${touchType===t?biz.color:T.border}`, borderRadius:7, padding:'5px 12px', color:touchType===t?biz.color:T.textDim, cursor:'pointer', fontSize:12}}>
                  {t}
                </button>
              ))}
            </div>
            <textarea value={touchNote} onChange={e=>setTouchNote(e.target.value)} placeholder="Notes..."
              style={{...inp, minHeight:50, resize:'vertical', fontSize:12, marginBottom:8}}/>
            <div style={{display:'flex', gap:8, alignItems:'center'}}>
              <input type="date" value={touchNext} onChange={e=>setTouchNext(e.target.value)}
                style={{...inp, flex:1, padding:'7px 10px', fontSize:12}}/>
              <button onClick={handleLogTouch} style={{...btn(biz.color), padding:'8px 16px', fontSize:12, whiteSpace:'nowrap'}}>Log</button>
            </div>
          </div>
        )}
        {showNoteForm&&(
          <div style={{background:T.bg, borderRadius:8, padding:14, marginBottom:14, border:`1px solid ${T.border}`}}>
            <textarea value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Add a note..."
              style={{...inp, minHeight:64, resize:'vertical', fontSize:13, marginBottom:8}}/>
            <div style={{display:'flex', gap:8}}>
              <button onClick={handleAddNote} style={{...btn(biz.color), padding:'7px 16px', fontSize:12}}>Save Note</button>
              <button onClick={()=>setShowNoteForm(false)} style={{background:'transparent', border:`1px solid ${T.border}`, borderRadius:7, padding:'7px 14px', color:T.textDim, cursor:'pointer', fontSize:12}}>Cancel</button>
            </div>
          </div>
        )}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10, color:T.textDim, letterSpacing:2, fontFamily:T.mono, marginBottom:10}}>DETAILS</div>
          <div className="form-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
            {renderField('name','NAME')}
            {renderField('company','COMPANY')}
            {renderField('source','SOURCE',undefined,LEAD_SOURCES)}
            {renderField('dealValue','DEAL VALUE AED','number')}
            {renderField('phone','PHONE')}
            {renderField('email','EMAIL')}
            {renderField('instagram','INSTAGRAM')}
            {renderField('lastContactDate','LAST CONTACT','date')}
            {renderField('nextFollowUpDate','NEXT FOLLOW-UP','date')}
          </div>
          <div style={{marginTop:10}}>
            {renderField('notes','NOTES')}
          </div>
        </div>
        <div>
          <div style={{fontSize:10, color:T.textDim, letterSpacing:2, fontFamily:T.mono, marginBottom:10}}>ACTIVITY LOG</div>
          {activities.length===0&&<div style={{color:T.textFaint, fontSize:13}}>No activity yet.</div>}
          <div style={{display:'grid', gap:8}}>
            {activities.map((act,i)=>(
              <div key={i} style={{display:'flex', gap:10, padding:'8px 10px', background:T.bg, borderRadius:7, border:`1px solid ${T.border}`}}>
                <div style={{fontSize:10, color:T.textFaint, flexShrink:0, paddingTop:2, fontFamily:T.mono}}>{actLabel(act)}</div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:12, color:T.text}}>{actText(act)}</div>
                  <div style={{fontSize:10, color:T.textFaint, marginTop:2}}>{fmtTime(act.timestamp)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{marginTop:20, paddingTop:14, borderTop:`1px solid ${T.border}`}}>
          <button onClick={onDelete} style={{background:'#1a0808', border:'1px solid #E74C3C30', color:'#E74C3C', borderRadius:8, padding:'8px 16px', cursor:'pointer', fontSize:12}}>Delete Lead</button>
        </div>
      </div>
    </div>
  );
}

function ContactBtns({lead, size}) {
  const s = size || 13;
  const phone = lead.phone ? lead.phone.replace(/[^\d]/g, '') : '';
  const email = lead.email || '';
  const waStyle = {
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    width:s+10, height:s+10, borderRadius:5, textDecoration:'none', cursor:phone?'pointer':'default',
    background:phone?'#25D36618':'transparent',
    border:`1px solid ${phone?'#25D36640':'#1a2a3a'}`,
    color:phone?'#25D366':'#2a3a4a',
    flexShrink:0,
  };
  const emStyle = {
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    width:s+10, height:s+10, borderRadius:5, textDecoration:'none', cursor:email?'pointer':'default',
    background:email?'#3498DB18':'transparent',
    border:`1px solid ${email?'#3498DB40':'#1a2a3a'}`,
    color:email?'#3498DB':'#2a3a4a',
    flexShrink:0,
  };
  return (
    <>
      <a href={phone ? `https://wa.me/${phone}` : undefined}
        target="_blank" rel="noreferrer"
        title={phone ? `WhatsApp ${lead.phone}` : 'No phone number saved'}
        onClick={e=>{if(!phone)e.preventDefault();}}
        style={waStyle}>
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
      <a href={email ? `mailto:${email}` : undefined}
        title={email ? `Email ${email}` : 'No email saved'}
        onClick={e=>{if(!email)e.preventDefault();}}
        style={emStyle}>
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2"/>
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
        </svg>
      </a>
    </>
  );
}

function ImportLeadsModal({biz, onImport, onClose}) {
  const [csvFile, setCsvFile] = React.useState(null);
  const [preview, setPreview] = React.useState([]);
  const [parsed, setParsed] = React.useState([]);
  const [importing, setImporting] = React.useState(false);

  const EXPECTED_COLS = ['name','company','source','phone','email','instagram','dealValue','stage','notes'];
  const SAMPLE_CSV = 'name,company,source,phone,email,instagram,dealValue,stage,notes\nAhmed Al Mansouri,Tech Corp,Instagram,+971501234567,ahmed@techcorp.ae,@ahmed_tech,5000,New Lead,Met at event\nSarah Johnson,Media Plus,LinkedIn,+971559876543,sarah@mediaplus.com,,8000,Contacted,Warm lead';

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], {type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'leads_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return {rows:[]};
    const hdrs = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g,''));
    const rows = lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g,''));
      const obj = {};
      hdrs.forEach((h, i) => { obj[h] = vals[i]||''; });
      return obj;
    }).filter(r => r.name && r.name.trim());
    return {rows};
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const {rows} = parseCSV(ev.target.result);
      setParsed(rows);
      setPreview(rows.slice(0,5));
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!parsed.length || importing) return;
    setImporting(true);
    let count = 0;
    for (const row of parsed) {
      if (!row.name) continue;
      const id = `lead_${Date.now()}_${count}`;
      await setDoc(doc(db, 'businesses', 'byforce', 'leads', id), {
        id, name:row.name||'', company:row.company||'', source:row.source||'Instagram',
        phone:row.phone||'', email:row.email||'', instagram:row.instagram||'',
        dealValue:parseFloat(row.dealValue)||0, stage:row.stage||'New Lead', notes:row.notes||'',
        createdAt:Date.now(), status:'active', touchCount:0,
        activities:[{type:'created', timestamp:Date.now()}],
      });
      count++;
    }
    setImporting(false);
    onImport(count);
  };

  return (
    <div style={{position:'fixed', inset:0, background:'#000000cc', zIndex:2500, display:'flex', alignItems:'center', justifyContent:'center'}}>
      <div style={{background:T.card, borderRadius:16, padding:28, width:640, maxWidth:'96vw', maxHeight:'90vh', overflowY:'auto', border:`1px solid ${T.border}`}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
          <h2 style={{fontSize:17, fontWeight:700, color:T.text}}>Import Leads — <span style={{color:biz.color}}>{biz.name}</span></h2>
          <button onClick={onClose} style={{background:'none', border:'none', color:T.textDim, cursor:'pointer', fontSize:20}}>x</button>
        </div>
        <div style={{background:T.bg, borderRadius:8, padding:'12px 14px', marginBottom:16, border:`1px solid ${T.border}`}}>
          <div style={{fontSize:10, color:T.textDim, letterSpacing:2, marginBottom:8}}>EXPECTED COLUMNS</div>
          <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
            {EXPECTED_COLS.map(c=><span key={c} style={{background:`${biz.color}18`, color:biz.color, fontSize:11, padding:'2px 8px', borderRadius:6, fontFamily:T.mono}}>{c}</span>)}
          </div>
        </div>
        <div style={{display:'flex', gap:10, marginBottom:16, flexWrap:'wrap'}}>
          <label style={{...btn(biz.color), padding:'9px 16px', fontSize:13, cursor:'pointer', display:'inline-block'}}>
            Choose CSV File
            <input type="file" accept=".csv" onChange={handleFile} style={{display:'none'}}/>
          </label>
          <button onClick={downloadSample} style={{background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'9px 16px', color:T.textDim, cursor:'pointer', fontSize:13}}>Download Sample</button>
        </div>
        {csvFile&&(
          <div style={{fontSize:12, color:T.textDim, marginBottom:12}}>
            {csvFile.name} � {parsed.length} valid lead{parsed.length!==1?'s':''} found
            {parsed.length===0&&<span style={{color:'#E74C3C', marginLeft:8}}>No valid rows found. Check the name column is populated.</span>}
          </div>
        )}
        {preview.length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:10, color:T.textDim, letterSpacing:2, marginBottom:8}}>PREVIEW (first {preview.length} rows)</div>
            <div style={{background:T.bg, borderRadius:8, border:`1px solid ${T.border}`, overflow:'hidden'}}>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
                  <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
                    {['name','company','source','phone','dealValue','stage'].map(h=>(
                      <th key={h} style={{padding:'8px 12px', textAlign:'left', fontSize:10, color:T.textDim, letterSpacing:1, whiteSpace:'nowrap'}}>{h.toUpperCase()}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {preview.map((row,i)=>(
                      <tr key={i} style={{borderBottom:`1px solid ${T.border}30`}}>
                        <td style={{padding:'7px 12px', color:T.text, fontWeight:500}}>{row.name}</td>
                        <td style={{padding:'7px 12px', color:T.textDim}}>{row.company||'—'}</td>
                        <td style={{padding:'7px 12px', color:T.textDim}}>{row.source||'—'}</td>
                        <td style={{padding:'7px 12px', color:T.textDim, fontFamily:T.mono, fontSize:11}}>{row.phone||'—'}</td>
                        <td style={{padding:'7px 12px', color:'#2ECC71', fontFamily:T.mono}}>{row.dealValue?`AED ${row.dealValue}`:''}</td>
                        <td style={{padding:'7px 12px', color:CRM_STAGE_COLORS[row.stage]||T.textDim, fontSize:11}}>{row.stage||'New Lead'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        <div style={{display:'flex', gap:10}}>
          <button onClick={handleImport} disabled={!parsed.length||importing}
            style={{...btn(biz.color), flex:1, padding:12, opacity:parsed.length&&!importing?1:0.5}}>
            {importing ? 'Importing...' : `Import ${parsed.length} Lead${parsed.length!==1?'s':''}`}
          </button>
          <button onClick={onClose} style={{padding:'12px 18px', background:T.bg, color:T.textDim, border:`1px solid ${T.border}`, borderRadius:8, cursor:'pointer', fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
